import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useNavigate, useLocation, useParams, Outlet, useOutletContext, Routes, Route } from "react-router";
import { App, useRecentView, RequirePolicy, createEmbed } from "@livefree/applications";
import { ConfirmableButton, NewButton, Forbidden, Toggle, DrawerActions, SaveButton, WidgetField } from "@livefree/react-ui";
import { ReactiveCore, replaceRows, DexieDatabaseProvider, useSuccessReactor, useErrorReactor, useReactor } from "@livefree/reactive";
import { dexieRequestBrokerAddon } from "@livefree/request-broker";
import { EnvironmentConfigurations, useComponent, useUserContext } from "@livefree/runtime";
import { RestClient } from "@livefree/js-sdk";
import { useState, useEffect, useMemo, useRef, useCallback, createElement } from "react";
import { Button, Spinner, Card, Modal, ListBox, Separator, Checkbox, Label, CardContent, Description, Chip, Avatar, Table, Select, Breadcrumbs, BreadcrumbsItem, Skeleton } from "@heroui/react";
import { Stepper, RadioButtonGroup, DataGrid, EmptyState } from "@heroui-pro/react";
import { DateTime, fromISO, parseAndFormatDateTime, toISO, deepEquals, formatCurrencyUSD, sentenceCase, unCamelCase } from "@livefree/common";
import { SelectField, TextField, DateField, USTimeZoneComboBoxField, AddressForm, EmailField, PhoneField, TextAreaField, Uniformly, PasswordField, SwitchField, masks, ToggleButtonsField } from "@livefree/uniformly";
import { Filters, FilterDateRange, FilterPresetChips, FiltersProvider } from "@livefree/filters";
import { useInterval } from "@livefree/react";
const reactive = new ReactiveCore("sales");
const SalesConfig = EnvironmentConfigurations.hydrate({
  development: {
    host: "https://localhost:9002",
    api: {
      host: "https://localhost:7104",
      apiKey: "SalesAPI"
    }
  },
  staging: {
    host: "https://lf-artemis-staging.azurewebsites.net",
    api: {
      host: "https://lf-artemis-api-staging.azurewebsites.net",
      apiKey: "SalesAPI"
    }
  },
  production: {
    host: [
      "https://artemis.lifebeacon.com",
      "https://app.alert1.com",
      "https://app.lifebeacon.com"
    ],
    api: {
      host: "https://artemis.lfapi.net",
      apiKey: "SalesAPI"
    }
  }
});
SalesConfig.extend({
  dependencies: {
    googleMapsKey: "AIzaSyBk67alAd9TWqehZxRSYXQzv2S_71olvdo",
    googleLibraries: ["places"]
  }
});
const OrderType = {
  Customer: 2,
  Subscription: 4
};
const OrderStatusType = {
  New: 1,
  Processing: 2,
  Shipped: 3,
  Hold: 4,
  Completed: 5,
  Cancelled: 6,
  Activated: 7,
  Declined: 8,
  UnableToActivate: 9,
  AssumedActivated: 10,
  Upgrading: 11,
  ShipmentReceived: 12,
  NoService: 13,
  Fulfilled: 14,
  NonBillable: 15
};
const PaymentType = {
  ACH: 2
};
const PaymentStatusType = {
  Pending: 1,
  Authorized: 2,
  Paid: 3,
  Voided: 6,
  Declined: 7,
  Error: 9,
  InvalidRouting: 10,
  InvalidAccount: 11,
  AccountNotFound: 12,
  NSF: 13,
  AccountClosed: 14
};
const PaymentProfileType = {
  CreditCard: 1,
  Ach: 2
};
const ConfirmationChannels = {
  Sms: 1,
  Email: 2
};
class EnrollmentsClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "sales/enrollments");
  }
  /**
   * `GET /sales/enrollments?salesAgentId=` — the agent's enrollment sessions.
   * Omit `salesAgentId` for the caller's own: the service resolves the authenticated user to the
   * dealer they act as, as it does on `start`. Returns lean rows: the composed detail members are
   * not populated by this list.
   */
  async getBySalesAgent(salesAgentId) {
    const query = {};
    if (salesAgentId !== void 0) query.salesAgentId = String(salesAgentId);
    return await this.get(query);
  }
  /**
   * `POST /sales/enrollments` — open a new enrollment session.
   * The service assigns `id`, and resolves `salesAgentId` from the authenticated user
   * when it is left unset.
   */
  async start(enrollment = {}) {
    return await this.create(enrollment);
  }
  /**
   * `GET /sales/enrollments/by-payment/{paymentId}` — the enrollment that took a payment. Lean: the
   * composed detail is not populated. Rejects with a 404 when no enrollment took the payment.
   */
  async getByPayment(paymentId) {
    return await this.httpGET({
      url: this.appendUrl(`/by-payment/${paymentId}`)
    });
  }
  /** `GET /sales/enrollments/{id}` — one enrollment with its composed detail. */
  async getById(id) {
    return await this.fetch(id);
  }
  /** `PUT /sales/enrollments/{id}` — update the enrollment row itself (ids + `statusType`). */
  async save(id, enrollment) {
    return await this.update(id, enrollment);
  }
  // ===== Customer =====
  /** `GET /sales/enrollments/{id}/customer` */
  async getCustomer(id) {
    return await this.httpGET({ url: this.appendUrl(`/${id}/customer`) });
  }
  /** `POST /sales/enrollments/{id}/customer` — create the customer (and addresses) for the session. */
  async createCustomer(id, request) {
    return await this.httpPOST({
      url: this.appendUrl(`/${id}/customer`),
      body: request
    });
  }
  /** `PUT /sales/enrollments/{id}/customer` */
  async updateCustomer(id, request) {
    return await this.httpPUT({
      url: this.appendUrl(`/${id}/customer`),
      body: request
    });
  }
  // ===== Emergency contacts =====
  /** `GET /sales/enrollments/{id}/customer/contacts` */
  async getContacts(id) {
    return await this.httpGET({
      url: this.appendUrl(`/${id}/customer/contacts`)
    });
  }
  /** `POST /sales/enrollments/{id}/customer/contacts` */
  async addContact(id, contact) {
    return await this.httpPOST({
      url: this.appendUrl(`/${id}/customer/contacts`),
      body: contact
    });
  }
  /** `GET /sales/enrollments/{id}/customer/contacts/{contactId}` */
  async getContact(id, contactId) {
    return await this.httpGET({
      url: this.appendUrl(`/${id}/customer/contacts/${contactId}`)
    });
  }
  /** `PUT /sales/enrollments/{id}/customer/contacts/{contactId}` */
  async updateContact(id, contactId, contact) {
    return await this.httpPUT({
      url: this.appendUrl(`/${id}/customer/contacts/${contactId}`),
      body: contact
    });
  }
  /** `DELETE /sales/enrollments/{id}/customer/contacts/{contactId}` */
  async removeContact(id, contactId) {
    return await this.httpDELETE({
      url: this.appendUrl(`/${id}/customer/contacts/${contactId}`)
    });
  }
  // ===== Station info =====
  /** `GET /sales/enrollments/{id}/customer/station-info` */
  async getStationInfo(id) {
    return await this.httpGET({
      url: this.appendUrl(`/${id}/customer/station-info`)
    });
  }
  /**
   * `PUT /sales/enrollments/{id}/customer/station-info` — replaces the record whole: a field left
   * null or blank clears the stored value. There is no DELETE; save an empty model to clear it.
   */
  async saveStationInfo(id, info) {
    return await this.httpPUT({
      url: this.appendUrl(`/${id}/customer/station-info`),
      body: info
    });
  }
  // ===== Order =====
  /** `GET /sales/enrollments/{id}/order` */
  async getOrder(id) {
    return await this.httpGET({ url: this.appendUrl(`/${id}/order`) });
  }
  /** `POST /sales/enrollments/{id}/order` — place the session's order (order + device + items). */
  async createOrder(id, request) {
    return await this.httpPOST({
      url: this.appendUrl(`/${id}/order`),
      body: request
    });
  }
  /**
   * `PUT /sales/enrollments/{id}/order` — rebuild the session's order from the same request
   * `createOrder` takes. Refused (400) once a payment is submitted, scheduled or taken.
   */
  async updateOrder(id, request) {
    return await this.httpPUT({
      url: this.appendUrl(`/${id}/order`),
      body: request
    });
  }
  // ===== Order items =====
  /** `GET /sales/enrollments/{id}/order/items` */
  async getOrderItems(id) {
    return await this.httpGET({ url: this.appendUrl(`/${id}/order/items`) });
  }
  /** `POST /sales/enrollments/{id}/order/items` */
  async addOrderItem(id, item) {
    return await this.httpPOST({
      url: this.appendUrl(`/${id}/order/items`),
      body: item
    });
  }
  /** `GET /sales/enrollments/{id}/order/items/{itemId}` */
  async getOrderItem(id, itemId) {
    return await this.httpGET({
      url: this.appendUrl(`/${id}/order/items/${itemId}`)
    });
  }
  /** `PUT /sales/enrollments/{id}/order/items/{itemId}` */
  async updateOrderItem(id, itemId, item) {
    return await this.httpPUT({
      url: this.appendUrl(`/${id}/order/items/${itemId}`),
      body: item
    });
  }
  /** `DELETE /sales/enrollments/{id}/order/items/{itemId}` */
  async removeOrderItem(id, itemId) {
    return await this.httpDELETE({
      url: this.appendUrl(`/${id}/order/items/${itemId}`)
    });
  }
  // ===== Payments =====
  /** `GET /sales/enrollments/{id}/payments` */
  async getPayments(id) {
    return await this.httpGET({ url: this.appendUrl(`/${id}/payments`) });
  }
  /** `POST /sales/enrollments/{id}/payments` — take a payment against the session's order. */
  async addPayment(id, request) {
    return await this.httpPOST({
      url: this.appendUrl(`/${id}/payments`),
      body: request
    });
  }
  // ===== Finalize =====
  /**
   * `POST /sales/enrollments/{id}/finalize` — the last step of a sale: moves a paid enrollment to
   * Complete and, when asked, sends the customer an order confirmation.
   *
   * Rejects with a 400 when the enrollment cannot be finalized: already Complete, Cancelled or
   * Voided; no customer or order; a balance still due (an ACH payment that has not cleared does
   * not count); or no live subscription. Confirmations are best-effort — one that fails is
   * reported in `confirmations`, not raised, and the enrollment is finalized regardless.
   */
  async finalize(id, request = { sendConfirmation: 0 }) {
    return await this.httpPOST({
      url: this.appendUrl(`/${id}/finalize`),
      body: request
    });
  }
}
class OrdersClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "sales/orders");
  }
  /** `GET /sales/orders/{id}` — rejects as not-found on a missing id (never 200-with-null). */
  async getById(id) {
    return await this.fetch(id);
  }
  /** `GET /sales/orders/{id}/items` — the order's lines; an empty list for an unknown id. */
  async getItems(id) {
    return await this.httpGET({ url: this.appendUrl(`/${id}/items`) });
  }
  /**
   * `PUT /sales/orders/{id}` — the order back with the fields to change set. The route id is
   * authoritative; `orderNumber`, `typeName` and `statusTypeName` are read-only and ignored.
   */
  async save(id, order) {
    return await this.update(id, order);
  }
  /**
   * `POST /sales/orders/place` — the order, its lines and optionally a payment, in one call. The
   * order and lines are one transaction; the payment follows and, if it fails, the error names the
   * order that was created so it is not placed twice.
   */
  async place(request) {
    return await this.httpPOST({
      url: this.appendUrl("/place"),
      body: request
    });
  }
}
class LeaderboardClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "sales/leaderboard");
  }
  /**
   * `GET /sales/leaderboard?fromUtc=&toUtc=` — the board for the period. Both bounds are optional
   * and default to today in the business timezone; `toUtc` is exclusive. A period with no sales is
   * an empty board (no agents), never a 404.
   */
  async getForPeriod(period = {}) {
    return await this.get(toPeriodQuery(period));
  }
}
function toPeriodQuery(period) {
  const query = {};
  if (period.fromUtc) query.fromUtc = period.fromUtc;
  if (period.toUtc) query.toUtc = period.toUtc;
  return query;
}
class SalesDashboardClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "sales/dashboard");
  }
  /**
   * `GET /sales/dashboard?scope=&fromUtc=&toUtc=` — the dashboard for the period. `scope` defaults
   * to `Agent` (the caller's own sales); `Team` and `Manager` answer 501 until teams exist, and an
   * unknown scope is a 400. The period follows the leaderboard's rules: both bounds optional,
   * default today in the business timezone, `toUtc` exclusive. A period with no enrollments is a
   * dashboard of zeros, never a 404.
   */
  async getForPeriod(period = {}, scope) {
    const query = toPeriodQuery(period);
    if (scope !== void 0) query.scope = String(scope);
    return await this.get(query);
  }
}
class ReferralSourcesClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "sales/referral-sources");
  }
  /** `GET /sales/referral-sources` — the whole table. */
  async getAll() {
    return await this.get();
  }
  /** `POST /sales/referral-sources` */
  async createSource(model) {
    return await this.create(model);
  }
  /** `PUT /sales/referral-sources/{id}` */
  async updateSource(id, model) {
    return await this.update(id, model);
  }
  /** `DELETE /sales/referral-sources/{id}` — refused while any account references the source. */
  async deleteSource(id) {
    await this.httpDELETE({ url: this.resourceUrl(id) });
    return id;
  }
}
const DASHBOARD_ROW_ID = "current";
const DashboardService = reactive.service(
  "dashboard",
  SalesDashboardClient
)((Service) => new Service(SalesConfig.current?.api?.host ?? ""));
DashboardService.reactor("dashboardReactor", {
  success: (response, services, db) => {
    const { source, payload } = response;
    if (source === DashboardService.getForPeriod) {
      return db.dashboard.put({ ...payload, id: DASHBOARD_ROW_ID });
    }
  },
  error: (response) => {
    appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
class DealersClient extends ArtemisSDK.DealersClient {
  queryDealers() {
    return super.queryDealers("FETCH DealerModel;");
  }
}
const DealersService = reactive.service(
  "dealers",
  DealersClient
)((Service) => new Service(SalesConfig));
DealersService.reactor("dealersReactor", {
  success: (response, services, db) => {
    const { source, payload } = response;
    if (source === DealersService.queryDealers) {
      const { values } = payload;
      return replaceRows(db, db.dealers, values);
    }
  },
  error: (response) => {
    appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
class TowersClient extends ArtemisSDK.TowersClient {
  getCellularTowers(metadata) {
    const { address, radius } = metadata;
    return super.getAllTowers(address, radius);
  }
}
const TowersService = reactive.service("towers", TowersClient, {
  recursive: true,
  recurseDepth: 2
})((Service) => new Service(SalesConfig));
TowersService.reactor("towersReactor", {
  success: () => {
  },
  error: (response) => {
    appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
const EnrollmentsService = reactive.service(
  "enrollments",
  EnrollmentsClient
)((Service) => new Service(SalesConfig.current?.api?.host ?? ""));
EnrollmentsService.reactor("enrollmentsReactor", {
  success: (response, services, db) => {
    const { source, payload } = response;
    if (source === EnrollmentsService.getBySalesAgent) {
      return replaceRows(db, db.enrollments, payload);
    }
    if (source === EnrollmentsService.finalize) {
      return payload?.enrollment?.id && db.enrollments.put(payload.enrollment);
    }
    if (payload?.id) {
      return db.enrollments.put(payload);
    }
  },
  error: (response) => {
    appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
const LEADERBOARD_ROW_ID = "current";
const LeaderboardService = reactive.service(
  "leaderboard",
  LeaderboardClient
)((Service) => new Service(SalesConfig.current?.api?.host ?? ""));
LeaderboardService.reactor("leaderboardReactor", {
  success: (response, services, db) => {
    const { source, payload } = response;
    if (source === LeaderboardService.getForPeriod) {
      return db.leaderboard.put({ ...payload, id: LEADERBOARD_ROW_ID });
    }
  },
  error: (response) => {
    appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
const OrderPlacementService = reactive.service(
  "orderPlacement",
  OrdersClient
)((Service) => new Service(SalesConfig.current?.api?.host ?? ""));
OrderPlacementService.reactor("orderPlacementReactor", {
  error: (response) => appRuntime.services.alerts.error(response.payload?.message ?? "Error")
});
const OrdersService = reactive.service(
  "orders",
  OrdersClient
)((Service) => new Service(SalesConfig.current?.api?.host ?? ""));
OrdersService.reactor("ordersReactor", {
  error: (response) => {
    if (response.source?.action === "getById" && response.payload?.statusCode === 404) return;
    appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
const ReferralSourcesService = reactive.service(
  "referralSources",
  ReferralSourcesClient
)((Service) => new Service(SalesConfig.current?.api?.host ?? ""));
ReferralSourcesService.reactor("referralSourcesReactor", {
  error: (response) => appRuntime.services.alerts.error(response.payload?.message ?? "Error")
});
const storeFormat = (store) => Object.entries(store).reduce((result, [key, value]) => {
  result[key] = value.join(",");
  return result;
}, {});
const ReactiveConfig = {
  db: {
    name: "livefree.sales.db",
    provider: new DexieDatabaseProvider([dexieRequestBrokerAddon]),
    init: (db) => {
      db.version(8).stores(storeFormat(_store));
    }
  }
};
const _store = {
  dashboard: ["&id"],
  dealers: ["&id"],
  enrollments: ["&id"],
  leaderboard: ["&id"]
};
const render = reactive.configure(ReactiveConfig).render;
const Shell = ({ children }) => /* @__PURE__ */ jsx("div", { className: "p-4 h-full flex flex-col", children });
let Bootstrapped;
const AppLayout = render(({ children }) => {
  Bootstrapped ??= App.bootstrap(Shell, { runtime: appRuntime });
  return /* @__PURE__ */ jsx(Bootstrapped, { children });
});
const NewSaleStep = Object.freeze({
  Customer: 0,
  Coverage: 1,
  Products: 2,
  Contacts: 3,
  Medical: 4,
  Payment: 5,
  Review: 6
});
const NewSaleStepType = Object.freeze({
  0: "customer",
  1: "coverage",
  2: "products",
  3: "contacts",
  4: "medical",
  5: "payment",
  6: "review"
});
const NewSaleStepDisplay = Object.freeze({
  [NewSaleStep.Customer]: "Customer",
  [NewSaleStep.Coverage]: "Coverage",
  [NewSaleStep.Products]: "Products",
  [NewSaleStep.Contacts]: "Contacts",
  [NewSaleStep.Medical]: "Medical",
  [NewSaleStep.Payment]: "Payment",
  [NewSaleStep.Review]: "Review"
});
Object.freeze({
  [NewSaleStep.Customer]: "Customer details",
  [NewSaleStep.Coverage]: "Cellular coverage",
  [NewSaleStep.Products]: "Products & subscription",
  [NewSaleStep.Contacts]: "Emergency contacts",
  [NewSaleStep.Medical]: "Medical info",
  [NewSaleStep.Payment]: "Payment",
  [NewSaleStep.Review]: "Review & submit"
});
Object.freeze({
  [NewSaleStep.Customer]: "Who is this device for, and how do we reach them?",
  [NewSaleStep.Coverage]: "Verify the address has service before we promise anything.",
  [NewSaleStep.Products]: "Pick a device and a plan.",
  [NewSaleStep.Contacts]: "Who do we call when the button is pressed?",
  [NewSaleStep.Medical]: "Anything responders should know about.",
  [NewSaleStep.Payment]: "Collect payment to complete the order.",
  [NewSaleStep.Review]: "Confirm the order details and submit."
});
function NewSaleHeader({ cancelSale, cancelling = false, direct, step }) {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs("div", { className: "flex gap-2 items-center p-8 bg-background h-20 -mx-4 -mt-4 shrink-0 border-b", children: [
      /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center size-12 rounded-xl bg-accent-soft", children: /* @__PURE__ */ jsx("i", { className: "text-3xl text-accent ri-user-add-line" }) }),
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("p", { className: "text-xl font-bold", children: "New sale" }),
        /* @__PURE__ */ jsxs("p", { className: "text-muted", children: [
          "Step ",
          step + 1,
          " of 7"
        ] })
      ] }),
      /* @__PURE__ */ jsx(
        ConfirmableButton,
        {
          startContent: /* @__PURE__ */ jsx("i", { className: "text-lg ri-close-line" }),
          label: "Void sale",
          onPress: cancelSale,
          isLoading: cancelling,
          size: "lg",
          variant: "tertiary",
          className: "ms-auto"
        }
      )
    ] }),
    /* @__PURE__ */ jsx("div", { className: "flex flex-col justify-center bg-background h-20 -mx-4 shrink-0 border-b", children: /* @__PURE__ */ jsx(Stepper, { currentStep: step, onStepChange: direct, children: new Array(7).fill("").map((x, i) => /* @__PURE__ */ jsxs(Stepper.Step, { children: [
      /* @__PURE__ */ jsx(Stepper.Indicator, {}),
      /* @__PURE__ */ jsx(Stepper.Content, { children: /* @__PURE__ */ jsx(Stepper.Title, { children: NewSaleStepDisplay[i] }) }),
      /* @__PURE__ */ jsx(Stepper.Separator, {})
    ] }, i)) }) })
  ] });
}
function NewSaleFooter({ decrement, increment, step, canAdvance = true, saving = null }) {
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between bg-background h-18 -mx-4 -mb-4 shrink-0 p-8", children: [
    /* @__PURE__ */ jsx(
      Button,
      {
        size: "lg",
        onPress: decrement,
        variant: "tertiary",
        isPending: saving === "back",
        isDisabled: saving === "forward",
        children: ({ isPending }) => /* @__PURE__ */ jsxs(Fragment, { children: [
          isPending ? /* @__PURE__ */ jsx(Spinner, { color: "current", size: "sm" }) : /* @__PURE__ */ jsx("i", { className: "text-lg ri-arrow-left-line" }),
          "Back"
        ] })
      }
    ),
    /* @__PURE__ */ jsx(
      Button,
      {
        size: "lg",
        onPress: increment,
        isPending: saving === "forward",
        isDisabled: step === 6 || !canAdvance || saving === "back",
        children: ({ isPending }) => /* @__PURE__ */ jsxs(Fragment, { children: [
          isPending ? "Saving…" : "Continue",
          isPending ? /* @__PURE__ */ jsx(Spinner, { color: "current", size: "sm" }) : /* @__PURE__ */ jsx("i", { className: "text-lg ri-arrow-right-line" })
        ] })
      }
    )
  ] });
}
const Products = Object.freeze({
  LifeBeacon: 0,
  EZCare: 1,
  EZCarePlus: 2
});
const ProductSku = Object.freeze({
  [Products.LifeBeacon]: "100100",
  [Products.EZCare]: "100300",
  [Products.EZCarePlus]: "100500"
});
const productForSku = (sku) => Object.values(Products).find((device) => ProductSku[device] === String(sku ?? "").trim());
const ProductDisplay = Object.freeze({
  [Products.LifeBeacon]: "LifeBeacon",
  [Products.EZCare]: "EZCare",
  [Products.EZCarePlus]: "EZCare+"
});
const ProductIcon = Object.freeze({
  [Products.LifeBeacon]: "ri-compass-3-line",
  [Products.EZCare]: "ri-phone-line",
  [Products.EZCarePlus]: "ri-home-wifi-line"
});
const ProductLocation = Object.freeze({
  [Products.LifeBeacon]: ["On-the-go", "In-home"],
  [Products.EZCare]: ["In-home"],
  [Products.EZCarePlus]: ["In-home"]
});
const ProductConnectivity = Object.freeze({
  [Products.LifeBeacon]: "Cellular",
  [Products.EZCare]: "Landline",
  [Products.EZCarePlus]: "Cellular"
});
const ProductDescription = Object.freeze({
  [Products.LifeBeacon]: "Mobile device with two-way voice and GPS. Works anywhere with cell coverage.",
  [Products.EZCare]: "Classic in-home unit that connects through an existing phone line.",
  [Products.EZCarePlus]: "In-home base station with built-in cellular. No landline required."
});
const OptionLabel = Object.freeze({
  PendantStyle: "Wearable form factor",
  Carrier: "Cellular carrier"
});
const OptionValueDecor = Object.freeze({
  PendantStyle: {
    Pendant: { icon: "ri-medal-line", description: "Lightweight, worn around the neck." },
    Watch: { icon: "ri-time-line", description: "Wrist-worn; displays time of day." }
  },
  Carrier: {
    "AT&T": { badge: "AT&T", color: "#00A8E0", coverageKey: "att" },
    "T-Mobile": { badge: "T·Mo", color: "#E20074", coverageKey: "tmobile" }
  }
});
const ProductFeatures = Object.freeze({
  [Products.LifeBeacon]: [
    "Two-way voice through the device",
    "GPS location sent to responders",
    "Choose watch or pendant form factor"
  ],
  [Products.EZCare]: [
    "Requires an active landline",
    "Backup battery for power outages",
    "Up to 1,000 ft range from base"
  ],
  [Products.EZCarePlus]: [
    "No landline needed",
    "Backup battery for power outages",
    "Up to 1,500 ft range from base"
  ]
});
const isProductSelected = (device) => device !== "" && device != null;
const CADENCES = [
  {
    id: "monthly",
    frequency: "Monthly",
    label: "Monthly",
    hint: "Billed every month",
    months: 1,
    perYear: 12,
    periodAdj: "monthly"
  },
  {
    id: "quarterly",
    frequency: "Quarterly",
    label: "Quarterly",
    hint: "Billed every 3 months",
    months: 3,
    perYear: 4,
    periodAdj: "quarterly"
  },
  {
    id: "annual",
    frequency: "Annually",
    label: "Annual",
    hint: "Billed once a year",
    months: 12,
    perYear: 1,
    periodAdj: "annual"
  }
];
const ROUTING_BANKS = {
  "021000021": "JPMorgan Chase Bank",
  "026009593": "Bank of America",
  "121000358": "Wells Fargo",
  "031100209": "Citibank",
  "011401533": "Capital One",
  "113008465": "PNC Bank",
  "063100277": "TD Bank",
  "011000138": "U.S. Bank",
  "044000037": "Fifth Third Bank",
  "041000124": "Huntington National Bank"
};
const INITIAL_CARD = { number: "", name: null, exp: "", cvv: "", phone: null, email: null };
const EMPTY_BILLING_ADDRESS = Object.freeze({
  address1: "",
  address2: "",
  city: "",
  state: "",
  postalCode: "",
  country: "US"
});
const INITIAL_BANK = {
  routing: "",
  bankName: "",
  accountType: "checking",
  accountNumber: "",
  accountName: null
  // null until touched — the bank form shows the customer's name
};
const INITIAL_FORM = {
  customer: {
    leadSource: null,
    leadId: "",
    firstName: "",
    middleName: "",
    lastName: "",
    dob: "",
    email: "",
    address: null,
    // Checked by default: the device ships to the service address. Unchecked, `shippingAddress` is
    // where it ships instead.
    shippingSameAsService: true,
    shippingAddress: null,
    phones: {
      home: "",
      work: "",
      workExt: "",
      cell: ""
    },
    dealer: null,
    timezone: "America/New_York",
    notes: ""
  },
  cellular: {
    scenario: "strong",
    // Map display toggles only; `coverage` is the per-carrier result at the service address
    // (null until step 2's tower lookup answers).
    carriers: { att: true, tmobile: true },
    coverage: null,
    decision: ""
  },
  products: {
    device: "",
    deviceApiId: null,
    deviceName: "",
    // The device's options other than Frequency, which the cadence picker owns: { [typeName]: valueName }.
    options: {},
    // The catalogue's option definitions (ProductOptionModel[]) for `device`, copied in when it is
    // picked or when a saved order is reconciled; null until then. Completion and the order read it.
    deviceOptions: null,
    // The device line's variant name as saved (e.g. "EZ Care Plus (Monthly/Pendant)"), from which the
    // options are read back once the catalogue says which option each value belongs to.
    savedVariantName: "",
    // Every amount below is whole cents (see pricing.js). `services` and `fees` are keyed by catalogue
    // product id: { enabled, priceCents }. Accessories are { id, sku, name, qty, priceCents,
    // defaultPriceCents }.
    services: {},
    fees: {},
    accessories: [],
    subscription: {
      // Monthly, on a day of the month: the day a sale is started, which `newForm` fills in.
      cadence: "monthly",
      scheduleMode: "dayOfMonth",
      dayOfMonth: null,
      weekOfMonth: null,
      dayOfWeek: null,
      // What the customer pays per MONTH for the plan, whatever the billing cadence. A quarterly or
      // annual order bills this times 3 or 12; the server works that out from the cadence.
      monthlyCents: 0
    }
  },
  contacts: {
    list: []
  },
  payment: {
    method: "",
    card: { ...INITIAL_CARD },
    bank: { ...INITIAL_BANK },
    // At most one of the two "same as" flags is set; with neither, `address` is the billing address.
    billing: { sameAsHome: true, sameAsShipping: false, address: EMPTY_BILLING_ADDRESS },
    status: "idle",
    // Only for a submission the API refused before any payment existed; a processor decline's
    // reason is read off the payment in the enrollment.
    declineReason: "",
    // Billing's id for the latest payment submitted; the handle a correct-and-retry names.
    paymentId: null,
    // Set while correcting a declined card in place (that payment's id); null for a new profile.
    retryOfPaymentId: null,
    // The attempt whose message tops the step; the attempts themselves come from enrollment.payments.
    selectedPaymentId: null,
    schedule: "now",
    scheduledDate: ""
  },
  // Mirrors the responder-facing fields of StationAccountAdditionalInfo; medications is
  // stored there as a single '; '-joined string.
  medical: {
    lockboxLocation: "",
    lockboxCode: "",
    keyLocation: "",
    entryInfo: "",
    medications: [],
    isDisabled: false,
    usesOxygen: false,
    preferredHospital: ""
  }
};
function withoutRecordIdentity({ id, accountId, type, isDeleted, ...address }) {
  return address;
}
function newForm(today = /* @__PURE__ */ new Date()) {
  const form = structuredClone(INITIAL_FORM);
  form.products.subscription.dayOfMonth = today.getDate();
  return form;
}
const shippingAddressOf = (customer) => customer?.shippingSameAsService === false ? customer.shippingAddress : customer?.address;
const localDate = (day) => day ? DateTime.fromISO(day).toJSDate() : null;
const scheduledChargeDate = (payment) => payment?.schedule === "later" ? localDate(payment.scheduledDate) : null;
const ORDINAL_WEEKS = [
  { value: "1", label: "1st" },
  { value: "2", label: "2nd" },
  { value: "3", label: "3rd" },
  { value: "4", label: "4th" },
  { value: "last", label: "last" }
];
const WEEKDAYS = [
  { value: "0", label: "Sunday" },
  { value: "1", label: "Monday" },
  { value: "2", label: "Tuesday" },
  { value: "3", label: "Wednesday" },
  { value: "4", label: "Thursday" },
  { value: "5", label: "Friday" },
  { value: "6", label: "Saturday" }
];
const ordinalDay = (n) => {
  if (n === "last") return "last day";
  const s = n % 100;
  if (s >= 11 && s <= 13) return `${n}th`;
  const last = n % 10;
  return `${n}${["th", "st", "nd", "rd"][last] || "th"}`;
};
function nthWeekdayOfMonth(year, month, week, dow) {
  const last = new Date(year, month + 1, 0).getDate();
  if (week === "last") {
    for (let d = last; d >= 1; d--) {
      if (new Date(year, month, d).getDay() === dow) return new Date(year, month, d);
    }
    return null;
  }
  const firstDow = new Date(year, month, 1).getDay();
  const offset = (dow - firstDow + 7) % 7;
  const day = 1 + offset + (parseInt(week) - 1) * 7;
  return day > last ? null : new Date(year, month, day);
}
function dayOfMonthDate(year, month, dom) {
  const last = new Date(year, month + 1, 0).getDate();
  if (dom === "last") return new Date(year, month, last);
  return new Date(year, month, Math.min(parseInt(dom), last));
}
const scheduleChosen = (sub) => sub?.scheduleMode === "dayOfWeek" ? sub.weekOfMonth != null && sub.dayOfWeek != null : sub?.scheduleMode === "dayOfMonth" && sub.dayOfMonth != null;
function computeNextBillDate(sub, today = /* @__PURE__ */ new Date()) {
  if (!scheduleChosen(sub)) return null;
  const cutoff = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  let y = today.getFullYear(), m = today.getMonth();
  for (let i = 0; i < 5; i++) {
    let d;
    if (sub.scheduleMode === "dayOfWeek") {
      d = nthWeekdayOfMonth(y, m, sub.weekOfMonth, parseInt(sub.dayOfWeek));
    } else {
      d = dayOfMonthDate(y, m, sub.dayOfMonth);
    }
    if (d && d >= cutoff) return d;
    m++;
    if (m > 11) {
      m = 0;
      y++;
    }
  }
  return null;
}
const fmtFullDate = (d) => d ? d.toLocaleDateString("en-US", {
  weekday: "long",
  month: "long",
  day: "numeric"
}) : "—";
function computeFirstNextBillDate(sub, cad, today) {
  const nextOccurrence = computeNextBillDate(sub, today);
  if (!nextOccurrence) return null;
  if (cad.id === "monthly") {
    const days = Math.round((nextOccurrence.getTime() - today.getTime()) / 864e5);
    if (days <= 15) {
      return computeNextBillDate(
        sub,
        new Date(nextOccurrence.getFullYear(), nextOccurrence.getMonth() + 1, 1)
      );
    }
    return nextOccurrence;
  }
  return computeNextBillDate(
    sub,
    new Date(nextOccurrence.getFullYear(), nextOccurrence.getMonth() + cad.months, 1)
  );
}
function calcBillingDates(products, fromDate) {
  const sub = products.subscription;
  const cad = CADENCES.find((c) => c.id === sub.cadence) || CADENCES[0];
  const base = fromDate ? new Date(fromDate) : /* @__PURE__ */ new Date();
  base.setHours(0, 0, 0, 0);
  return { cad, chargeDate: base, nextDate: computeFirstNextBillDate(sub, cad, base) };
}
const FREQUENCY_OPTION = "Frequency";
const isFrequency = (option) => option.name === FREQUENCY_OPTION;
const offers = (option, valueName) => option.values.some((value) => value.name === valueName);
const choiceOptions = (deviceOptions = []) => (deviceOptions ?? []).filter((option) => !isFrequency(option));
function offeredCadences(deviceOptions = []) {
  const frequency = (deviceOptions ?? []).find(isFrequency);
  return frequency ? CADENCES.filter((c) => offers(frequency, c.frequency)) : CADENCES;
}
const cadenceFrequency = (subscription) => CADENCES.find((c) => c.id === subscription?.cadence)?.frequency;
function deviceOptionsComplete(products) {
  const { deviceOptions, options = {}, subscription } = products ?? {};
  if (!deviceOptions) return false;
  return deviceOptions.every((option) => {
    const value = isFrequency(option) ? cadenceFrequency(subscription) : options[option.name];
    return value ? offers(option, value) : !option.required;
  });
}
function variantOptions(products) {
  const { deviceOptions, options = {}, subscription } = products ?? {};
  return (deviceOptions ?? []).flatMap((option) => {
    const valueName = isFrequency(option) ? cadenceFrequency(subscription) : options[option.name];
    return valueName && offers(option, valueName) ? [{ typeName: option.name, valueName }] : [];
  });
}
const variantValues = (variantName) => {
  const listed = /\(([^)]*)\)\s*$/.exec(variantName ?? "")?.[1] ?? "";
  return new Set(
    listed.split("/").map((value) => value.trim()).filter(Boolean)
  );
};
function optionsFromVariantName(deviceOptions, variantName) {
  const values = variantValues(variantName);
  return Object.fromEntries(
    choiceOptions(deviceOptions).flatMap((option) => {
      const value = option.values.find((v) => values.has(v.name));
      return value ? [[option.name, value.name]] : [];
    })
  );
}
function optionsFromCoverage(deviceOptions, coverage) {
  return Object.fromEntries(
    choiceOptions(deviceOptions).flatMap((option) => {
      const decor = OptionValueDecor[option.name] ?? {};
      const confirmed = option.values.filter((v) => {
        const key = decor[v.name]?.coverageKey;
        return key && coverage?.[key] === true;
      });
      return confirmed.length === 1 ? [[option.name, confirmed[0].name]] : [];
    })
  );
}
const FREQUENCY_NAMES = new Set(CADENCES.map((c) => c.frequency));
function productConfigLabel(products) {
  const picked = Object.values(products?.options ?? {}).filter(Boolean);
  const values = picked.length ? picked : [...variantValues(products?.savedVariantName)].filter((v) => !FREQUENCY_NAMES.has(v));
  return values.join(" · ");
}
function parseCents(text) {
  const match = /^(\d*)(?:\.(\d*))?/.exec(String(text ?? "").replace(/[^0-9.]/g, ""));
  const whole = match[1] || "0";
  const fraction = (match[2] || "").padEnd(3, "0");
  return Number(whole) * 100 + Number(fraction.slice(0, 2)) + (fraction[2] >= "5" ? 1 : 0);
}
const toCents = (dollars) => Math.round(Number(dollars ?? 0) * 100);
const fromCents = (cents) => cents / 100;
const wholeAndFraction = (cents) => {
  const abs = Math.abs(Math.round(cents ?? 0));
  return `${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
};
const centsToText = (cents) => cents == null ? "" : wholeAndFraction(cents);
const formatCents = (cents) => `${(cents ?? 0) < 0 ? "-" : ""}$${wholeAndFraction(cents)}`;
const formatDollars = (dollars) => formatCents(toCents(dollars));
const cadenceById = (id) => CADENCES.find((c) => c.id === id) ?? CADENCES[0];
const selectedPriced = (picked = {}) => Object.entries(picked).filter(([, entry]) => entry?.enabled).map(([id, entry]) => ({ id: Number(id), priceCents: entry.priceCents ?? 0 }));
const sumCents = (list) => list.reduce((sum, { priceCents }) => sum + priceCents, 0);
function calcDraftTotals(products) {
  const subscription = products.subscription ?? {};
  const cadence = cadenceById(subscription.cadence);
  const monthlyCents = subscription.monthlyCents || 0;
  const addonsMonthlyCents = sumCents(selectedPriced(products.services));
  const feesCents = sumCents(selectedPriced(products.fees));
  const accessoriesCents = (products.accessories ?? []).reduce(
    (sum, a) => sum + (a.priceCents || 0) * (a.qty || 0),
    0
  );
  const planMonthlyCents = monthlyCents + addonsMonthlyCents;
  const recurringCents = planMonthlyCents * cadence.months;
  return {
    cadence,
    monthlyCents,
    addonsMonthlyCents,
    planMonthlyCents,
    recurringCents,
    feesCents,
    accessoriesCents,
    dueTodayCents: recurringCents + feesCents + accessoriesCents
  };
}
const PLAN_LINE = /^Service Fee \| (Monthly|Quarterly|Annually)$/i;
const isPlanLine = (item) => PLAN_LINE.test(item?.productDescription ?? "");
const lineCents = (item) => item.total != null ? toCents(item.total) : toCents(item.unitPrice) * (item.quantity ?? 1);
function summarizeOrder(enrollment) {
  const items = enrollment?.orderItems ?? [];
  const lines = items.map((item) => ({
    id: item.id,
    productId: item.productId,
    name: item.productDescription || item.productSku || `Product ${item.productId}`,
    quantity: item.quantity ?? 1,
    unitPriceCents: toCents(item.unitPrice),
    totalCents: lineCents(item),
    isPlan: isPlanLine(item)
  }));
  const plan = lines.find((line) => line.isPlan);
  return {
    lines,
    plan,
    // A service fee line the server folded into the plan has no price of its own left.
    chargedLines: lines.filter((line) => !line.isPlan && line.totalCents !== 0),
    includedLines: lines.filter((line) => !line.isPlan && line.totalCents === 0),
    totalCents: toCents(enrollment?.orderTotal),
    balanceCents: toCents(enrollment?.orderBalance),
    // What each later billing period costs: the plan line, which is one period's worth.
    recurringCents: plan?.totalCents ?? 0
  };
}
const PERS_PRODUCT = Object.freeze({
  [Products.LifeBeacon]: "LifeBeacon",
  [Products.EZCare]: "EZCare",
  [Products.EZCarePlus]: "EZCarePlus"
});
function toBillingDay(subscription = {}) {
  if (subscription.scheduleMode === "dayOfWeek") {
    return {
      billingWeekOfMonth: subscription.weekOfMonth === "last" ? 5 : subscription.weekOfMonth,
      billingDayOfWeek: subscription.dayOfWeek
    };
  }
  if (subscription.scheduleMode === "dayOfMonth") {
    return { billingDayOfMonth: subscription.dayOfMonth === "last" ? 31 : subscription.dayOfMonth };
  }
  return {};
}
function buildEnrollmentOrderRequest(enrollmentId, products, order = {}) {
  const { device, subscription = {}, accessories = [] } = products;
  const priced = [...selectedPriced(products.services), ...selectedPriced(products.fees)];
  return {
    enrollmentId,
    order,
    device: {
      product: PERS_PRODUCT[device],
      options: variantOptions(products),
      quantity: 1,
      unitPrice: fromCents(subscription.monthlyCents || 0)
    },
    items: [
      ...priced.map(({ id, priceCents }) => ({
        productId: id,
        quantity: 1,
        unitPrice: fromCents(priceCents)
      })),
      ...accessories.map((a) => ({
        productId: a.id,
        quantity: a.qty || 1,
        unitPrice: fromCents(a.priceCents ?? 0)
      }))
    ],
    ...toBillingDay(subscription)
  };
}
const EnrollmentStatusType = Object.freeze({
  Open: 1,
  PaymentScheduled: 2,
  PaymentSubmitted: 3,
  PaymentFailed: 4,
  PaymentReceived: 5,
  Complete: 6,
  Cancelled: 7,
  Voided: 8
});
const EnrollmentStatusColor = Object.freeze({
  [EnrollmentStatusType.Open]: "warning",
  [EnrollmentStatusType.PaymentScheduled]: "accent",
  [EnrollmentStatusType.PaymentSubmitted]: "default",
  [EnrollmentStatusType.PaymentFailed]: "danger",
  [EnrollmentStatusType.PaymentReceived]: "accent",
  [EnrollmentStatusType.Complete]: "success",
  [EnrollmentStatusType.Cancelled]: "default",
  [EnrollmentStatusType.Voided]: "default"
});
const isOrderOpenForChanges = (enrollment) => !enrollment || enrollment.statusType === EnrollmentStatusType.Open || enrollment.statusType === EnrollmentStatusType.PaymentFailed;
const canFinalize = (enrollment) => enrollment?.statusType === EnrollmentStatusType.PaymentReceived;
const isEnrollmentComplete = (enrollment) => enrollment?.statusType === EnrollmentStatusType.Complete;
const onlyDigits = (s) => (s || "").replace(/\D/g, "");
const last4 = (s) => onlyDigits(s).slice(-4);
const cardBrand = (s) => {
  const n = onlyDigits(s);
  if (/^4/.test(n)) return "Visa";
  if (/^(5[1-5]|2[2-7])/.test(n)) return "Mastercard";
  if (/^3[47]/.test(n)) return "Amex";
  if (/^6(?:011|5)/.test(n)) return "Discover";
  return "";
};
const FAILED_PAYMENT_STATUSES = /* @__PURE__ */ new Set([
  PaymentStatusType.Declined,
  PaymentStatusType.Error,
  PaymentStatusType.Voided,
  PaymentStatusType.InvalidRouting,
  PaymentStatusType.InvalidAccount,
  PaymentStatusType.AccountNotFound,
  PaymentStatusType.NSF,
  PaymentStatusType.AccountClosed
]);
const present = (value) => typeof value === "string" && value.trim() || null;
function processorDeclineReason(payment) {
  return present(payment?.authorizationTransactionResult) ?? `${payment?.statusTypeName || "Declined"} by processor`;
}
function processorChecks(payment) {
  return [
    { label: "AVS", value: present(payment?.avsResponse) },
    { label: "Card code", value: present(payment?.cardCodeResponse) }
  ].filter((check) => check.value);
}
function latestPayment(enrollment) {
  const payments = enrollment?.payments ?? [];
  return payments.reduce(
    (latest, p) => !latest || p.paymentId > latest.paymentId ? p : latest,
    null
  );
}
const IN_FLIGHT_PAYMENT_STATUSES = /* @__PURE__ */ new Set([
  PaymentStatusType.Pending,
  PaymentStatusType.Authorized
]);
const CORRECTABLE_PAYMENT_STATUSES = /* @__PURE__ */ new Set([PaymentStatusType.Declined, PaymentStatusType.Error]);
const isPaymentInFlight = (payment) => IN_FLIGHT_PAYMENT_STATUSES.has(payment?.statusType);
const hasPaymentInFlight = (enrollment) => (enrollment?.payments ?? []).some(isPaymentInFlight);
function attemptStatus(payment, now = DateTime.utc()) {
  if (FAILED_PAYMENT_STATUSES.has(payment.statusType)) return "declined";
  switch (payment.statusType) {
    case PaymentStatusType.Paid:
      return "paid";
    case PaymentStatusType.Authorized:
      return "posted_pending";
    case PaymentStatusType.Pending:
      return fromISO(payment.paymentDateUtc) > now ? "scheduled" : "processing";
    default:
      return "other";
  }
}
function paymentAttempts(enrollment, now = DateTime.utc()) {
  return [...enrollment?.payments ?? []].sort((a, b) => b.paymentId - a.paymentId).map((payment) => {
    const status = attemptStatus(payment, now);
    const method = payment.type === PaymentType.ACH ? "bank" : "card";
    return {
      paymentId: payment.paymentId,
      status,
      statusTypeName: payment.statusTypeName || "",
      method,
      // The stored profile's name, e.g. "Visa •••• 4242"; null when Accounts did not supply it.
      instrument: payment.profileName || null,
      amount: payment.amount,
      paymentDateUtc: payment.paymentDateUtc,
      reason: status === "declined" ? processorDeclineReason(payment) : "",
      checks: processorChecks(payment),
      correctable: method === "card" && CORRECTABLE_PAYMENT_STATUSES.has(payment.statusType)
    };
  });
}
function attemptActions(attempt, { inFlight = false, settled = false } = {}) {
  const canRetry = attempt?.status === "declined" && !inFlight && !settled;
  return { canRetry, canCorrect: canRetry && !!attempt.correctable };
}
const LIVE_STATUSES = /* @__PURE__ */ new Set(["processing", "paid", "posted_pending", "scheduled"]);
const instrumentInHand = (payment) => !!(payment.method === "card" ? last4(payment.card?.number) : last4(payment.bank?.accountNumber));
function paymentMessage(payment, attempts, { inFlight = false, settled = false } = {}) {
  const live = LIVE_STATUSES.has(payment.status);
  const selected = attempts.find((a) => a.paymentId === payment.selectedPaymentId) ?? null;
  if (selected) {
    if (live && selected.paymentId === payment.paymentId) return null;
    if (selected.status !== "declined") return { kind: "status", attempt: selected };
    return {
      kind: "declined",
      attempt: selected,
      ...attemptActions(selected, { inFlight, settled: settled || live }),
      canRetrySame: selected.paymentId === payment.paymentId && instrumentInHand(payment)
    };
  }
  if (payment.status !== "declined") return null;
  const canRetry = !inFlight && !settled;
  return {
    kind: "declined",
    attempt: null,
    canRetry,
    // Correcting in place needs the card the refused submission was for still in the form.
    canCorrect: canRetry && payment.method === "card" && payment.paymentId != null && !!last4(payment.card?.number),
    canRetrySame: true
  };
}
function resolveOutcome(payment, enrollment) {
  if (!payment) return null;
  if (FAILED_PAYMENT_STATUSES.has(payment.statusType) || enrollment?.statusType === EnrollmentStatusType.PaymentFailed) {
    return "declined";
  }
  if (enrollment?.statusType === EnrollmentStatusType.PaymentReceived || enrollment?.statusType === EnrollmentStatusType.Complete) {
    return "paid";
  }
  if (payment.statusType === PaymentStatusType.Authorized) return "posted_pending";
  return null;
}
const SERVICE_LINE = /^Service Fee \| (Monthly|Quarterly|Annually)$/i;
const ADDRESS_FIELDS = ["address1", "address2", "city", "state", "postalCode", "country"];
const sameAddress = (a, b) => ADDRESS_FIELDS.every(
  (k) => (a?.[k] || "").trim().toLowerCase() === (b?.[k] || "").trim().toLowerCase()
);
function hydrateCustomer(form, enrollment) {
  const c = enrollment.customer;
  if (!c) return;
  const physical = enrollment.physicalAddress;
  const shipping = enrollment.shippingAddress;
  const shippingSameAsService = !shipping || sameAddress(shipping, physical);
  form.customer = {
    ...form.customer,
    dealer: c.dealerId ?? null,
    leadSource: c.leadSourceId ?? null,
    leadId: c.leadId || "",
    firstName: c.firstName || "",
    middleName: c.middleName || "",
    lastName: c.lastName || "",
    dob: (c.dateOfBirth || "").slice(0, 10),
    email: c.email || "",
    timezone: c.timeZone || form.customer.timezone,
    phones: {
      home: c.homePhone || "",
      work: c.workPhone || "",
      workExt: c.workPhoneExt || "",
      cell: c.mobilePhone || ""
    },
    address: physical ?? form.customer.address,
    shippingSameAsService,
    shippingAddress: shippingSameAsService ? form.customer.shippingAddress : shipping
  };
}
function hydrateProducts(form, enrollment) {
  const items = enrollment.orderItems ?? [];
  if (!enrollment.order || items.length === 0) return;
  const deviceItem = items.find((item) => productForSku(item.productSku) !== void 0);
  const serviceLine = items.find(
    (item) => item !== deviceItem && SERVICE_LINE.test(item.productDescription ?? "")
  );
  if (deviceItem) {
    const variantName = deviceItem.productDescription || "";
    form.products.device = productForSku(deviceItem.productSku);
    form.products.deviceApiId = deviceItem.productId;
    form.products.deviceName = variantName || deviceItem.productSku || "";
    form.products.savedVariantName = variantName;
    const listed = variantValues(variantName);
    form.products.subscription.cadence = (CADENCES.find((c) => listed.has(c.frequency)) ?? CADENCES[0]).id;
    form.products.subscription.monthlyCents = toCents(
      serviceLine?.unitPrice || deviceItem.unitPrice || 0
    );
  }
  form.products.accessories = items.filter((item) => item !== deviceItem && item !== serviceLine).map((item) => ({
    id: item.productId,
    sku: item.productSku ?? "",
    name: item.productDescription || item.productSku || "",
    priceCents: toCents(item.unitPrice),
    defaultPriceCents: toCents(item.unitPrice),
    qty: item.quantity ?? 1
  }));
  form.products.pendingReconcile = true;
}
function reconcileHydratedProducts(products, catalog = {}) {
  if (!products.pendingReconcile) return products;
  const {
    addonServices = [],
    feeProducts = [],
    accessoryCatalog = [],
    optionsByDevice = {}
  } = catalog;
  const deviceOptions = optionsByDevice[products.device] ?? null;
  const addonById = new Map(addonServices.map((s) => [s.id, s]));
  const feeIds = new Set(feeProducts.map((f) => f.id));
  const accessoryById = new Map(accessoryCatalog.map((a) => [a.id, a]));
  const services = {};
  const fees = {};
  const accessories = [];
  let addonsMonthlyCents = 0;
  for (const line of products.accessories) {
    const addon = addonById.get(line.id);
    if (addon) {
      const priceCents = toCents(addon.suggestedPrice);
      services[line.id] = { enabled: true, priceCents };
      addonsMonthlyCents += priceCents;
    } else if (feeIds.has(line.id)) {
      fees[line.id] = { enabled: true, priceCents: line.priceCents };
    } else {
      const listed = accessoryById.get(line.id);
      accessories.push({
        ...line,
        defaultPriceCents: listed ? toCents(listed.suggestedPrice) : line.priceCents
      });
    }
  }
  return {
    ...products,
    deviceOptions,
    options: optionsFromVariantName(deviceOptions, products.savedVariantName),
    services,
    fees,
    accessories,
    subscription: {
      ...products.subscription,
      monthlyCents: Math.max(0, products.subscription.monthlyCents - addonsMonthlyCents)
    },
    pendingReconcile: false
  };
}
function hydrateMedical(form, enrollment) {
  const s = enrollment.stationInfo;
  if (!s) return;
  form.medical = {
    lockboxLocation: s.lockboxLocation || "",
    lockboxCode: s.lockboxCode || "",
    keyLocation: s.keyLocation || "",
    entryInfo: s.entryInfo || "",
    medications: (s.medications || "").split(";").map((m) => m.trim()).filter(Boolean),
    isDisabled: s.isDisabled ?? false,
    usesOxygen: s.usesOxygen ?? false,
    preferredHospital: s.preferredHospital || ""
  };
}
function hydratePayment(form, enrollment) {
  const latest = latestPayment(enrollment);
  if (!latest) return;
  form.payment.method = latest.type === PaymentType.ACH ? "bank" : "card";
  form.payment.paymentId = latest.paymentId;
  form.payment.selectedPaymentId = latest.paymentId;
  const scheduled = latest.statusType === PaymentStatusType.Pending && fromISO(latest.paymentDateUtc) > DateTime.utc();
  if (scheduled) {
    form.payment.status = "scheduled";
    form.payment.schedule = "later";
    form.payment.scheduledDate = fromISO(latest.paymentDateUtc).toLocal().toISODate();
    return;
  }
  const outcome = resolveOutcome(latest, enrollment);
  if (outcome === "paid" || outcome === "posted_pending") {
    form.payment.status = outcome;
  } else if (outcome === "declined") {
    form.payment.status = "declined";
  } else {
    form.payment.status = "processing";
  }
}
function hydrateBillingDay(form, enrollment) {
  const sub = form.products.subscription;
  if (enrollment.billingDayOfMonth != null) {
    sub.scheduleMode = "dayOfMonth";
    sub.dayOfMonth = enrollment.billingDayOfMonth === 31 ? "last" : enrollment.billingDayOfMonth;
  } else if (enrollment.billingWeekOfMonth != null && enrollment.billingDayOfWeek != null) {
    sub.scheduleMode = "dayOfWeek";
    sub.weekOfMonth = enrollment.billingWeekOfMonth === 5 ? "last" : enrollment.billingWeekOfMonth;
    sub.dayOfWeek = enrollment.billingDayOfWeek;
  }
}
function hydrateFormFromEnrollment(enrollment) {
  const form = newForm();
  if (!enrollment) return form;
  hydrateCustomer(form, enrollment);
  hydrateProducts(form, enrollment);
  hydrateBillingDay(form, enrollment);
  if (enrollment.contacts?.length) {
    form.contacts = { ...form.contacts, list: enrollment.contacts };
  }
  hydrateMedical(form, enrollment);
  hydratePayment(form, enrollment);
  return form;
}
const RoutePaths = {
  sales: {
    get overview() {
      delete this.overview;
      this.overview = `${appRuntime.baseUrl}/`;
      return this.overview;
    },
    create: {
      get start() {
        delete this.start;
        this.start = `${appRuntime.baseUrl}/create`;
        return this.start;
      },
      /** An existing enrollment, opened on the step its status starts on. */
      open: (enrollmentId) => `${appRuntime.baseUrl}/create/${enrollmentId}`,
      step: (enrollmentId, step) => `${appRuntime.baseUrl}/create/${enrollmentId}/${step}`
    }
  }
};
const filled = (v) => typeof v === "string" ? v.trim().length > 0 : v != null;
const addressComplete = (a) => ["address1", "city", "state", "postalCode"].every((k) => filled(a?.[k]));
function customerComplete(c) {
  if (!c) return false;
  return filled(c.leadSource) && filled(c.dealer) && filled(c.firstName) && filled(c.lastName) && filled(c.dob) && filled(c.timezone) && onlyDigits(c.phones?.home).length >= 10 && addressComplete(c.address) && addressComplete(shippingAddressOf(c));
}
function productsComplete(p) {
  if (!p) return false;
  const s = p.subscription ?? {};
  const scheduled = filled(s.cadence) && scheduleChosen(s);
  return isProductSelected(p.device) && deviceOptionsComplete(p) && scheduled && Number(s.monthlyCents) > 0;
}
function stepComplete(step, form, { paymentDone: paymentDone2, productsLocked = false }) {
  switch (step) {
    case NewSaleStep.Customer:
      return customerComplete(form.customer);
    case NewSaleStep.Products:
      return productsLocked || productsComplete(form.products);
    case NewSaleStep.Payment:
      return paymentDone2;
    default:
      return true;
  }
}
const PAYMENT_STATUSES = /* @__PURE__ */ new Set([
  EnrollmentStatusType.PaymentScheduled,
  EnrollmentStatusType.PaymentSubmitted,
  EnrollmentStatusType.PaymentFailed,
  EnrollmentStatusType.PaymentReceived
]);
function paymentDone(enrollment) {
  switch (enrollment?.statusType) {
    case EnrollmentStatusType.PaymentScheduled:
    case EnrollmentStatusType.PaymentReceived:
      return true;
    case EnrollmentStatusType.PaymentSubmitted:
      return latestPayment(enrollment)?.statusType === PaymentStatusType.Authorized;
    default:
      return false;
  }
}
function completedFromEnrollment(enrollment) {
  const completed = /* @__PURE__ */ new Set();
  if (!enrollment) return completed;
  if (enrollment.customerId) completed.add(NewSaleStep.Customer);
  if (enrollment.orderId) {
    completed.add(NewSaleStep.Coverage);
    completed.add(NewSaleStep.Products);
  }
  if (enrollment.contacts?.length) completed.add(NewSaleStep.Contacts);
  if (PAYMENT_STATUSES.has(enrollment.statusType)) {
    completed.add(NewSaleStep.Contacts);
    completed.add(NewSaleStep.Medical);
  }
  if (paymentDone(enrollment)) completed.add(NewSaleStep.Payment);
  return completed;
}
const startStep = (enrollment) => PAYMENT_STATUSES.has(enrollment?.statusType) ? NewSaleStep.Payment : NewSaleStep.Customer;
const FINISHED = /* @__PURE__ */ new Set([
  EnrollmentStatusType.Complete,
  EnrollmentStatusType.Cancelled,
  EnrollmentStatusType.Voided
]);
const enrollmentRecentView = (enrollment) => {
  if (!enrollment?.id || FINISHED.has(enrollment.statusType)) return {};
  const customer = enrollment.customer;
  const name = enrollment.customerName || [customer?.firstName, customer?.lastName].filter(Boolean).join(" ").trim();
  return {
    title: name ? `Enrollment · ${name}` : "New sale",
    route: RoutePaths.sales.create.open(enrollment.id)
  };
};
const ACCOUNT_LINK = "accounts.account-link";
const EnrollmentCustomerId = ({ enrollment, className }) => {
  const AccountLink = useComponent(ACCOUNT_LINK);
  const customerId = enrollment?.customerId;
  if (!customerId) return null;
  if (!isEnrollmentComplete(enrollment)) return /* @__PURE__ */ jsx("span", { className, children: customerId });
  return /* @__PURE__ */ jsx(AccountLink, { accountId: customerId, accountType: "customer", className });
};
const CompletedSaleMessage = ({ enrollment }) => /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2 text-sm", children: [
  /* @__PURE__ */ jsxs("p", { children: [
    enrollment?.completedOnUtc ? `This sale was completed on ${parseAndFormatDateTime(enrollment.completedOnUtc)}.` : "This sale is complete.",
    " ",
    "It can no longer be changed here; open the customer to work on their account."
  ] }),
  enrollment?.customerId && /* @__PURE__ */ jsxs("p", { children: [
    /* @__PURE__ */ jsx("span", { className: "text-muted", children: "Customer " }),
    enrollment.customerName && /* @__PURE__ */ jsxs("span", { className: "font-medium", children: [
      enrollment.customerName,
      " "
    ] }),
    /* @__PURE__ */ jsx("span", { className: "text-muted", children: "#" }),
    /* @__PURE__ */ jsx(EnrollmentCustomerId, { enrollment, className: "font-medium" })
  ] })
] });
const CompletedSaleModal = ({ enrollment, onClose }) => /* @__PURE__ */ jsx(Modal.Backdrop, { isOpen: !!enrollment, onOpenChange: (open) => !open && onClose(), children: /* @__PURE__ */ jsx(Modal.Container, { size: "sm", placement: "center", children: /* @__PURE__ */ jsxs(Modal.Dialog, { children: [
  /* @__PURE__ */ jsx(Modal.Header, { children: /* @__PURE__ */ jsxs(Modal.Heading, { children: [
    /* @__PURE__ */ jsx("i", { className: "ri-checkbox-circle-line text-success", "aria-hidden": "true" }),
    " Sale complete"
  ] }) }),
  /* @__PURE__ */ jsx(Modal.Body, { children: /* @__PURE__ */ jsx(CompletedSaleMessage, { enrollment }) }),
  /* @__PURE__ */ jsx(Modal.Footer, { className: "flex justify-end", children: /* @__PURE__ */ jsx(Button, { size: "sm", variant: "tertiary", onPress: onClose, children: "Close" }) })
] }) }) });
const CompletedSaleCard = ({ enrollment, onBack }) => /* @__PURE__ */ jsxs(Card, { className: "w-full max-w-lg", children: [
  /* @__PURE__ */ jsx(Card.Header, { children: /* @__PURE__ */ jsxs(Card.Title, { children: [
    /* @__PURE__ */ jsx("i", { className: "ri-checkbox-circle-line text-success", "aria-hidden": "true" }),
    " Sale complete"
  ] }) }),
  /* @__PURE__ */ jsx(Card.Content, { children: /* @__PURE__ */ jsx(CompletedSaleMessage, { enrollment }) }),
  /* @__PURE__ */ jsx(Card.Footer, { className: "flex justify-end", children: /* @__PURE__ */ jsx(Button, { size: "sm", variant: "tertiary", onPress: onBack, children: "Back to dashboard" }) })
] });
const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const LAST_STEP = NewSaleStep.Review;
const orderFieldsOf = (enrollment) => {
  const order = enrollment?.order;
  return order ? {
    notes: order.notes ?? null,
    discountTotal: order.discountTotal ?? 0,
    shippingTotal: order.shippingTotal ?? 0
  } : {};
};
const shippingRequestAddress = (customer, saved) => {
  const shipping = shippingAddressOf(customer);
  if (!shipping || typeof shipping !== "object") return null;
  const fields = withoutRecordIdentity(shipping);
  return saved?.id ? { ...fields, id: saved.id } : fields;
};
const STEP_BY_SLUG = Object.freeze(
  Object.fromEntries(Object.entries(NewSaleStepType).map(([step, slug]) => [slug, Number(step)]))
);
const NewSaleController = reactive.component(
  ({ services, monitors }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { enrollmentId } = useParams();
    const [form, setForm] = useState(newForm);
    const [sessionCompleted, setSessionCompleted] = useState(() => /* @__PURE__ */ new Set());
    const [enrollment, setEnrollment] = useState(null);
    const [openedComplete, setOpenedComplete] = useState(false);
    useUserContext("accounts.current-dealer");
    const validEnrollment = GUID_PATTERN.test(enrollmentId ?? "");
    useRecentView(enrollmentRecentView(enrollment));
    const slug = location.pathname.split("/").filter(Boolean).pop() ?? "";
    const step = STEP_BY_SLUG[slug] ?? NewSaleStep.Customer;
    useEffect(() => {
      if (validEnrollment) {
        services.enrollments.getById(enrollmentId);
      }
    }, [enrollmentId, validEnrollment]);
    useEffect(() => {
      if (!validEnrollment) {
        navigate(RoutePaths.sales.overview, { replace: true });
        return;
      }
      if (STEP_BY_SLUG[slug] === void 0 && enrollment?.id?.toLowerCase() === enrollmentId.toLowerCase()) {
        navigate(
          RoutePaths.sales.create.step(enrollmentId, NewSaleStepType[startStep(enrollment)]),
          {
            replace: true
          }
        );
      }
    }, [slug, enrollmentId, validEnrollment, enrollment]);
    const completed = useMemo(
      () => /* @__PURE__ */ new Set([...sessionCompleted, ...completedFromEnrollment(enrollment)]),
      [sessionCompleted, enrollment]
    );
    function patchForm(section, patch) {
      setForm((prev) => ({ ...prev, [section]: { ...prev[section], ...patch } }));
    }
    const canAdvance = stepComplete(step, form, {
      paymentDone: paymentDone(enrollment),
      productsLocked: !isOrderOpenForChanges(enrollment)
    });
    const saving = monitors.createCustomer.executing || monitors.updateCustomer.executing || monitors.createOrder.executing || monitors.updateOrder.executing || monitors.saveStationInfo.executing;
    function goTo(newStep) {
      navigate(RoutePaths.sales.create.step(enrollmentId, NewSaleStepType[newStep]));
    }
    const pendingLeave = useRef(null);
    const savedCustomerRequest = useRef(null);
    const savedStationInfo = useRef(null);
    const savedOrderRequest = useRef(null);
    function markOrderSaved(products) {
      savedOrderRequest.current = JSON.stringify(
        buildEnrollmentOrderRequest(enrollmentId, products, orderFieldsOf(enrollment))
      );
    }
    function finishLeave() {
      const leave = pendingLeave.current;
      pendingLeave.current = null;
      if (!leave) return;
      if (leave.complete) setSessionCompleted((prev) => new Set(prev).add(leave.from));
      if (leave.target !== leave.from && leave.target <= LAST_STEP) goTo(leave.target);
    }
    function leaveStep(target, complete) {
      if (saving) return;
      pendingLeave.current = { from: step, target, complete };
      if (!saveStep(step)) finishLeave();
    }
    function increment() {
      if (!canAdvance) return;
      leaveStep(step + 1, true);
    }
    function decrement() {
      step > 0 ? leaveStep(step - 1, false) : navigate(RoutePaths.sales.overview);
    }
    function direct(newStep) {
      if (newStep === step) return;
      if (newStep > step && (!canAdvance || !completed.has(newStep - 1))) return;
      leaveStep(newStep, false);
    }
    function saveStep(fromStep) {
      if (fromStep === NewSaleStep.Customer) return saveCustomer();
      if (fromStep === NewSaleStep.Products) return saveOrder();
      if (fromStep === NewSaleStep.Medical) return saveStationInfo();
      return false;
    }
    function buildCustomerRequest(c, saved = enrollment) {
      return {
        customer: {
          dealerId: typeof c.dealer === "number" ? c.dealer : void 0,
          leadSourceId: typeof c.leadSource === "number" ? c.leadSource : null,
          leadId: c.leadId || null,
          firstName: c.firstName || null,
          middleName: c.middleName || null,
          lastName: c.lastName || null,
          dateOfBirth: c.dob || null,
          email: c.email || null,
          timeZone: c.timezone || null,
          homePhone: c.phones?.home || null,
          workPhone: c.phones?.work || null,
          workPhoneExt: c.phones?.workExt || null,
          mobilePhone: c.phones?.cell || null
        },
        physicalAddress: c.address && typeof c.address === "object" ? c.address : null,
        shippingAddress: shippingRequestAddress(c, saved?.shippingAddress)
      };
    }
    function saveCustomer() {
      const request = buildCustomerRequest(form.customer);
      if (enrollment?.customerId) {
        if (JSON.stringify(request) === savedCustomerRequest.current) return false;
        savedCustomerRequest.current = JSON.stringify(request);
        services.enrollments.updateCustomer(enrollmentId, request);
      } else {
        savedCustomerRequest.current = JSON.stringify(request);
        services.enrollments.createCustomer(enrollmentId, request);
      }
      return true;
    }
    function saveOrder() {
      const products = form.products;
      if (!isProductSelected(products.device)) return false;
      if (!isOrderOpenForChanges(enrollment)) return false;
      if (products.pendingReconcile) return false;
      if (!deviceOptionsComplete(products)) return false;
      const request = buildEnrollmentOrderRequest(
        enrollmentId,
        products,
        orderFieldsOf(enrollment)
      );
      const signature = JSON.stringify(request);
      if (signature === savedOrderRequest.current) return false;
      savedOrderRequest.current = signature;
      if (enrollment?.orderId) {
        services.enrollments.updateOrder(enrollmentId, request);
      } else {
        services.enrollments.createOrder(enrollmentId, request);
      }
      return true;
    }
    function buildStationInfo(m, stored) {
      return {
        ...stored,
        lockboxLocation: m.lockboxLocation || null,
        lockboxCode: m.lockboxCode || null,
        keyLocation: m.keyLocation || null,
        entryInfo: m.entryInfo || null,
        medications: m.medications?.length ? m.medications.join("; ") : null,
        isDisabled: !!m.isDisabled,
        usesOxygen: !!m.usesOxygen,
        preferredHospital: m.preferredHospital || null
      };
    }
    function saveStationInfo() {
      if (!enrollment?.customerId) return false;
      const info = buildStationInfo(form.medical, enrollment.stationInfo);
      const snapshot = JSON.stringify(info);
      if (snapshot === savedStationInfo.current) return false;
      savedStationInfo.current = snapshot;
      services.enrollments.saveStationInfo(enrollmentId, info);
      return true;
    }
    function voidEnrollment() {
      services.enrollments.save(enrollmentId, {
        ...enrollment,
        statusType: EnrollmentStatusType.Voided
      });
    }
    useSuccessReactor(services.enrollments.save, () => navigate(RoutePaths.sales.overview));
    useSuccessReactor(services.enrollments.getById, (payload) => {
      const hydrated = hydrateFormFromEnrollment(payload);
      setEnrollment(payload);
      setOpenedComplete(isEnrollmentComplete(payload));
      setForm(hydrated);
      savedOrderRequest.current = null;
      if (payload.customerId && (payload.shippingAddress || !shippingAddressOf(hydrated.customer))) {
        savedCustomerRequest.current = JSON.stringify(
          buildCustomerRequest(hydrated.customer, payload)
        );
      }
      if (payload.stationInfo) {
        savedStationInfo.current = JSON.stringify(
          buildStationInfo(hydrated.medical, payload.stationInfo)
        );
      }
    });
    useSuccessReactor(services.enrollments.createCustomer, (payload) => {
      setEnrollment(payload);
      finishLeave();
    });
    useSuccessReactor(services.enrollments.updateCustomer, (payload) => {
      setEnrollment(payload);
      finishLeave();
    });
    useSuccessReactor(services.enrollments.createOrder, (payload) => {
      setEnrollment(payload);
      finishLeave();
    });
    useSuccessReactor(services.enrollments.updateOrder, (payload) => {
      setEnrollment(payload);
      finishLeave();
    });
    useSuccessReactor(services.enrollments.saveStationInfo, (payload) => {
      setEnrollment(payload);
      finishLeave();
    });
    const abandonLeave = () => {
      pendingLeave.current = null;
      savedCustomerRequest.current = null;
      savedStationInfo.current = null;
      savedOrderRequest.current = null;
    };
    useErrorReactor(services.enrollments.createCustomer, abandonLeave);
    useErrorReactor(services.enrollments.updateCustomer, abandonLeave);
    useErrorReactor(services.enrollments.createOrder, abandonLeave);
    useErrorReactor(services.enrollments.updateOrder, abandonLeave);
    useErrorReactor(services.enrollments.saveStationInfo, abandonLeave);
    const savingDirection = saving ? (pendingLeave.current?.target ?? step + 1) < step ? "back" : "forward" : null;
    const context = useMemo(
      () => ({ enrollmentId, enrollment, setEnrollment, form, patchForm, markOrderSaved }),
      [enrollmentId, enrollment, form]
    );
    if (!validEnrollment) {
      return null;
    }
    if (openedComplete) {
      return /* @__PURE__ */ jsx("div", { className: "grow flex flex-col items-center overflow-y-auto w-full p-4", children: /* @__PURE__ */ jsx(
        CompletedSaleCard,
        {
          enrollment,
          onBack: () => navigate(RoutePaths.sales.overview)
        }
      ) });
    }
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        NewSaleHeader,
        {
          cancelSale: voidEnrollment,
          cancelling: monitors.save.executing,
          direct,
          step
        }
      ),
      /* @__PURE__ */ jsx("div", { className: "grow flex flex-col items-center overflow-y-auto w-full p-4", children: /* @__PURE__ */ jsx(Outlet, { context }) }),
      /* @__PURE__ */ jsx(
        NewSaleFooter,
        {
          decrement,
          increment,
          step,
          canAdvance,
          saving: savingDirection
        }
      )
    ] });
  },
  {
    name: "NewSaleController",
    monitors: (services) => [
      services.enrollments.createCustomer,
      services.enrollments.updateCustomer,
      services.enrollments.createOrder,
      services.enrollments.updateOrder,
      services.enrollments.saveStationInfo,
      services.enrollments.save
    ]
  }
);
const NewSaleStartController = reactive.component(
  ({ services }) => {
    const navigate = useNavigate();
    const started = useRef(false);
    useEffect(() => {
      if (started.current) return;
      started.current = true;
      services.enrollments.start();
    }, []);
    useSuccessReactor(services.enrollments.start, (enrollment) => {
      navigate(RoutePaths.sales.create.step(enrollment.id, NewSaleStepType[NewSaleStep.Customer]), {
        replace: true
      });
    });
    return /* @__PURE__ */ jsx("div", { className: "grow flex items-center justify-center", children: /* @__PURE__ */ jsxs(Card, { className: "flex flex-row items-center gap-3 px-6 py-4", children: [
      /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
      /* @__PURE__ */ jsx("span", { className: "text-muted", children: "Starting a new sale…" })
    ] }) });
  },
  {
    name: "NewSaleStartController"
  }
);
const LeadSourcesLookup = reactive.component(
  ({ services, monitors, selectionMode, value, ...rest }) => {
    const [sources, setSources] = useState([]);
    useSuccessReactor(services.referralSources.getAll, (list) => setSources(list ?? []));
    const items = sources.filter((s) => s.isActive || s.id === value).sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    return /* @__PURE__ */ jsx(
      SelectField,
      {
        ...rest,
        value,
        items,
        isLoading: monitors.getAll.executing,
        selectionMode: selectionMode || "single",
        children: (s) => /* @__PURE__ */ jsx(ListBox.Item, { id: s.id, textValue: s.name, children: s.name }, s.id)
      }
    );
  },
  {
    name: "LeadSourcesLookup",
    sync: (services) => [{ execute: () => services.referralSources.getAll(), deps: [] }],
    monitors: (services) => [services.referralSources.getAll]
  }
);
const FormSection = ({ title, subtitle, isLast, children }) => /* @__PURE__ */ jsxs(Fragment, { children: [
  /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsx("p", { className: "text-xs font-semibold tracking-wider uppercase text-muted", children: title }),
    /* @__PURE__ */ jsx("p", { className: "text-xs text-muted mt-1", children: subtitle })
  ] }),
  children,
  !isLast && /* @__PURE__ */ jsx(Separator, {})
] });
const CustomerFormLead = ({ data }) => {
  const DealerSearchField = useComponent("accounts.dealer-search-field");
  return /* @__PURE__ */ jsxs(FormSection, { title: "Lead", subtitle: "Where did this customer come from?", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex gap-4", children: [
      /* @__PURE__ */ jsx(
        LeadSourcesLookup,
        {
          className: "flex-1",
          isRequired: true,
          name: "leadSource",
          label: "Lead source",
          value: data.leadSource,
          validation: (x) => x.number().required("Lead source is required")
        }
      ),
      /* @__PURE__ */ jsx(
        TextField,
        {
          className: "flex-1",
          name: "leadId",
          label: "Lead ID",
          value: data.leadId,
          placeholder: "e.g. LD-48201",
          description: "From the CRM, if known"
        }
      )
    ] }),
    /* @__PURE__ */ jsx(
      DealerSearchField,
      {
        isRequired: true,
        name: "dealer",
        label: "Dealer",
        value: data?.dealer,
        validation: (x) => x.string().required("Dealer is required"),
        transformOut: (val) => val != null ? Number(val) : null
      }
    )
  ] });
};
const CustomerFormCustomer = ({ data }) => /* @__PURE__ */ jsxs(FormSection, { title: "Customer", subtitle: "The person who will wear the device.", children: [
  /* @__PURE__ */ jsxs("div", { className: "flex", children: [
    /* @__PURE__ */ jsx(
      TextField,
      {
        isRequired: true,
        name: "firstName",
        label: "First name",
        value: data.firstName,
        validation: (x) => x.string().max(100).required("First name is required")
      }
    ),
    /* @__PURE__ */ jsx(
      TextField,
      {
        name: "middleName",
        label: "Middle name",
        value: data.middleName,
        validation: (x) => x.string().max(50).notRequired()
      }
    ),
    /* @__PURE__ */ jsx(
      TextField,
      {
        isRequired: true,
        name: "lastName",
        label: "Last name",
        value: data.lastName,
        validation: (x) => x.string().max(100).required("Last name is required")
      }
    )
  ] }),
  /* @__PURE__ */ jsxs("div", { className: "flex gap-4", children: [
    /* @__PURE__ */ jsx(
      DateField,
      {
        className: "flex-1",
        isRequired: true,
        name: "dob",
        label: "Date of birth",
        value: data.dob,
        validation: (x) => x.string().required("Date of birth is required")
      }
    ),
    /* @__PURE__ */ jsx(
      USTimeZoneComboBoxField,
      {
        className: "flex-1",
        isRequired: true,
        name: "timezone",
        label: "Timezone",
        value: data.timezone,
        validation: (x) => x.string().required("Timezone is required")
      }
    )
  ] })
] });
const CustomerFormAddress = ({ data }) => /* @__PURE__ */ jsx(
  FormSection,
  {
    title: "Service address",
    subtitle: "Where the device will be used. We'll check cellular coverage in the next step.",
    children: /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(AddressForm, { name: "address", value: data?.address }) })
  }
);
const CustomerFormShipping = ({ data, onSameAsServiceChange = () => {
} }) => {
  const sameAsService = data?.shippingSameAsService !== false;
  return /* @__PURE__ */ jsxs(FormSection, { title: "Shipping address", subtitle: "Where we'll send the device.", children: [
    /* @__PURE__ */ jsx(Checkbox, { isSelected: sameAsService, onChange: onSameAsServiceChange, children: /* @__PURE__ */ jsxs(Checkbox.Content, { children: [
      /* @__PURE__ */ jsx(Checkbox.Control, { children: /* @__PURE__ */ jsx(Checkbox.Indicator, {}) }),
      /* @__PURE__ */ jsx(Label, { children: "Same as Service address" })
    ] }) }),
    !sameAsService && /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(AddressForm, { name: "shippingAddress", value: data?.shippingAddress }) })
  ] });
};
const CustomerFormPhones = ({ data }) => /* @__PURE__ */ jsx(
  FormSection,
  {
    title: "Contact details",
    subtitle: "At least one number is required. The cell number is what we'll reach on the device.",
    children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4", children: [
      /* @__PURE__ */ jsx(
        EmailField,
        {
          className: "!px-0",
          name: "email",
          label: "Email",
          value: data?.email || "",
          validationMessage: "Invalid email"
        }
      ),
      /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-1", children: /* @__PURE__ */ jsx(
        PhoneField,
        {
          isRequired: true,
          name: "home",
          label: "Home",
          value: data?.phones?.home || "",
          validation: (x) => x.string().required("Home phone is required")
        }
      ) }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-2", children: [
        /* @__PURE__ */ jsx("div", { className: "flex min-w-0 flex-1 flex-col gap-1", children: /* @__PURE__ */ jsx(PhoneField, { name: "work", label: "Work", value: data?.phones?.work || "" }) }),
        /* @__PURE__ */ jsx(
          TextField,
          {
            className: "w-24",
            name: "workExt",
            label: "Ext.",
            placeholder: "Ext.",
            inputMode: "numeric",
            maxLength: 10,
            value: data?.phones?.workExt || "",
            validation: (x) => x.string().matches(/^\d*$/, "Extension must be digits only").test(
              "needs-work-phone",
              "Extension needs a work phone",
              (ext, { options }) => !ext || !!options.context?.work
            )
          }
        )
      ] }),
      /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-1", children: /* @__PURE__ */ jsx(
        PhoneField,
        {
          name: "cell",
          label: "Cell",
          value: data?.phones?.cell || ""
        }
      ) }),
      /* @__PURE__ */ jsx("span", { className: "text-warning", children: "Email and/or Cell are required to send an automated order confirmation" })
    ] })
  }
);
const CustomerFormNotes = ({ data }) => /* @__PURE__ */ jsx(
  FormSection,
  {
    isLast: true,
    title: "Agent notes",
    subtitle: "Why is the customer interested? Anything an agent picking this up later should know.",
    children: /* @__PURE__ */ jsx(
      TextAreaField,
      {
        name: "notes",
        label: "Reason for interest",
        value: data.notes,
        placeholder: "Daughter called in. Mom (Margaret) had a fall last month while gardening. Family is in NY, Mom lives alone in OH. Looking for fall detection + GPS.",
        minRows: 5,
        maxLength: 1e3,
        description: `${(data.notes || "").length}/1000`,
        validation: (x) => x.string().max(1e3).notRequired()
      }
    )
  }
);
const sliceFromForm$1 = (d, prev) => ({
  leadSource: d.leadSource ?? prev.leadSource,
  leadId: d.leadId ?? prev.leadId,
  dealer: d.dealer ?? prev.dealer,
  firstName: d.firstName ?? prev.firstName,
  middleName: d.middleName ?? prev.middleName,
  lastName: d.lastName ?? prev.lastName,
  dob: d.dob ?? prev.dob,
  email: d.email ?? prev.email,
  timezone: d.timezone ?? prev.timezone,
  address: d.address ?? prev.address,
  shippingSameAsService: prev.shippingSameAsService,
  shippingAddress: d.shippingAddress ?? prev.shippingAddress,
  notes: d.notes ?? prev.notes,
  phones: {
    home: d.home ?? prev.phones?.home ?? "",
    work: d.work ?? prev.phones?.work ?? "",
    workExt: d.workExt ?? prev.phones?.workExt ?? "",
    cell: d.cell ?? prev.phones?.cell ?? ""
  }
});
const CustomerForm = ({ data = {}, onChange = () => {
} }) => /* @__PURE__ */ jsx(Card, { children: /* @__PURE__ */ jsxs(
  Uniformly,
  {
    className: "flex flex-col gap-4",
    onChange: (result) => onChange(sliceFromForm$1(result.data, data)),
    children: [
      /* @__PURE__ */ jsx(CustomerFormLead, { data }),
      /* @__PURE__ */ jsx(CustomerFormCustomer, { data }),
      /* @__PURE__ */ jsx(CustomerFormAddress, { data }),
      /* @__PURE__ */ jsx(
        CustomerFormShipping,
        {
          data,
          onSameAsServiceChange: (shippingSameAsService) => onChange({ ...data, shippingSameAsService })
        }
      ),
      /* @__PURE__ */ jsx(CustomerFormPhones, { data }),
      /* @__PURE__ */ jsx(CustomerFormNotes, { data })
    ]
  }
) });
const addressKey = (a) => a ? [a.address1, a.city, a.state, a.postalCode].join("|") : "";
const CustomerController = () => {
  const { form, patchForm } = useOutletContext();
  const handleChange = (patch) => {
    patchForm("customer", patch);
    if (addressKey(patch.address) !== addressKey(form.customer?.address)) {
      patchForm("cellular", { coverage: null });
    }
  };
  return /* @__PURE__ */ jsx(CustomerForm, { data: form.customer, onChange: handleChange });
};
const CoverageFormAddress = ({
  address: addressProp,
  checking = false,
  onRecheck = () => {
  }
}) => {
  const address = addressProp || {};
  const line2 = `${address.city}, ${address.state} ${address.postalCode}`;
  return /* @__PURE__ */ jsx(Card, { className: "w-full", children: /* @__PURE__ */ jsxs(CardContent, { className: "flex-row items-center gap-3.5 py-3.5", children: [
    /* @__PURE__ */ jsx("span", { className: "w-9 h-9 rounded-lg bg-accent-soft text-accent inline-flex items-center justify-center flex-none", children: /* @__PURE__ */ jsx("i", { className: "ri-map-pin-2-fill" }) }),
    /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
      /* @__PURE__ */ jsx("p", { className: "text-xs font-semibold tracking-widest uppercase text-muted", children: "Service address" }),
      /* @__PURE__ */ jsx("p", { className: "text-sm font-semibold mt-0.5", children: address.address1 }),
      /* @__PURE__ */ jsx("p", { className: "text-xs text-muted", children: line2 })
    ] }),
    /* @__PURE__ */ jsx(
      Button,
      {
        variant: "bordered",
        size: "sm",
        onPress: onRecheck,
        isDisabled: checking,
        isLoading: checking,
        startContent: !checking ? /* @__PURE__ */ jsx("i", { className: "ri-radar-line" }) : void 0,
        children: checking ? "Checking…" : "Re-check coverage"
      }
    )
  ] }) });
};
const EmptyAddressState = () => /* @__PURE__ */ jsx("div", { className: "max-w-[760px] mx-auto", children: /* @__PURE__ */ jsx(Card, { className: "w-full", children: /* @__PURE__ */ jsxs(CardContent, { className: "items-center py-10 text-center gap-3", children: [
  /* @__PURE__ */ jsx("i", { className: "ri-map-pin-line text-muted" }),
  /* @__PURE__ */ jsx("h3", { className: "text-base font-semibold m-0", children: "No service address yet" }),
  /* @__PURE__ */ jsx("p", { className: "m-0 text-sm text-muted", children: "Go back to step 1 and add a service address — we need it to check coverage." })
] }) }) });
const CoverageForm = ({
  data = {},
  address = null,
  towers = [],
  onChange = () => {
  },
  onTowersChange = () => {
  }
}) => {
  const CoverageMap = useComponent("cellular.coverage-map");
  if (!address?.address1) return /* @__PURE__ */ jsx(EmptyAddressState, {});
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4 w-full h-full", children: [
    /* @__PURE__ */ jsx(CoverageFormAddress, { address }),
    /* @__PURE__ */ jsx(
      CoverageMap,
      {
        address,
        carriers: data.carriers || { att: true, tmobile: true },
        onCarriersChange: (carriers) => onChange({ carriers }),
        onCoverageChange: (coverage) => onChange({ coverage }),
        onTowersChange
      }
    )
  ] });
};
const CoverageController = () => {
  const { form, patchForm } = useOutletContext();
  const [towers, setTowers] = useState([]);
  return /* @__PURE__ */ jsx(
    CoverageForm,
    {
      data: form.cellular,
      address: form.customer?.address,
      towers,
      onChange: (patch) => patchForm("cellular", patch),
      onTowersChange: setTowers
    }
  );
};
function applyFocus(el) {
  el.style.borderColor = "var(--border-focus)";
  el.style.boxShadow = "var(--shadow-focus)";
}
function clearFocus(el) {
  el.style.borderColor = "var(--border-default-c)";
  el.style.boxShadow = "none";
}
const INPUT_BASE = {
  width: "100%",
  height: 36,
  padding: "0 12px",
  fontFamily: "var(--font-sans)",
  fontSize: 13.5,
  color: "var(--fg-1)",
  background: "var(--bg-input)",
  border: "1px solid var(--border-default-c)",
  borderRadius: "var(--radius-md)",
  outline: "none",
  transition: "border-color var(--dur-fast), box-shadow var(--dur-fast)",
  boxSizing: "border-box"
};
function SubConfigLabel({ children, required }) {
  return /* @__PURE__ */ jsxs("p", { className: "text-xs font-semibold text-muted mb-2", children: [
    children,
    required && /* @__PURE__ */ jsx("span", { className: "text-danger ml-0.5", children: "*" })
  ] });
}
function SubtotalRow({ label, value, suffix }) {
  return /* @__PURE__ */ jsxs("div", { className: "mt-2.5 pt-2.5 flex items-center justify-between border-t border-dashed border-border", children: [
    /* @__PURE__ */ jsxs("span", { className: "text-xs text-muted font-medium", children: [
      label,
      " subtotal"
    ] }),
    /* @__PURE__ */ jsxs("span", { className: "text-sm font-bold text-foreground tabular-nums", children: [
      formatCents(value),
      suffix && /* @__PURE__ */ jsx("span", { className: "text-xs font-medium text-muted ml-0.5", children: suffix })
    ] })
  ] });
}
function PriceInput({ value, defaultPrice, onChange, disabled, suffix, width = 96 }) {
  const overridden = !disabled && (value ?? 0) !== defaultPrice;
  const [text, setText] = useState(() => centsToText(value));
  useEffect(() => {
    setText(centsToText(value));
  }, [value]);
  const commit = () => {
    const cents = parseCents(text);
    onChange(cents);
    setText(centsToText(cents));
  };
  return /* @__PURE__ */ jsxs("div", { className: "inline-flex items-center gap-1", onClick: (e) => e.stopPropagation(), children: [
    /* @__PURE__ */ jsxs("div", { className: "relative inline-flex", children: [
      /* @__PURE__ */ jsx("span", { className: "absolute left-2 top-1/2 -translate-y-1/2 text-muted text-xs pointer-events-none", children: "$" }),
      /* @__PURE__ */ jsx(
        "input",
        {
          type: "text",
          inputMode: "decimal",
          disabled,
          value: text,
          onChange: (e) => setText(e.target.value),
          onFocus: (e) => {
            applyFocus(e.target);
            e.target.select();
          },
          onBlur: (e) => {
            clearFocus(e.target);
            commit();
          },
          onKeyDown: (e) => {
            if (e.key === "Enter") e.target.blur();
          },
          style: {
            ...INPUT_BASE,
            width,
            height: 30,
            padding: "0 8px 0 18px",
            paddingRight: suffix ? 30 : 8,
            fontSize: 13,
            fontVariantNumeric: "tabular-nums",
            textAlign: "right",
            color: disabled ? "var(--fg-3)" : overridden ? "var(--lf-warning-600)" : "var(--fg-1)",
            borderColor: overridden ? "var(--lf-warning-500)" : "var(--border-default-c)",
            fontWeight: overridden ? 600 : 500,
            opacity: disabled ? 0.55 : 1,
            cursor: disabled ? "not-allowed" : "text"
          }
        }
      ),
      suffix && /* @__PURE__ */ jsx("span", { className: "absolute right-2 top-1/2 -translate-y-1/2 text-muted text-xs pointer-events-none", children: suffix })
    ] }),
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: () => onChange(defaultPrice),
        title: overridden ? `Revert to ${formatCents(defaultPrice)}` : "At default",
        disabled: !overridden || disabled,
        className: "inline-flex items-center justify-center w-5.5 h-5.5 rounded bg-transparent border-none cursor-pointer text-muted hover:bg-background hover:text-foreground transition-colors",
        style: { visibility: overridden && !disabled ? "visible" : "hidden" },
        children: /* @__PURE__ */ jsx("i", { className: "ri-refresh-line" })
      }
    )
  ] });
}
function ProductCard({ device, product, selected, onSelect, loading }) {
  const price = product.suggestedPrice;
  const name = ProductDisplay[device] ?? product.displayName ?? product.name;
  return /* @__PURE__ */ jsx(
    Card,
    {
      onClick: onSelect,
      className: `w-full cursor-pointer transition-all ${selected ? "border-accent border-[1.5px] shadow-md" : "border border-border shadow-sm"}`,
      children: /* @__PURE__ */ jsxs(CardContent, { className: "flex flex-col gap-2.5 p-4 relative", children: [
        /* @__PURE__ */ jsx(
          "span",
          {
            className: `absolute top-3.5 right-3.5 w-4.5 h-4.5 rounded-full border-[1.5px] inline-flex items-center justify-center ${selected ? "border-accent bg-accent text-white" : "border-border bg-transparent"}`,
            children: selected && /* @__PURE__ */ jsx("i", { className: "ri-check-line" })
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2.5 pr-7", children: [
          /* @__PURE__ */ jsx(
            "span",
            {
              className: `w-9 h-9 rounded-md inline-flex items-center justify-center flex-none transition-colors ${selected ? "bg-accent text-white" : "bg-background text-muted"}`,
              children: /* @__PURE__ */ jsx("i", { className: ProductIcon[device] ?? "ri-device-line" })
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
            /* @__PURE__ */ jsx("p", { className: "text-base font-semibold text-foreground leading-tight", children: name }),
            ProductLocation[device] && /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted mt-0.5", children: [
              ProductLocation[device].join(" · "),
              " · ",
              ProductConnectivity[device]
            ] })
          ] })
        ] }),
        /* @__PURE__ */ jsx("p", { className: "text-xs text-muted leading-relaxed", children: ProductDescription[device] ?? product.shortDescription }),
        /* @__PURE__ */ jsx("ul", { className: "m-0 p-0 list-none flex flex-col gap-1 flex-1", children: (ProductFeatures[device] ?? []).map((f, i) => /* @__PURE__ */ jsxs("li", { className: "flex items-start gap-1.5", children: [
          /* @__PURE__ */ jsx(
            "i",
            {
              className: `ri-check-line mt-0.5 flex-none ${selected ? "text-accent" : "text-muted"}`
            }
          ),
          /* @__PURE__ */ jsx("span", { className: "text-xs text-muted", children: f })
        ] }, i)) }),
        /* @__PURE__ */ jsxs("div", { className: "mt-1 pt-2.5 border-t border-separator flex items-center justify-between", children: [
          /* @__PURE__ */ jsx("span", { className: "text-xs text-muted uppercase tracking-wider font-semibold", children: "Included in subscription" }),
          loading ? /* @__PURE__ */ jsx("span", { className: "text-xs text-muted", children: "—" }) : price != null ? /* @__PURE__ */ jsxs(
            "span",
            {
              className: `text-sm font-bold tabular-nums ${selected ? "text-accent" : "text-muted"}`,
              children: [
                "$",
                price.toFixed(2),
                /* @__PURE__ */ jsx("span", { className: "text-xs font-normal text-muted", children: "/mo" })
              ]
            }
          ) : null
        ] })
      ] })
    }
  );
}
function CoverageNote({ covered }) {
  if (covered === true) {
    return /* @__PURE__ */ jsxs("p", { className: "text-xs text-success inline-flex items-center gap-1 mt-0.5", children: [
      /* @__PURE__ */ jsx("i", { className: "ri-checkbox-circle-fill" }),
      "Coverage confirmed at service address"
    ] });
  }
  if (covered === false) {
    return /* @__PURE__ */ jsxs("p", { className: "text-xs text-danger inline-flex items-center gap-1 mt-0.5", children: [
      /* @__PURE__ */ jsx("i", { className: "ri-close-circle-fill" }),
      "No coverage at service address"
    ] });
  }
  return /* @__PURE__ */ jsx("p", { className: "text-xs text-muted mt-0.5", children: "Not checked in step 2" });
}
function ValueMark({ decor, active }) {
  if (decor?.badge) {
    return /* @__PURE__ */ jsx(
      "span",
      {
        className: "w-8 h-8 rounded-md inline-flex items-center justify-center flex-none text-white text-xs font-bold tracking-wide",
        style: { background: decor.color },
        children: decor.badge
      }
    );
  }
  if (!decor?.icon) return null;
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: `w-8 h-8 rounded-md inline-flex items-center justify-center flex-none transition-colors ${active ? "bg-accent text-white" : "bg-background text-muted"}`,
      children: /* @__PURE__ */ jsx("i", { className: decor.icon })
    }
  );
}
const humanize = (name) => {
  const words = name.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};
function OptionPicker({ option, value, onChange, cellular }) {
  const decorFor = OptionValueDecor[option.name] ?? {};
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsx(SubConfigLabel, { required: option.required, children: OptionLabel[option.name] ?? humanize(option.name) }),
    /* @__PURE__ */ jsx("div", { className: "grid grid-cols-2 gap-2.5", children: option.values.map((v) => {
      const active = value === v.name;
      const decor = decorFor[v.name];
      return /* @__PURE__ */ jsxs(
        "button",
        {
          type: "button",
          onClick: () => onChange(v.name),
          className: `flex items-center gap-3 px-3 py-2.5 rounded-xl border-[1.5px] cursor-pointer text-left transition-colors ${active ? "bg-accent-soft border-accent" : "bg-surface border-border hover:border-border"}`,
          children: [
            /* @__PURE__ */ jsx(ValueMark, { decor, active }),
            /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
              /* @__PURE__ */ jsx("p", { className: "text-sm font-semibold text-foreground", children: v.name }),
              decor?.coverageKey ? /* @__PURE__ */ jsx(CoverageNote, { covered: cellular?.coverage?.[decor.coverageKey] }) : decor?.description && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted", children: decor.description })
            ] })
          ]
        },
        v.id
      );
    }) })
  ] });
}
function DeviceSection({
  device,
  deviceOptions,
  options = {},
  cellular,
  devices = [],
  loading,
  onDeviceSelect,
  onOptionChange
}) {
  const hasDevice = isProductSelected(device);
  const choices = hasDevice ? choiceOptions(deviceOptions) : [];
  const name = ProductDisplay[device] ?? devices.find((d) => d.device === device)?.product.displayName;
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    loading && devices.length === 0 ? /* @__PURE__ */ jsx("p", { className: "text-sm text-muted", children: "Loading devices…" }) : /* @__PURE__ */ jsx("div", { className: "grid grid-cols-3 gap-3 items-stretch", children: devices.map((d) => /* @__PURE__ */ jsx(
      ProductCard,
      {
        device: d.device,
        product: d.product,
        selected: device === d.device,
        onSelect: () => onDeviceSelect(d.device),
        loading
      },
      d.product.id
    )) }),
    choices.length > 0 && /* @__PURE__ */ jsxs("div", { className: "mt-3.5 p-4 bg-background border border-border rounded-xl flex flex-col gap-4", children: [
      /* @__PURE__ */ jsxs("p", { className: "text-xs font-semibold tracking-wider uppercase text-muted flex items-center gap-1.5", children: [
        /* @__PURE__ */ jsx("i", { className: "ri-settings-3-line" }),
        name,
        " configuration"
      ] }),
      choices.map((option) => /* @__PURE__ */ jsx(
        OptionPicker,
        {
          option,
          value: options[option.name],
          onChange: (value) => onOptionChange(option.name, value),
          cellular
        },
        option.id
      ))
    ] }),
    hasDevice && deviceOptions && choices.length === 0 && /* @__PURE__ */ jsxs("div", { className: "mt-3.5 px-3.5 py-3 bg-background border border-dashed border-border rounded-xl flex items-center gap-2.5 text-sm text-muted", children: [
      /* @__PURE__ */ jsx("i", { className: "ri-information-line text-muted flex-none" }),
      /* @__PURE__ */ jsxs("span", { children: [
        /* @__PURE__ */ jsx("strong", { className: "text-foreground", children: name }),
        " has no options to choose."
      ] })
    ] })
  ] });
}
const ProductsFormDevice = ({
  data,
  cellular,
  devices = [],
  loading = false,
  onDeviceSelect = () => {
  },
  onOptionChange = () => {
  }
}) => /* @__PURE__ */ jsx(
  FormSection,
  {
    title: "Primary device",
    subtitle: "Pick the device the customer will wear or place in their home.",
    children: /* @__PURE__ */ jsx(
      DeviceSection,
      {
        device: data.device,
        deviceOptions: data.deviceOptions,
        options: data.options,
        cellular,
        devices,
        loading,
        onDeviceSelect,
        onOptionChange
      }
    )
  }
);
function ToggleRow({ enabled, onToggle, icon, name, desc, right }) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      onClick: onToggle,
      className: `flex items-center gap-3 px-3.5 py-2.5 rounded-xl border cursor-pointer transition-colors ${enabled ? "bg-surface border-border" : "bg-background border-border"}`,
      children: [
        /* @__PURE__ */ jsx(
          "span",
          {
            className: `w-4.5 h-4.5 rounded flex-none inline-flex items-center justify-center ${enabled ? "bg-accent border-accent" : "bg-surface border-border"} border-[1.5px] text-white`,
            children: enabled && /* @__PURE__ */ jsx("i", { className: "ri-check-line" })
          }
        ),
        icon && /* @__PURE__ */ jsx(
          "span",
          {
            className: `w-8 h-8 rounded-md inline-flex items-center justify-center flex-none bg-background ${enabled ? "text-foreground" : "text-muted"}`,
            children: /* @__PURE__ */ jsx("i", { className: icon })
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
          /* @__PURE__ */ jsx("p", { className: `text-sm font-semibold ${enabled ? "text-foreground" : "text-muted"}`, children: name }),
          desc && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted mt-0.5", children: desc })
        ] }),
        right
      ]
    }
  );
}
const priceOf = (value, def) => value?.priceCents ?? def.defaultPriceCents;
function ServiceRow({ def, value, onChange }) {
  return /* @__PURE__ */ jsx(
    ToggleRow,
    {
      enabled: !!value?.enabled,
      onToggle: () => onChange({ enabled: !value?.enabled, priceCents: priceOf(value, def) }),
      icon: def.icon,
      name: def.name,
      desc: def.desc,
      right: /* @__PURE__ */ jsx(
        PriceInput,
        {
          value: priceOf(value, def),
          defaultPrice: def.defaultPriceCents,
          onChange: (p) => onChange({ priceCents: p }),
          disabled: !value?.enabled,
          suffix: "/mo",
          width: 110
        }
      )
    }
  );
}
function FeeRow({ def, value, onChange }) {
  return /* @__PURE__ */ jsx(
    ToggleRow,
    {
      enabled: !!value?.enabled,
      onToggle: () => onChange({ enabled: !value?.enabled, priceCents: priceOf(value, def) }),
      name: def.name,
      desc: def.desc,
      right: /* @__PURE__ */ jsx(
        PriceInput,
        {
          value: priceOf(value, def),
          defaultPrice: def.defaultPriceCents,
          onChange: (p) => onChange({ priceCents: p }),
          disabled: !value?.enabled,
          width: 96
        }
      )
    }
  );
}
function ServicesRows({ services, addonServices, onServiceChange }) {
  return /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-2", children: addonServices.map((s) => {
    const key = String(s.id);
    const def = { id: key, name: s.name, defaultPriceCents: toCents(s.suggestedPrice) };
    return /* @__PURE__ */ jsx(
      ServiceRow,
      {
        def,
        value: services[key],
        onChange: (patch) => onServiceChange(key, patch)
      },
      key
    );
  }) });
}
function FeesRows({ fees, feeProducts, onFeeChange }) {
  return /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-2", children: feeProducts.map((f) => {
    const key = String(f.id);
    const def = {
      id: key,
      name: f.name,
      desc: f.shortDescription !== f.name ? f.shortDescription || "" : "",
      defaultPriceCents: toCents(f.suggestedPrice)
    };
    return /* @__PURE__ */ jsx(
      FeeRow,
      {
        def,
        value: fees[key],
        onChange: (patch) => onFeeChange(key, patch)
      },
      key
    );
  }) });
}
const ProductsFormServices = ({ data, addonServices = [], onServiceChange = () => {
} }) => {
  const services = data.services || {};
  const servicesMonthly = selectedPriced(services).reduce((sum, s) => sum + s.priceCents, 0);
  return /* @__PURE__ */ jsx(
    FormSection,
    {
      title: "Add-on services",
      subtitle: "Monthly add-ons. Each contributes to the subscription total but is reported separately.",
      children: /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx(
          ServicesRows,
          {
            services,
            addonServices,
            onServiceChange
          }
        ),
        /* @__PURE__ */ jsx(SubtotalRow, { label: "Monthly add-ons", value: servicesMonthly, suffix: "/mo" })
      ] })
    }
  );
};
const ProductsFormFees = ({ data, feeProducts = [], onFeeChange = () => {
} }) => {
  const fees = data.fees || {};
  const feesOneTime = selectedPriced(fees).reduce((sum, f) => sum + f.priceCents, 0);
  return /* @__PURE__ */ jsx(
    FormSection,
    {
      title: "One-time fees",
      subtitle: "Applied to the first invoice only. Uncheck to waive, or type to override.",
      children: /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx(FeesRows, { fees, feeProducts, onFeeChange }),
        /* @__PURE__ */ jsx(SubtotalRow, { label: "One-time fees", value: feesOneTime })
      ] })
    }
  );
};
function AccessoryLookup({ selected, catalog, onAdd }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const selectedIds = new Set(selected.map((a) => a.id));
  const matches = catalog.filter((a) => {
    if (selectedIds.has(a.id)) return false;
    if (!search) return true;
    return (a.name + " " + a.sku + " " + (a.shortDescription || "")).toLowerCase().includes(search.toLowerCase());
  });
  return /* @__PURE__ */ jsxs("div", { className: "relative", children: [
    /* @__PURE__ */ jsxs("div", { className: "relative", children: [
      /* @__PURE__ */ jsx(
        "input",
        {
          type: "text",
          value: search,
          placeholder: "Search accessories by name or SKU…",
          onChange: (e) => {
            setSearch(e.target.value);
            setOpen(true);
          },
          onFocus: (e) => {
            applyFocus(e.target);
            setOpen(true);
          },
          onBlur: (e) => {
            clearFocus(e.target);
            setTimeout(() => setOpen(false), 150);
          },
          style: { ...INPUT_BASE, paddingLeft: 34 }
        }
      ),
      /* @__PURE__ */ jsx("span", { className: "absolute left-2.5 top-1/2 -translate-y-1/2 text-muted pointer-events-none inline-flex", children: /* @__PURE__ */ jsx("i", { className: "ri-search-line" }) })
    ] }),
    open && /* @__PURE__ */ jsx("div", { className: "absolute top-10 left-0 right-0 z-10 bg-surface border border-border rounded-xl shadow-md overflow-hidden max-h-72 overflow-y-auto", children: matches.length === 0 ? /* @__PURE__ */ jsx("p", { className: "p-3.5 text-sm text-muted", children: search ? `No accessories match "${search}".` : "All catalog accessories have been added." }) : matches.map((a, i) => /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        onMouseDown: (e) => {
          e.preventDefault();
          onAdd(a.id);
          setSearch("");
          setOpen(false);
        },
        className: `flex items-center gap-3 w-full px-3 py-2.5 bg-transparent border-none cursor-pointer text-left hover:bg-surface-secondary ${i < matches.length - 1 ? "border-b border-border" : ""}`,
        children: [
          /* @__PURE__ */ jsx("span", { className: "font-mono text-xs text-muted w-14 flex-none", children: a.sku }),
          /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
            /* @__PURE__ */ jsx("p", { className: "text-sm font-medium text-foreground", children: a.name }),
            /* @__PURE__ */ jsx("p", { className: "text-xs text-muted mt-0.5", children: a.shortDescription })
          ] }),
          /* @__PURE__ */ jsx("p", { className: "text-xs text-muted font-semibold tabular-nums", children: formatDollars(a.suggestedPrice) })
        ]
      },
      a.id
    )) })
  ] });
}
function QtyStepper$1({ value, onChange, min = 1, max = 99 }) {
  return /* @__PURE__ */ jsxs("div", { className: "inline-flex items-center border border-border rounded-lg bg-background overflow-hidden h-8 flex-none", children: [
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: () => onChange(Math.max(min, value - 1)),
        disabled: value <= min,
        className: `w-6.5 h-7 bg-transparent border-none inline-flex items-center justify-center text-muted transition-opacity ${value <= min ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:bg-surface-secondary"}`,
        children: /* @__PURE__ */ jsx("i", { className: "ri-subtract-line" })
      }
    ),
    /* @__PURE__ */ jsx("span", { className: "w-6.5 text-center text-sm font-semibold text-foreground tabular-nums", children: value }),
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: () => onChange(Math.min(max, value + 1)),
        disabled: value >= max,
        className: `w-6.5 h-7 bg-transparent border-none inline-flex items-center justify-center text-muted transition-opacity ${value >= max ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:bg-surface-secondary"}`,
        children: /* @__PURE__ */ jsx("i", { className: "ri-add-line" })
      }
    )
  ] });
}
function AccessoryRow({ item, onChange, onRemove }) {
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3 px-3.5 py-2.5 bg-surface border border-border rounded-xl", children: [
    /* @__PURE__ */ jsx("span", { className: "font-mono text-xs text-muted w-14 flex-none", children: item.sku }),
    /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
      /* @__PURE__ */ jsx("p", { className: "text-sm font-semibold text-foreground", children: item.name }),
      /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted mt-0.5 tabular-nums", children: [
        "Line total:",
        " ",
        /* @__PURE__ */ jsx("span", { className: "text-muted font-semibold", children: formatCents(item.priceCents * item.qty) })
      ] })
    ] }),
    /* @__PURE__ */ jsx(QtyStepper$1, { value: item.qty, onChange: (q) => onChange({ qty: q }) }),
    /* @__PURE__ */ jsx(
      PriceInput,
      {
        value: item.priceCents,
        defaultPrice: item.defaultPriceCents,
        onChange: (p) => onChange({ priceCents: p }),
        width: 96
      }
    ),
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: onRemove,
        className: "w-8 h-8 rounded-lg bg-transparent border border-border cursor-pointer text-muted flex-none inline-flex items-center justify-center hover:bg-danger-soft hover:border-danger hover:text-danger transition-colors",
        title: "Remove",
        children: /* @__PURE__ */ jsx("i", { className: "ri-close-line" })
      }
    )
  ] });
}
function AccessorySection({
  accessories,
  accessoryCatalog,
  accessoriesOneTime,
  onAdd,
  onUpdate,
  onRemove
}) {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(AccessoryLookup, { selected: accessories, catalog: accessoryCatalog, onAdd }),
    accessories.length > 0 && /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-2 mt-3.5", children: accessories.map((a) => /* @__PURE__ */ jsx(
      AccessoryRow,
      {
        item: a,
        onChange: (patch) => onUpdate(a.id, patch),
        onRemove: () => onRemove(a.id)
      },
      a.id
    )) }),
    accessories.length > 0 && /* @__PURE__ */ jsx(SubtotalRow, { label: "Accessories", value: accessoriesOneTime }),
    accessories.length === 0 && /* @__PURE__ */ jsx("p", { className: "mt-3 py-2.5 px-3 text-xs text-muted text-center", children: "No accessories added." })
  ] });
}
const ProductsFormAccessories = ({
  data,
  accessoryCatalog = [],
  onAdd = () => {
  },
  onUpdate = () => {
  },
  onRemove = () => {
  }
}) => {
  const accessories = data.accessories || [];
  const accessoriesOneTime = accessories.reduce(
    (sum, a) => sum + (a.priceCents || 0) * (a.qty || 0),
    0
  );
  return /* @__PURE__ */ jsx(
    FormSection,
    {
      title: "Accessories & other items",
      subtitle: "Look up additional hardware or extras. Each is a one-time charge.",
      children: /* @__PURE__ */ jsx(
        AccessorySection,
        {
          accessories,
          accessoryCatalog,
          accessoriesOneTime,
          onAdd,
          onUpdate,
          onRemove
        }
      )
    }
  );
};
function InlineSelect({ value, onChange, options, width = 110 }) {
  return /* @__PURE__ */ jsxs("div", { className: "relative inline-block", style: { width }, children: [
    /* @__PURE__ */ jsxs(
      "select",
      {
        value: value == null ? "" : String(value),
        onChange: (e) => onChange(e.target.value),
        onFocus: (e) => applyFocus(e.target),
        onBlur: (e) => clearFocus(e.target),
        style: {
          ...INPUT_BASE,
          height: 32,
          padding: "0 26px 0 10px",
          appearance: "none",
          fontSize: 13,
          fontWeight: 600,
          color: "var(--fg-1)",
          background: "var(--bg-surface)",
          cursor: "pointer"
        },
        children: [
          value == null && /* @__PURE__ */ jsx("option", { value: "", disabled: true, children: "Select…" }),
          options.map((o) => /* @__PURE__ */ jsx("option", { value: o.value, children: o.label }, o.value))
        ]
      }
    ),
    /* @__PURE__ */ jsx("span", { className: "absolute right-2 top-1/2 -translate-y-1/2 text-muted pointer-events-none inline-flex", children: /* @__PURE__ */ jsx("i", { className: "ri-arrow-down-s-line" }) })
  ] });
}
function CadencePicker({ value, onChange, cadences = CADENCES }) {
  return /* @__PURE__ */ jsx(
    RadioButtonGroup,
    {
      "aria-label": "Billing cadence",
      name: "cadence",
      layout: "grid",
      value: value ?? null,
      onChange,
      className: "grid-cols-3",
      children: cadences.map((c) => /* @__PURE__ */ jsxs(RadioButtonGroup.Item, { value: c.id, "aria-label": c.label, children: [
        /* @__PURE__ */ jsx(RadioButtonGroup.Indicator, {}),
        /* @__PURE__ */ jsxs(RadioButtonGroup.ItemContent, { children: [
          /* @__PURE__ */ jsx(Label, { children: c.label }),
          /* @__PURE__ */ jsx(Description, { children: c.hint }),
          /* @__PURE__ */ jsxs("p", { className: "mt-1 text-xs text-muted uppercase tracking-wider font-semibold", children: [
            c.perYear,
            " ",
            c.perYear === 1 ? "payment" : "payments",
            " / year"
          ] })
        ] })
      ] }, c.id))
    }
  );
}
const SCHEDULE_MODES = [
  { id: "dayOfMonth", label: "Specific day of month" },
  { id: "dayOfWeek", label: "Specific week & weekday" }
];
function BillingSchedule({ value, onChange }) {
  const isDayOfWeek = value.scheduleMode === "dayOfWeek";
  const { cad, nextDate } = calcBillingDates({ subscription: value });
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3", children: [
    /* @__PURE__ */ jsx(
      RadioButtonGroup,
      {
        "aria-label": "Billing date",
        name: "scheduleMode",
        orientation: "horizontal",
        value: value.scheduleMode ?? null,
        onChange: (m) => onChange({ scheduleMode: m }),
        children: SCHEDULE_MODES.map((m) => /* @__PURE__ */ jsx(RadioButtonGroup.Item, { value: m.id, "aria-label": m.label, children: /* @__PURE__ */ jsx(RadioButtonGroup.ItemContent, { children: /* @__PURE__ */ jsx(Label, { children: m.label }) }) }, m.id))
      }
    ),
    value.scheduleMode && /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2 px-4 py-3.5 bg-background border border-border rounded-xl text-sm text-foreground leading-relaxed", children: [
      /* @__PURE__ */ jsx("span", { children: "Bill on the" }),
      isDayOfWeek ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(
          InlineSelect,
          {
            width: 86,
            value: value.weekOfMonth,
            onChange: (v) => onChange({ weekOfMonth: v === "last" ? "last" : parseInt(v) }),
            options: ORDINAL_WEEKS
          }
        ),
        /* @__PURE__ */ jsx(
          InlineSelect,
          {
            width: 130,
            value: value.dayOfWeek,
            onChange: (v) => onChange({ dayOfWeek: parseInt(v) }),
            options: WEEKDAYS
          }
        )
      ] }) : /* @__PURE__ */ jsx(
        InlineSelect,
        {
          width: 130,
          value: value.dayOfMonth,
          onChange: (v) => onChange({ dayOfMonth: v === "last" ? "last" : parseInt(v) }),
          options: [
            ...Array.from({ length: 31 }, (_, i) => ({
              value: String(i + 1),
              label: ordinalDay(i + 1)
            })),
            { value: "last", label: "last day" }
          ]
        }
      ),
      /* @__PURE__ */ jsx("span", { children: "of the month." })
    ] }),
    nextDate && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 text-xs text-muted", children: [
      /* @__PURE__ */ jsx("i", { className: "ri-calendar-event-line text-muted" }),
      "Next bill will fall on",
      " ",
      /* @__PURE__ */ jsx("strong", { className: "text-foreground font-semibold", children: fmtFullDate(nextDate) }),
      /* @__PURE__ */ jsxs("span", { className: "text-muted", children: [
        "· repeats every ",
        cad.months,
        " month(s)"
      ] })
    ] })
  ] });
}
function SummaryRow({ label, hint, cents, emphasized }) {
  return /* @__PURE__ */ jsxs("div", { className: "flex justify-between items-baseline gap-3", children: [
    /* @__PURE__ */ jsxs("span", { className: emphasized ? "text-sm font-semibold text-foreground" : "text-xs text-muted", children: [
      label,
      hint && /* @__PURE__ */ jsxs("span", { className: "text-xs font-normal text-muted", children: [
        " · ",
        hint
      ] })
    ] }),
    /* @__PURE__ */ jsx(
      "span",
      {
        className: `font-bold text-foreground tabular-nums ${emphasized ? "text-base" : "text-sm"}`,
        children: formatCents(cents)
      }
    )
  ] });
}
function SubscriptionAmount({ value, onChange, totals }) {
  const cad = totals.cadence;
  const [text, setText] = useState(() => centsToText(value));
  useEffect(() => {
    setText((t) => parseCents(t) === value ? t : centsToText(value));
  }, [value]);
  const commit = () => {
    const cents = parseCents(text);
    onChange(cents);
    setText(centsToText(cents));
  };
  return /* @__PURE__ */ jsxs("div", { className: "flex items-stretch gap-3.5 flex-wrap", children: [
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsxs("p", { className: "text-xs font-medium text-muted mb-1.5", children: [
        "Monthly subscription rate ",
        /* @__PURE__ */ jsx("span", { className: "text-danger", children: "*" })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "relative w-60", children: [
        /* @__PURE__ */ jsx("span", { className: "absolute left-3.5 top-1/2 -translate-y-1/2 text-muted text-xl font-medium pointer-events-none", children: "$" }),
        /* @__PURE__ */ jsx(
          "input",
          {
            type: "text",
            inputMode: "decimal",
            value: text,
            placeholder: "0.00",
            onChange: (e) => {
              setText(e.target.value);
              onChange(parseCents(e.target.value));
            },
            onFocus: (e) => {
              applyFocus(e.target);
              e.target.select();
            },
            onBlur: (e) => {
              clearFocus(e.target);
              commit();
            },
            onKeyDown: (e) => {
              if (e.key === "Enter") e.target.blur();
            },
            style: {
              ...INPUT_BASE,
              height: 50,
              padding: "0 16px 0 34px",
              fontSize: 22,
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
              textAlign: "right",
              letterSpacing: "-0.01em"
            }
          }
        )
      ] }),
      /* @__PURE__ */ jsx("p", { className: "text-xs text-muted mt-1.5 tracking-wide", children: "per month" })
    ] }),
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: `flex-1 min-w-[220px] px-4 py-3 bg-background border border-border rounded-xl flex flex-col justify-center gap-1.5 ${totals.dueTodayCents > 0 ? "opacity-100" : "opacity-55"}`,
        children: [
          /* @__PURE__ */ jsx("p", { className: "text-xs font-semibold text-muted uppercase tracking-wider", children: "Summary" }),
          /* @__PURE__ */ jsx(
            SummaryRow,
            {
              emphasized: true,
              label: "Total due today",
              hint: "including one-time fees",
              cents: totals.dueTodayCents
            }
          ),
          cad.months > 1 && /* @__PURE__ */ jsx(
            SummaryRow,
            {
              label: `${cad.label} (every ${cad.months} months)`,
              cents: totals.recurringCents
            }
          ),
          /* @__PURE__ */ jsx(
            SummaryRow,
            {
              label: `Monthly ${cad.months > 1 ? "(Avg.)" : ""}`,
              hint: totals.addonsMonthlyCents > 0 ? "incl. add-ons" : null,
              cents: totals.planMonthlyCents
            }
          )
        ]
      }
    )
  ] });
}
function SubscriptionSection({ subscription, totals, cadences, onSubscriptionChange }) {
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-5", children: [
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx(SubConfigLabel, { required: true, children: "Billing cadence" }),
      /* @__PURE__ */ jsx(
        CadencePicker,
        {
          value: subscription.cadence,
          cadences,
          onChange: (v) => onSubscriptionChange({ cadence: v })
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx(SubConfigLabel, { required: true, children: "Billing date" }),
      /* @__PURE__ */ jsx(BillingSchedule, { value: subscription, onChange: onSubscriptionChange })
    ] }),
    /* @__PURE__ */ jsx(
      SubscriptionAmount,
      {
        value: subscription.monthlyCents,
        onChange: (v) => onSubscriptionChange({ monthlyCents: v }),
        totals
      }
    )
  ] });
}
const ProductsFormSubscription = ({ data, onSubscriptionChange = () => {
} }) => {
  const subscription = data.subscription || INITIAL_FORM.products.subscription;
  const totals = calcDraftTotals({ ...data, subscription });
  return /* @__PURE__ */ jsx(
    FormSection,
    {
      isLast: true,
      title: "Subscription details",
      subtitle: "How and when the customer is billed for the recurring plan.",
      children: /* @__PURE__ */ jsx(
        SubscriptionSection,
        {
          subscription,
          totals,
          cadences: offeredCadences(data.deviceOptions),
          onSubscriptionChange
        }
      )
    }
  );
};
const ProductsForm = ({
  data = {},
  cellular = {},
  onChange = () => {
  },
  addonServices = [],
  feeProducts = [],
  accessoryCatalog = [],
  devices = [],
  loading = false,
  readOnly = false
}) => {
  const handleDeviceSelect = (device) => {
    if (device === data.device) return;
    const picked = devices.find((d) => d.device === device);
    if (!picked) return;
    const { product, options: deviceOptions } = picked;
    const patch = {
      device,
      deviceName: ProductDisplay[device] ?? product.displayName ?? "",
      deviceOptions,
      // Choices belong to the device they were made for; step 2's coverage lookup can settle one
      // (a carrier covered alone at the service address).
      options: optionsFromCoverage(deviceOptions, cellular?.coverage)
    };
    const subscription = { ...data.subscription };
    if (product.suggestedPrice != null) subscription.monthlyCents = toCents(product.suggestedPrice);
    if (!offeredCadences(deviceOptions).some((c) => c.id === subscription.cadence)) {
      subscription.cadence = "";
    }
    patch.subscription = subscription;
    onChange(patch);
  };
  const handleOptionChange = (name, value) => onChange({ options: { ...data.options, [name]: value } });
  const handleServiceChange = (id, patch) => onChange({ services: { ...data.services, [id]: { ...data.services?.[id], ...patch } } });
  const handleFeeChange = (id, patch) => onChange({ fees: { ...data.fees, [id]: { ...data.fees?.[id], ...patch } } });
  const handleSubscriptionChange = (patch) => onChange({ subscription: { ...data.subscription, ...patch } });
  const handleAccessoryAdd = (id) => {
    const accessories = data.accessories || [];
    if (accessories.some((a) => a.id === id)) return;
    const ref = accessoryCatalog.find((a) => a.id === id);
    if (!ref) return;
    onChange({
      accessories: [
        ...accessories,
        {
          id: ref.id,
          sku: ref.sku,
          name: ref.name,
          qty: 1,
          priceCents: toCents(ref.suggestedPrice),
          defaultPriceCents: toCents(ref.suggestedPrice)
        }
      ]
    });
  };
  const handleAccessoryUpdate = (id, patch) => onChange({
    accessories: (data.accessories || []).map((a) => a.id === id ? { ...a, ...patch } : a)
  });
  const handleAccessoryRemove = (id) => onChange({ accessories: (data.accessories || []).filter((a) => a.id !== id) });
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    readOnly && /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-2 px-4 py-3 mb-4 bg-background border border-dashed border-border rounded-xl text-sm text-muted", children: [
      /* @__PURE__ */ jsx("i", { className: "ri-lock-line mt-0.5 flex-none" }),
      /* @__PURE__ */ jsx("span", { children: "A payment has been submitted for this order, so it can no longer be changed here." })
    ] }),
    /* @__PURE__ */ jsxs(Card, { className: "gap-4", inert: readOnly || void 0, children: [
      /* @__PURE__ */ jsx(
        ProductsFormDevice,
        {
          data,
          cellular,
          devices,
          loading,
          onDeviceSelect: handleDeviceSelect,
          onOptionChange: handleOptionChange
        }
      ),
      /* @__PURE__ */ jsx(
        ProductsFormServices,
        {
          data,
          addonServices,
          onServiceChange: handleServiceChange
        }
      ),
      /* @__PURE__ */ jsx(ProductsFormFees, { data, feeProducts, onFeeChange: handleFeeChange }),
      /* @__PURE__ */ jsx(
        ProductsFormAccessories,
        {
          data,
          accessoryCatalog,
          onAdd: handleAccessoryAdd,
          onUpdate: handleAccessoryUpdate,
          onRemove: handleAccessoryRemove
        }
      ),
      /* @__PURE__ */ jsx(ProductsFormSubscription, { data, onSubscriptionChange: handleSubscriptionChange })
    ] })
  ] });
};
const CATEGORIES = ["PERS", "Services", "Fees", "Accessories"];
const OPTIONS_FOR = ["PERS"];
const toAddonServices = (services = []) => services.filter((product) => product.typeName?.toLowerCase() !== "internal");
const toFees = (services = []) => services.filter((product) => product.typeName?.toLowerCase() !== "internal");
const toDevices = (pers = [], optionsByProduct = {}) => pers.map((product) => ({
  device: productForSku(product.sku),
  product,
  options: optionsByProduct[product.id] ?? []
})).filter(({ device }) => device !== void 0).sort((a, b) => (a.product.sortOrder ?? 0) - (b.product.sortOrder ?? 0));
const ReconcileSavedLines = ({ products, catalog, loading, onReconciled }) => {
  const pending = !!products.pendingReconcile;
  useEffect(() => {
    if (loading || !pending) return;
    onReconciled(reconcileHydratedProducts(products, catalog));
  }, [loading, pending]);
  return null;
};
const ProductsController = () => {
  const { form, patchForm, enrollment, markOrderSaved } = useOutletContext();
  const ProductCatalog = useComponent("products.catalog");
  return /* @__PURE__ */ jsx(ProductCatalog, { categories: CATEGORIES, optionsFor: OPTIONS_FOR, children: ({ byCategory, optionsByProduct, isLoading }) => {
    const addonServices = toAddonServices(byCategory.Services);
    const feeProducts = toFees(byCategory.Fees);
    const accessoryCatalog = byCategory.Accessories ?? [];
    const devices = toDevices(byCategory.PERS, optionsByProduct);
    const optionsByDevice = Object.fromEntries(devices.map((d) => [d.device, d.options]));
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        ReconcileSavedLines,
        {
          products: form.products,
          catalog: { addonServices, feeProducts, accessoryCatalog, optionsByDevice },
          loading: isLoading,
          onReconciled: (next) => {
            patchForm("products", next);
            markOrderSaved(next);
          }
        }
      ),
      /* @__PURE__ */ jsx(
        ProductsForm,
        {
          data: form.products,
          cellular: form.cellular,
          onChange: (patch) => patchForm("products", patch),
          addonServices,
          feeProducts,
          accessoryCatalog,
          devices,
          loading: isLoading,
          readOnly: !isOrderOpenForChanges(enrollment)
        }
      )
    ] });
  } });
};
const ContactsController = reactive.component(
  ({ services, monitors }) => {
    const CustomerContactsPanel = useComponent("accounts.customer-contacts-panel");
    const { enrollmentId, enrollment, setEnrollment } = useOutletContext();
    const [editingId, setEditingId] = useState(null);
    const [order, setOrder] = useState([]);
    const panel = useRef(null);
    const contacts = useMemo(() => {
      const list = enrollment?.contacts ?? [];
      if (order.length === 0) return list;
      const rank = new Map(order.map((id, i) => [id, i]));
      return [...list].sort((a, b) => (rank.get(a.id) ?? Infinity) - (rank.get(b.id) ?? Infinity));
    }, [enrollment, order]);
    const saving = monitors.addContact.executing || monitors.updateContact.executing || monitors.removeContact.executing;
    function closeForm() {
      setEditingId(null);
      panel.current?.close();
    }
    function saveContact(model) {
      if (saving) return;
      if (editingId != null) {
        const existing = contacts.find((c) => c.id === editingId);
        services.enrollments.updateContact(enrollmentId, editingId, {
          ...existing,
          ...model
        });
      } else {
        services.enrollments.addContact(enrollmentId, model);
      }
    }
    function removeContact(id) {
      if (saving) return;
      if (editingId === id) closeForm();
      services.enrollments.removeContact(enrollmentId, id);
    }
    useSuccessReactor(services.enrollments.addContact, (payload) => {
      setEnrollment(payload);
      closeForm();
    });
    useSuccessReactor(services.enrollments.updateContact, (payload) => {
      setEnrollment(payload);
      closeForm();
    });
    useSuccessReactor(services.enrollments.removeContact, setEnrollment);
    return /* @__PURE__ */ jsx("div", { className: "w-full", children: /* @__PURE__ */ jsx(
      CustomerContactsPanel,
      {
        contacts,
        ref: panel,
        isSaving: saving,
        onDelete: removeContact,
        onSave: saveContact
      }
    ) });
  },
  {
    name: "ContactsController",
    monitors: (services) => [
      services.enrollments.addContact,
      services.enrollments.updateContact,
      services.enrollments.removeContact
    ]
  }
);
const MedicalFormAccess = ({ data }) => /* @__PURE__ */ jsxs(
  FormSection,
  {
    title: "Home access",
    subtitle: "How emergency responders get into the home. Keep the lockbox code secure.",
    children: [
      /* @__PURE__ */ jsx(
        TextAreaField,
        {
          name: "lockboxLocation",
          label: "Lockbox location",
          description: "Be specific — building entrance, porch railing, garage door, etc.",
          value: data.lockboxLocation || "",
          placeholder: "Gray lockbox mounted on the front porch railing, right of the door.",
          minRows: 3,
          maxLength: 500,
          size: "sm"
        }
      ),
      /* @__PURE__ */ jsx("div", { className: "max-w-[280px]", children: /* @__PURE__ */ jsx(
        PasswordField,
        {
          name: "lockboxCode",
          label: "Lockbox code",
          description: "Stored securely — hidden from printed summaries.",
          value: data.lockboxCode || "",
          placeholder: "Enter code…",
          size: "sm"
        }
      ) }),
      /* @__PURE__ */ jsx(
        TextAreaField,
        {
          name: "keyLocation",
          label: "Key location",
          description: "Where a spare key is kept, if there's no lockbox.",
          value: data.keyLocation || "",
          placeholder: "Under the mat by the back door.",
          minRows: 2,
          maxLength: 500,
          size: "sm"
        }
      ),
      /* @__PURE__ */ jsx(
        TextAreaField,
        {
          name: "entryInfo",
          label: "Entry instructions",
          description: "Anything responders should know to get in — gate codes, pets, which door to use.",
          value: data.entryInfo || "",
          placeholder: "Ring twice. Side gate code 4321. Friendly dog in the yard.",
          minRows: 2,
          maxLength: 500,
          size: "sm"
        }
      )
    ]
  }
);
function TagInput({ tags, onChange, placeholder, suggestions, maxTags = 40 }) {
  const [inputVal, setInputVal] = useState("");
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);
  const addTag = (raw) => {
    const val = raw.trim().replace(/,$/, "").trim();
    if (!val || tags.includes(val) || tags.length >= maxTags) return;
    onChange([...tags, val]);
    setInputVal("");
  };
  const removeTag = (i) => onChange(tags.filter((_, idx) => idx !== i));
  const handleKeyDown = (e) => {
    if ((e.key === "Enter" || e.key === "Tab" || e.key === ",") && inputVal.trim()) {
      e.preventDefault();
      addTag(inputVal);
    }
    if (e.key === "Backspace" && !inputVal && tags.length > 0) {
      removeTag(tags.length - 1);
    }
  };
  const handleInput = (v) => {
    if (v.endsWith(",")) {
      addTag(v);
      return;
    }
    setInputVal(v);
  };
  const quickAdd = (s) => {
    if (!tags.includes(s) && tags.length < maxTags) onChange([...tags, s]);
  };
  const availableSuggestions = suggestions?.filter((s) => !tags.includes(s)) || [];
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
    /* @__PURE__ */ jsxs(
      "div",
      {
        onClick: () => inputRef.current?.focus(),
        className: `flex flex-wrap items-center gap-1.5 min-h-[42px] px-2.5 py-1.5 bg-background border rounded-lg cursor-text transition-all ${focused ? "border-accent shadow-[0_0_0_2px_rgba(var(--heroui-primary)/0.15)]" : "border-border"}`,
        children: [
          tags.map((t, i) => /* @__PURE__ */ jsxs(
            "span",
            {
              className: "inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent-soft border border-accent rounded-full text-xs font-medium text-accent leading-tight",
              children: [
                t,
                /* @__PURE__ */ jsx(
                  "button",
                  {
                    type: "button",
                    onClick: (e) => {
                      e.stopPropagation();
                      removeTag(i);
                    },
                    className: "inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-transparent border-none cursor-pointer text-accent hover:bg-accent-soft hover:text-accent transition-colors p-0",
                    children: /* @__PURE__ */ jsx("i", { className: "ri-close-line" })
                  }
                )
              ]
            },
            i
          )),
          /* @__PURE__ */ jsx(
            "input",
            {
              ref: inputRef,
              value: inputVal,
              onChange: (e) => handleInput(e.target.value),
              onKeyDown: handleKeyDown,
              onFocus: () => setFocused(true),
              onBlur: () => {
                setFocused(false);
                if (inputVal.trim()) addTag(inputVal);
              },
              placeholder: tags.length === 0 ? placeholder : "",
              className: "flex-1 min-w-[120px] bg-transparent border-none outline-none text-sm text-foreground h-7 placeholder:text-muted"
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1.5 text-xs text-muted", children: [
      /* @__PURE__ */ jsx("i", { className: "ri-corner-down-left-line" }),
      "Press",
      " ",
      /* @__PURE__ */ jsx("kbd", { className: "px-1 py-0.5 rounded bg-background border border-border text-[10.5px] text-muted", children: "Enter" }),
      " ",
      "or",
      " ",
      /* @__PURE__ */ jsx("kbd", { className: "px-1 py-0.5 rounded bg-background border border-border text-[10.5px] text-muted", children: "," }),
      " ",
      "to add each item",
      tags.length > 0 && /* @__PURE__ */ jsxs("span", { className: "ml-2 text-muted", children: [
        tags.length,
        " item",
        tags.length !== 1 ? "s" : "",
        " added"
      ] })
    ] }),
    availableSuggestions.length > 0 && /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap gap-1.5 mt-0.5", children: [
      /* @__PURE__ */ jsx("span", { className: "text-xs text-muted leading-6 mr-1 flex-none", children: "Common:" }),
      availableSuggestions.map((s) => /* @__PURE__ */ jsxs(
        "button",
        {
          type: "button",
          onClick: () => quickAdd(s),
          className: "inline-flex items-center gap-1 h-6 px-2.5 bg-background border border-border rounded-full cursor-pointer text-xs text-muted hover:bg-accent-soft hover:border-accent hover:text-accent transition-colors",
          children: [
            /* @__PURE__ */ jsx("i", { className: "ri-add-line" }),
            s
          ]
        },
        s
      ))
    ] })
  ] });
}
const MedicalFormMedications = ({ data, onChange = () => {
} }) => /* @__PURE__ */ jsx(
  FormSection,
  {
    title: "Medications",
    subtitle: "List any medications the customer regularly takes. Use generic or brand names.",
    children: /* @__PURE__ */ jsx(
      TagInput,
      {
        tags: data.medications || [],
        onChange: (v) => onChange({ medications: v }),
        placeholder: "Type a medication and press Enter…"
      }
    )
  }
);
const MedicalFormResponders = ({ data }) => /* @__PURE__ */ jsxs(
  FormSection,
  {
    isLast: true,
    title: "Emergency care",
    subtitle: "What the monitoring station passes on to first responders.",
    children: [
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3", children: [
        /* @__PURE__ */ jsx(SwitchField, { name: "usesOxygen", value: data.usesOxygen ?? false, children: "Uses oxygen" }),
        /* @__PURE__ */ jsx(SwitchField, { name: "isDisabled", value: data.isDisabled ?? false, children: "Has a disability or limited mobility" })
      ] }),
      /* @__PURE__ */ jsx(
        TextField,
        {
          name: "preferredHospital",
          label: "Preferred hospital",
          value: data.preferredHospital || "",
          placeholder: "Utah Valley Hospital",
          maxLength: 200,
          size: "sm"
        }
      )
    ]
  }
);
const sliceFromForm = (d, prev) => ({
  lockboxLocation: d.lockboxLocation ?? prev.lockboxLocation,
  lockboxCode: d.lockboxCode ?? prev.lockboxCode,
  keyLocation: d.keyLocation ?? prev.keyLocation,
  entryInfo: d.entryInfo ?? prev.entryInfo,
  medications: prev.medications ?? [],
  isDisabled: d.isDisabled ?? prev.isDisabled,
  usesOxygen: d.usesOxygen ?? prev.usesOxygen,
  preferredHospital: d.preferredHospital ?? prev.preferredHospital
});
const MedicalForm = ({ data = {}, onChange = () => {
} }) => /* @__PURE__ */ jsx(Card, { children: /* @__PURE__ */ jsxs(
  Uniformly,
  {
    className: "flex flex-col gap-4",
    onChange: (result) => onChange(sliceFromForm(result.data, data)),
    children: [
      /* @__PURE__ */ jsx(MedicalFormAccess, { data }),
      /* @__PURE__ */ jsx(MedicalFormMedications, { data, onChange }),
      /* @__PURE__ */ jsx(MedicalFormResponders, { data })
    ]
  }
) });
const MedicalController = () => {
  const { form, patchForm } = useOutletContext();
  return /* @__PURE__ */ jsx(MedicalForm, { data: form.medical, onChange: (patch) => patchForm("medical", patch) });
};
function PaymentFormCard({
  value,
  onChange,
  defaults = {},
  locked = false,
  lockedLabel = null
}) {
  const handleChange = ({ rawData }) => onChange({ ...value, ...rawData });
  const brand = cardBrand(value.number);
  return /* @__PURE__ */ jsxs(Uniformly, { onChange: handleChange, className: "flex flex-col gap-3.5", children: [
    /* @__PURE__ */ jsx(
      TextField,
      {
        name: "number",
        label: "Card number",
        isRequired: true,
        isDisabled: locked,
        value: value.number,
        placeholder: locked && lockedLabel ? lockedLabel : "1234-5678-9012-3456",
        inputMode: "numeric",
        size: "sm",
        mask: { mask: masks.cardNumberMask },
        validation: (x) => x.string().required("Card number is required").test("complete", "Enter a complete card number", (v) => onlyDigits(v).length >= 13),
        startContent: /* @__PURE__ */ jsx("i", { className: "ri-bank-card-line text-muted" }),
        endContent: brand && /* @__PURE__ */ jsx("span", { className: "px-2 py-0.5 rounded bg-background text-[10.5px] font-bold tracking-wide uppercase text-muted mr-2", children: brand })
      }
    ),
    /* @__PURE__ */ jsx(
      TextField,
      {
        name: "name",
        label: "Cardholder name",
        isRequired: true,
        isDisabled: locked,
        value: value.name ?? defaults.name ?? "",
        placeholder: "As shown on card",
        size: "sm",
        validation: (x) => x.string().required("Cardholder name is required")
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-2 gap-3", children: [
      /* @__PURE__ */ jsx(
        TextField,
        {
          name: "exp",
          label: "Expiration",
          isRequired: true,
          description: "MM/YY",
          value: value.exp,
          placeholder: "MM/YY",
          inputMode: "numeric",
          size: "sm",
          mask: { mask: masks.cardDateMask },
          validation: (x) => x.string().length(5, "Enter a valid expiry MM/YY").required("Expiry is required")
        }
      ),
      /* @__PURE__ */ jsx(
        TextField,
        {
          name: "cvv",
          label: "CVV",
          isRequired: true,
          value: value.cvv || "",
          placeholder: "123",
          inputMode: "numeric",
          size: "sm",
          mask: { mask: "0000" },
          validation: (x) => x.string().min(3, "Enter CVV").required("CVV is required"),
          startContent: /* @__PURE__ */ jsx("i", { className: "ri-lock-2-line text-muted" })
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-2 gap-3", children: [
      /* @__PURE__ */ jsx(
        PhoneField,
        {
          name: "phone",
          label: "Billing phone",
          value: value.phone ?? defaults.phone ?? "",
          size: "sm"
        }
      ),
      /* @__PURE__ */ jsx(
        EmailField,
        {
          name: "email",
          label: "Billing email",
          value: value.email ?? defaults.email ?? "",
          size: "sm",
          validationMessage: "Invalid email"
        }
      )
    ] })
  ] });
}
const ACCOUNT_TYPES = [
  { id: "checking", label: "Checking" },
  { id: "savings", label: "Savings" }
];
function PaymentFormBank({ value, onChange, defaults = {} }) {
  const handleChange = ({ rawData }) => onChange({ ...value, ...rawData });
  const setF = (k, v) => onChange({ ...value, [k]: v });
  const routingValid = (value.routing || "").length === 9;
  const knownBank = ROUTING_BANKS[value.routing];
  const lookupKnown = routingValid && knownBank;
  const lookupUnknown = routingValid && !knownBank;
  useEffect(() => {
    if (lookupKnown && value.bankName !== knownBank) setF("bankName", knownBank);
  }, [knownBank, lookupKnown]);
  return /* @__PURE__ */ jsxs(Uniformly, { onChange: handleChange, className: "flex flex-col gap-3.5", children: [
    /* @__PURE__ */ jsx(
      TextField,
      {
        name: "routing",
        label: "Routing number",
        isRequired: true,
        description: lookupUnknown ? "Routing not recognized — enter the bank name manually below." : "9-digit number on the bottom-left of a check.",
        value: value.routing || "",
        placeholder: "123456789",
        inputMode: "numeric",
        size: "sm",
        mask: { mask: "000000000" },
        validation: (x) => x.string().length(9, "9-digit routing number required").required("Routing number is required"),
        startContent: /* @__PURE__ */ jsx("i", { className: "ri-bank-line text-muted" }),
        endContent: routingValid && /* @__PURE__ */ jsx(
          Chip,
          {
            size: "sm",
            color: lookupKnown ? "success" : "warning",
            variant: "flat",
            className: "mr-2 text-[10.5px] uppercase",
            children: /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1", children: [
              /* @__PURE__ */ jsx(
                "i",
                {
                  className: lookupKnown ? "ri-checkbox-circle-fill" : "ri-error-warning-fill"
                }
              ),
              lookupKnown ? "Found" : "Unknown"
            ] })
          }
        )
      }
    ),
    lookupKnown && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2.5 px-3 py-2.5 bg-success-soft border border-success rounded-xl text-success", children: [
      /* @__PURE__ */ jsx("i", { className: "ri-bank-line" }),
      /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold text-foreground flex-1", children: knownBank }),
      /* @__PURE__ */ jsx("span", { className: "text-[10.5px] font-bold tracking-widest uppercase text-success", children: "Verified" })
    ] }),
    lookupUnknown && /* @__PURE__ */ jsx(
      TextField,
      {
        name: "bankName",
        label: "Bank name",
        isRequired: true,
        value: value.bankName || "",
        placeholder: "Enter bank name as shown on the check",
        size: "sm",
        validation: (x) => x.string().required("Bank name is required")
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
      /* @__PURE__ */ jsxs("p", { className: "text-sm font-medium text-muted", children: [
        "Account type ",
        /* @__PURE__ */ jsx("span", { className: "text-danger", children: "*" })
      ] }),
      /* @__PURE__ */ jsx(
        RadioButtonGroup,
        {
          "aria-label": "Account type",
          name: "accountType",
          orientation: "horizontal",
          value: value.accountType,
          onChange: (v) => setF("accountType", v),
          children: ACCOUNT_TYPES.map((t) => /* @__PURE__ */ jsx(RadioButtonGroup.Item, { value: t.id, "aria-label": t.label, children: /* @__PURE__ */ jsx(RadioButtonGroup.ItemContent, { children: /* @__PURE__ */ jsx(Label, { children: t.label }) }) }, t.id))
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-2 gap-3", children: [
      /* @__PURE__ */ jsx(
        TextField,
        {
          name: "accountNumber",
          label: "Account number",
          isRequired: true,
          value: value.accountNumber || "",
          placeholder: "0000000000",
          inputMode: "numeric",
          size: "sm",
          validation: (x) => x.string().matches(/^\d+$/, "Digits only").min(4, "Account number is required").max(17, "Account number too long").required("Account number is required"),
          startContent: /* @__PURE__ */ jsx("i", { className: "ri-hashtag text-muted" })
        }
      ),
      /* @__PURE__ */ jsx(
        TextField,
        {
          name: "accountName",
          label: "Account holder name",
          isRequired: true,
          value: value.accountName ?? defaults.name ?? "",
          placeholder: "Full name on account",
          size: "sm",
          validation: (x) => x.string().required("Account holder name is required")
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-2 px-3 py-2.5 bg-background border border-dashed border-border rounded-xl text-xs text-muted", children: [
      /* @__PURE__ */ jsx("i", { className: "ri-time-line text-muted mt-0.5 flex-none" }),
      /* @__PURE__ */ jsxs("span", { children: [
        "Bank transfers post immediately but can take up to",
        " ",
        /* @__PURE__ */ jsx("strong", { className: "text-foreground", children: "5 business days" }),
        ` to fully clear. The order moves forward on a "posted" response; we'll flag the account if it later returns.`
      ] })
    ] })
  ] });
}
const METHOD_OPTIONS = [
  { id: "card", label: "Credit card", icon: "ri-bank-card-line", hint: "Charged immediately" },
  {
    id: "bank",
    label: "Bank account (ACH)",
    icon: "ri-bank-line",
    hint: "Posts immediately, clears in 5 business days"
  }
];
function MethodTabs({ value, onChange }) {
  return /* @__PURE__ */ jsx("div", { className: "grid grid-cols-2 gap-2.5", children: METHOD_OPTIONS.map((o) => {
    const active = value === o.id;
    return /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        onClick: () => onChange(o.id),
        className: `flex items-center gap-2.5 px-3.5 py-3 rounded-xl border-[1.5px] cursor-pointer text-left transition-colors ${active ? "bg-accent-soft border-accent" : "bg-surface border-border hover:border-border"}`,
        children: [
          /* @__PURE__ */ jsx(
            "span",
            {
              className: `w-8 h-8 rounded-md inline-flex items-center justify-center flex-none transition-colors ${active ? "bg-accent text-white" : "bg-background text-muted"}`,
              children: /* @__PURE__ */ jsx("i", { className: o.icon })
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
            /* @__PURE__ */ jsx("p", { className: "text-sm font-semibold text-foreground", children: o.label }),
            /* @__PURE__ */ jsx("p", { className: "text-xs text-muted mt-0.5", children: o.hint })
          ] }),
          /* @__PURE__ */ jsx(
            "span",
            {
              className: `w-4.5 h-4.5 rounded-full flex-none border-[1.5px] inline-flex items-center justify-center text-white transition-colors ${active ? "border-accent bg-accent" : "border-border bg-transparent"}`,
              children: active && /* @__PURE__ */ jsx("i", { className: "ri-check-line" })
            }
          )
        ]
      },
      o.id
    );
  }) });
}
const PaymentFormMethod = ({
  data,
  defaults = {},
  retrying = false,
  retryInstrument = null,
  onTryNew = () => {
  },
  onSetMethod = () => {
  },
  onSetCard = () => {
  },
  onSetBank = () => {
  }
}) => /* @__PURE__ */ jsxs(
  FormSection,
  {
    title: "Payment method",
    subtitle: retrying ? "Correct the expiration, CVV or billing address, then retry the same card." : "Pick how the customer is paying today.",
    children: [
      retrying ? /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 px-3 py-2.5 bg-background border border-dashed border-border rounded-xl text-xs text-muted", children: [
        /* @__PURE__ */ jsx("i", { className: "ri-information-line flex-none" }),
        /* @__PURE__ */ jsxs("span", { className: "flex-1", children: [
          "Updating ",
          retryInstrument ? /* @__PURE__ */ jsx("code", { className: "font-mono", children: retryInstrument }) : "the card on file",
          " ",
          "— the card number can't change."
        ] }),
        /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "sm", onPress: onTryNew, children: "Try a different payment" })
      ] }) : /* @__PURE__ */ jsx(MethodTabs, { value: data.method, onChange: onSetMethod }),
      !data.method ? /* @__PURE__ */ jsx("div", { className: "px-3.5 py-5 bg-background border border-dashed border-border rounded-xl text-center text-sm text-muted", children: "Choose a payment method above to enter details." }) : data.method === "card" ? /* @__PURE__ */ jsx(
        PaymentFormCard,
        {
          value: data.card || INITIAL_CARD,
          onChange: onSetCard,
          defaults,
          locked: retrying,
          lockedLabel: retryInstrument
        }
      ) : /* @__PURE__ */ jsx(
        PaymentFormBank,
        {
          value: data.bank || INITIAL_BANK,
          onChange: onSetBank,
          defaults
        }
      )
    ]
  }
);
const formatAddress = (a) => a?.address1 ? `${a.address1}${a.address2 ? ` ${a.address2}` : ""}, ${a.city}, ${a.state} ${a.postalCode}` : "";
const SameAsCheckbox = ({ label, isSelected, onChange }) => /* @__PURE__ */ jsx(Checkbox, { isSelected, onChange, children: /* @__PURE__ */ jsxs(Checkbox.Content, { children: [
  /* @__PURE__ */ jsx(Checkbox.Control, { children: /* @__PURE__ */ jsx(Checkbox.Indicator, {}) }),
  /* @__PURE__ */ jsx(Label, { children: label })
] }) });
const PaymentFormBilling = ({
  value,
  homeAddress,
  shippingAddress,
  onChange = () => {
  }
}) => {
  const home = formatAddress(homeAddress);
  const shipping = formatAddress(shippingAddress);
  return /* @__PURE__ */ jsx(FormSection, { title: "Billing address", subtitle: "Where statements for this payment method go.", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3", children: [
    /* @__PURE__ */ jsx(
      SameAsCheckbox,
      {
        label: "Same as Home Address",
        isSelected: !!value.sameAsHome,
        onChange: (sameAsHome) => onChange({
          ...value,
          sameAsHome,
          sameAsShipping: sameAsHome ? false : value.sameAsShipping
        })
      }
    ),
    /* @__PURE__ */ jsx(
      SameAsCheckbox,
      {
        label: "Same as Shipping Address",
        isSelected: !!value.sameAsShipping,
        onChange: (sameAsShipping) => onChange({
          ...value,
          sameAsShipping,
          sameAsHome: sameAsShipping ? false : value.sameAsHome
        })
      }
    ),
    value.sameAsHome ? /* @__PURE__ */ jsx("p", { className: "text-xs text-muted", children: home || "No home address on file. Add one in step 1, or enter a billing address." }) : value.sameAsShipping ? /* @__PURE__ */ jsx("p", { className: "text-xs text-muted", children: shipping || "No shipping address on file. Add one in step 1, or enter a billing address." }) : /* @__PURE__ */ jsx(
      Uniformly,
      {
        onChange: ({ rawData }) => rawData.billingAddress && onChange({ ...value, address: rawData.billingAddress }),
        children: /* @__PURE__ */ jsx(AddressForm, { name: "billingAddress", value: value.address })
      }
    )
  ] }) });
};
const SCHEDULE_OPTIONS = [
  { id: "now", label: "Process today" },
  { id: "later", label: "Post-date payment" }
];
const PaymentFormSchedule = ({
  data,
  dueCents = 0,
  canSubmit = false,
  processing = false,
  retrying = false,
  onSetSchedule = () => {
  },
  onSetScheduledDate = () => {
  },
  onSubmit = () => {
  }
}) => {
  const isLater = data.schedule === "later";
  const isProcessing = processing;
  return /* @__PURE__ */ jsx(
    FormSection,
    {
      isLast: true,
      title: "Processing date",
      subtitle: "Process the payment today, or post-date it for a later run.",
      children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3.5", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-3 row-gap-2.5", children: [
          /* @__PURE__ */ jsx(
            RadioButtonGroup,
            {
              "aria-label": "Processing date",
              name: "schedule",
              orientation: "horizontal",
              value: data.schedule,
              onChange: onSetSchedule,
              children: SCHEDULE_OPTIONS.map((o) => /* @__PURE__ */ jsx(RadioButtonGroup.Item, { value: o.id, "aria-label": o.label, children: /* @__PURE__ */ jsx(RadioButtonGroup.ItemContent, { children: /* @__PURE__ */ jsx(Label, { children: o.label }) }) }, o.id))
            }
          ),
          isLater && /* @__PURE__ */ jsx("div", { className: "flex items-center gap-2 flex-none", children: /* @__PURE__ */ jsx(
            Uniformly,
            {
              onChange: ({ data: d }) => d.scheduledDate && onSetScheduledDate(d.scheduledDate),
              renderForm: false,
              children: /* @__PURE__ */ jsx(DateField, { name: "scheduledDate", value: data.scheduledDate || null, size: "sm" })
            }
          ) })
        ] }),
        isLater && /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-2 px-3 py-2.5 bg-background border border-dashed border-border rounded-xl text-xs text-muted", children: [
          /* @__PURE__ */ jsx("i", { className: "ri-information-line text-muted mt-0.5 flex-none" }),
          /* @__PURE__ */ jsx("span", { children: "The payment runs automatically on the scheduled date. You'll need to return to this order once the payment has processed to complete it; if the post-dated payment declines, the account is flagged for follow-up." })
        ] }),
        /* @__PURE__ */ jsx("div", { className: "flex items-center gap-3 flex-wrap justify-end", children: /* @__PURE__ */ jsxs(
          Button,
          {
            color: "primary",
            size: "md",
            onPress: onSubmit,
            isDisabled: !canSubmit,
            isLoading: isProcessing,
            spinner: /* @__PURE__ */ jsx(Spinner, {}),
            className: "min-w-52 justify-center",
            children: [
              !isProcessing && /* @__PURE__ */ jsx("i", { className: isLater ? "ri-calendar-event-line" : "ri-lock-2-line" }),
              isProcessing ? "Processing…" : isLater && data.scheduledDate ? `Schedule ${formatCents(dueCents)} for ${localDate(data.scheduledDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : isLater ? "Pick a scheduled date" : retrying ? `Update card & retry ${formatCents(dueCents)}` : `Process payment ${formatCents(dueCents)}`
            ]
          }
        ) })
      ] })
    }
  );
};
const RESULT_TONES = {
  paid: { badge: "bg-success-soft text-success", icon: "ri-checkbox-circle-fill" },
  posted_pending: { badge: "bg-accent-soft text-accent", icon: "ri-time-fill" },
  scheduled: { badge: "bg-accent-soft text-accent", icon: "ri-calendar-event-fill" },
  declined: { badge: "bg-danger-soft text-danger", icon: "ri-close-circle-fill" }
};
const ATTEMPT_TONES = {
  declined: {
    text: "text-danger",
    badge: "bg-danger-soft text-danger",
    icon: "ri-close-circle-fill"
  },
  posted_pending: {
    text: "text-accent",
    badge: "bg-accent-soft text-accent",
    icon: "ri-time-fill"
  },
  scheduled: {
    text: "text-accent",
    badge: "bg-accent-soft text-accent",
    icon: "ri-calendar-event-fill"
  },
  processing: { text: "text-accent", badge: "bg-accent-soft text-accent", icon: "ri-time-fill" },
  paid: {
    text: "text-success",
    badge: "bg-success-soft text-success",
    icon: "ri-checkbox-circle-fill"
  },
  other: { text: "text-muted", badge: "bg-background text-muted", icon: "ri-information-fill" }
};
function instrumentName(payment, record) {
  if (payment.method === "card") {
    const tail = last4(payment.card?.number);
    if (tail) return `${cardBrand(payment.card.number) || "Card"} •••• ${tail}`;
  } else {
    const tail = last4(payment.bank?.accountNumber);
    if (tail) {
      const bankName = ROUTING_BANKS[payment.bank?.routing] || payment.bank?.bankName || "Bank";
      return `${bankName} ${payment.bank?.accountType || ""} •••• ${tail}`.replace(/\s+/g, " ");
    }
  }
  return record?.profileName || null;
}
function Instrument({ name, preposition = "to" }) {
  if (!name) return null;
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    " ",
    preposition,
    " ",
    /* @__PURE__ */ jsx("code", { className: "font-mono text-foreground", children: name })
  ] });
}
function ProcessorChecks({ checks, className = "" }) {
  if (!checks?.length) return null;
  return /* @__PURE__ */ jsx("dl", { className: `flex flex-wrap gap-x-4 gap-y-0.5 text-xs ${className}`, children: checks.map((check) => /* @__PURE__ */ jsxs("div", { className: "flex items-baseline gap-1.5", children: [
    /* @__PURE__ */ jsx("dt", { className: "text-muted", children: check.label }),
    /* @__PURE__ */ jsx("dd", { className: "font-mono font-semibold text-foreground", children: check.value })
  ] }, check.label)) });
}
function SuccessCard({ status, payment, record, amount }) {
  const tone = RESULT_TONES[status];
  const isPosted = status === "posted_pending";
  const isScheduled = status === "scheduled";
  const name = instrumentName(payment, record);
  const scheduledFor = localDate(payment.scheduledDate);
  const scheduledLabel = scheduledFor ? fmtFullDate(scheduledFor) : "";
  const title = isScheduled ? "Payment scheduled" : isPosted ? "Payment posted — pending bank verification" : "Payment captured";
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3.5", children: [
      /* @__PURE__ */ jsx(
        "span",
        {
          className: `w-11 h-11 rounded-full inline-flex items-center justify-center flex-none ${tone.badge}`,
          children: /* @__PURE__ */ jsx("i", { className: tone.icon })
        }
      ),
      /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
        /* @__PURE__ */ jsx("p", { className: "text-lg font-bold text-foreground tracking-tight", children: title }),
        /* @__PURE__ */ jsx("p", { className: "text-sm text-muted mt-1 leading-relaxed", children: isScheduled ? /* @__PURE__ */ jsxs(Fragment, { children: [
          "Will charge ",
          /* @__PURE__ */ jsx("strong", { className: "text-foreground", children: formatDollars(amount) }),
          /* @__PURE__ */ jsx(Instrument, { name }),
          " on",
          " ",
          /* @__PURE__ */ jsx("strong", { className: "text-foreground", children: scheduledLabel }),
          ". You'll need to return to this order once the payment has processed to complete it."
        ] }) : isPosted ? /* @__PURE__ */ jsxs(Fragment, { children: [
          "Transferred ",
          /* @__PURE__ */ jsx("strong", { className: "text-foreground", children: formatDollars(amount) }),
          /* @__PURE__ */ jsx(Instrument, { name, preposition: "from" }),
          ". Funds clear in up to 5 business days; we'll flag the account if the transfer is returned."
        ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
          "Charged ",
          /* @__PURE__ */ jsx("strong", { className: "text-foreground", children: formatDollars(amount) }),
          /* @__PURE__ */ jsx(Instrument, { name }),
          "."
        ] }) })
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-3 gap-3 px-4 py-3.5 bg-background border border-border rounded-xl", children: [
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("p", { className: "text-[10.5px] font-bold tracking-widest uppercase text-muted", children: isScheduled ? "Will charge" : "Charged" }),
        /* @__PURE__ */ jsx("p", { className: "text-sm font-bold text-foreground mt-0.5 tabular-nums", children: formatDollars(amount) })
      ] }),
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("p", { className: "text-[10.5px] font-bold tracking-widest uppercase text-muted", children: isScheduled ? "On" : "Method" }),
        /* @__PURE__ */ jsx("p", { className: "text-sm font-bold text-foreground mt-0.5 truncate", children: isScheduled ? scheduledFor ? scheduledFor.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric"
        }) : "—" : name || (payment.method === "card" ? "Card" : "Bank account") })
      ] }),
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("p", { className: "text-[10.5px] font-bold tracking-widest uppercase text-muted", children: "Status" }),
        /* @__PURE__ */ jsx(
          "p",
          {
            className: `text-sm font-bold mt-0.5 ${(record ? record.statusType === PaymentStatusType.Paid : !isScheduled && !isPosted) ? "text-success" : "text-accent"}`,
            children: record?.statusTypeName ?? (isScheduled ? "Scheduled" : isPosted ? "Posted" : "Paid")
          }
        )
      ] }),
      processorChecks(record).map((check) => /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("p", { className: "text-[10.5px] font-bold tracking-widest uppercase text-muted", children: check.label }),
        /* @__PURE__ */ jsx("p", { className: "text-sm font-bold text-foreground mt-0.5 font-mono", children: check.value })
      ] }, check.label))
    ] })
  ] });
}
function ProcessingCard({ payment, record, amount, stopped, onCheckAgain }) {
  return /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3.5", children: [
    /* @__PURE__ */ jsx("span", { className: "w-11 h-11 rounded-full inline-flex items-center justify-center flex-none bg-accent-soft text-accent", children: stopped ? /* @__PURE__ */ jsx("i", { className: "ri-time-fill" }) : /* @__PURE__ */ jsx(Spinner, { size: "sm" }) }),
    /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
      /* @__PURE__ */ jsx("p", { className: "text-lg font-bold text-foreground tracking-tight", children: stopped ? "Still waiting on the processor" : "Processing payment…" }),
      /* @__PURE__ */ jsxs("p", { className: "text-sm text-muted mt-1 leading-relaxed", children: [
        "Submitted ",
        /* @__PURE__ */ jsx("strong", { className: "text-foreground", children: formatDollars(amount) }),
        /* @__PURE__ */ jsx(Instrument, { name: instrumentName(payment, record) }),
        ".",
        " ",
        stopped ? "No answer yet — check again in a moment." : "Checking for the result every few seconds."
      ] }),
      stopped && /* @__PURE__ */ jsxs(Button, { variant: "secondary", size: "sm", className: "mt-3", onPress: onCheckAgain, children: [
        /* @__PURE__ */ jsx("i", { className: "ri-refresh-line" }),
        "Check again"
      ] })
    ] })
  ] });
}
const METHOD_LABELS = { card: "Card", bank: "Bank account" };
function AttemptMeta({ attempt, className = "" }) {
  return /* @__PURE__ */ jsxs("p", { className: `text-xs text-muted ${className}`, children: [
    /* @__PURE__ */ jsx("span", { className: "tabular-nums", children: formatDollars(attempt.amount) }),
    attempt.paymentDateUtc && /* @__PURE__ */ jsxs(Fragment, { children: [
      " · ",
      parseAndFormatDateTime(attempt.paymentDateUtc)
    ] }),
    " ",
    "· ",
    METHOD_LABELS[attempt.method]
  ] });
}
function DeclineBanner({
  payment,
  attempt = null,
  canRetry = true,
  canCorrect = false,
  canRetrySame = true,
  inFlight = false,
  onRetrySame,
  onCorrectAndRetry,
  onTryNew
}) {
  const reason = attempt ? attempt.reason : payment.declineReason || "Declined by issuer";
  const name = attempt ? attempt.instrument : instrumentName(payment);
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3 px-4 py-4 bg-danger-soft border border-danger rounded-xl mb-4.5", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3", children: [
      /* @__PURE__ */ jsx("span", { className: "w-8 h-8 rounded-full bg-danger text-danger-foreground inline-flex items-center justify-center flex-none", children: /* @__PURE__ */ jsx("i", { className: "ri-close-circle-fill" }) }),
      /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
        /* @__PURE__ */ jsx("p", { className: "text-sm font-bold text-foreground", children: "Payment declined" }),
        /* @__PURE__ */ jsxs("p", { className: "text-xs text-danger mt-0.5", children: [
          /* @__PURE__ */ jsx("strong", { children: reason }),
          name && /* @__PURE__ */ jsxs(Fragment, { children: [
            " ",
            "· ",
            /* @__PURE__ */ jsx("code", { className: "font-mono", children: name })
          ] })
        ] }),
        attempt && /* @__PURE__ */ jsx(AttemptMeta, { attempt, className: "mt-0.5" }),
        /* @__PURE__ */ jsx(ProcessorChecks, { checks: attempt?.checks, className: "mt-1" })
      ] })
    ] }),
    canRetry ? /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap gap-2", children: [
      canCorrect ? /* @__PURE__ */ jsxs(
        Button,
        {
          variant: "danger",
          size: "sm",
          onPress: () => attempt ? onCorrectAndRetry(attempt.paymentId) : onCorrectAndRetry(),
          children: [
            /* @__PURE__ */ jsx("i", { className: "ri-edit-line" }),
            "Update card & retry"
          ]
        }
      ) : canRetrySame && /* @__PURE__ */ jsxs(Button, { variant: "danger", size: "sm", onPress: () => onRetrySame(), children: [
        /* @__PURE__ */ jsx("i", { className: "ri-refresh-line" }),
        "Retry with same info"
      ] }),
      /* @__PURE__ */ jsx(Button, { variant: "danger-soft", size: "sm", onPress: () => onTryNew(), children: "Try a different payment" })
    ] }) : inFlight && /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted", children: [
      /* @__PURE__ */ jsx("i", { className: "ri-time-line" }),
      " A payment is still being processed. Wait for it to finish before retrying."
    ] })
  ] });
}
const ATTEMPT_STATUS_TITLES = {
  paid: "Payment captured",
  posted_pending: "Payment posted — pending bank verification",
  scheduled: "Payment scheduled",
  processing: "Payment processing"
};
function AttemptStatusCard({ attempt }) {
  const tone = ATTEMPT_TONES[attempt.status] ?? ATTEMPT_TONES.other;
  return /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3 px-4 py-4 bg-background border border-border rounded-xl mb-4.5", children: [
    /* @__PURE__ */ jsx(
      "span",
      {
        className: `w-8 h-8 rounded-full inline-flex items-center justify-center flex-none ${tone.badge}`,
        children: /* @__PURE__ */ jsx("i", { className: tone.icon })
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
      /* @__PURE__ */ jsxs("p", { className: "text-sm font-bold text-foreground", children: [
        ATTEMPT_STATUS_TITLES[attempt.status] ?? `Payment ${attempt.statusTypeName}`,
        attempt.statusTypeName && /* @__PURE__ */ jsx("span", { className: `ml-2 text-[10.5px] font-bold tracking-wider uppercase ${tone.text}`, children: attempt.statusTypeName })
      ] }),
      attempt.instrument && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted mt-0.5", children: /* @__PURE__ */ jsx("code", { className: "font-mono text-foreground", children: attempt.instrument }) }),
      /* @__PURE__ */ jsx(AttemptMeta, { attempt, className: "mt-0.5" }),
      /* @__PURE__ */ jsx(ProcessorChecks, { checks: attempt.checks, className: "mt-1" })
    ] })
  ] });
}
function AttemptList({ attempts, selectedId = null, onSelect = () => {
} }) {
  if (!attempts || attempts.length === 0) return null;
  return /* @__PURE__ */ jsxs("div", { className: "mt-4.5 pt-3.5 border-t border-dashed border-border", children: [
    /* @__PURE__ */ jsxs("p", { className: "text-[10.5px] font-bold tracking-widest uppercase text-muted mb-2", children: [
      "Payment attempts",
      /* @__PURE__ */ jsx("span", { className: "ml-2 font-normal normal-case tracking-normal", children: "Select one to see its details." })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-1.5", children: attempts.map((a) => {
      const tone = ATTEMPT_TONES[a.status] ?? ATTEMPT_TONES.other;
      const selected = a.paymentId === selectedId;
      return /* @__PURE__ */ jsxs(
        "button",
        {
          type: "button",
          "aria-pressed": selected,
          onClick: () => onSelect(a.paymentId),
          className: `flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-xs text-left cursor-pointer border transition-colors ${selected ? "bg-accent-soft border-accent" : "bg-background border-transparent hover:border-border"}`,
          children: [
            /* @__PURE__ */ jsx("i", { className: `${tone.icon} ${tone.text} flex-none` }),
            /* @__PURE__ */ jsxs("span", { className: "text-foreground font-medium flex-1 min-w-0", children: [
              a.instrument || METHOD_LABELS[a.method],
              /* @__PURE__ */ jsxs("span", { className: "text-muted font-normal", children: [
                " ",
                "· ",
                formatDollars(a.amount),
                a.paymentDateUtc ? ` · ${parseAndFormatDateTime(a.paymentDateUtc)}` : "",
                a.reason ? ` · ${a.reason}` : "",
                a.checks.map((check) => ` · ${check.label} ${check.value}`).join("")
              ] })
            ] }),
            selected && /* @__PURE__ */ jsx("span", { className: "text-[10.5px] font-bold tracking-wider uppercase text-accent", children: "Selected" }),
            /* @__PURE__ */ jsx("span", { className: `text-[10.5px] font-bold tracking-wider uppercase ${tone.text}`, children: a.statusTypeName || a.status })
          ]
        },
        a.paymentId
      );
    }) })
  ] });
}
function SumLine({ label, right, sub, accent }) {
  return /* @__PURE__ */ jsxs("div", { className: "flex items-baseline justify-between gap-2.5 py-1", children: [
    /* @__PURE__ */ jsx(
      "span",
      {
        className: `${sub ? "text-[11.5px] text-muted pl-2.5" : "text-xs text-muted font-medium"}`,
        children: label
      }
    ),
    /* @__PURE__ */ jsx(
      "span",
      {
        className: `${sub ? "text-[11.5px]" : "text-xs font-semibold"} ${accent ? "text-accent font-bold" : sub ? "text-muted" : "text-foreground"} tabular-nums`,
        children: right
      }
    )
  ] });
}
function SumSection({ title, children }) {
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsx("p", { className: "text-[10.5px] font-bold tracking-widest uppercase text-muted pb-1.5 border-b border-border mb-1", children: title }),
    children
  ] });
}
function OrderSummary({ products, payment, order = summarizeOrder(null) }) {
  const fromDate = scheduledChargeDate(payment);
  const { cad, nextDate } = calcBillingDates(products, fromDate);
  const isScheduled = !!fromDate;
  const dueLabel = isScheduled ? `DUE ${fromDate.toLocaleDateString("en-US", { month: "short", day: "numeric" }).toUpperCase()}` : "DUE TODAY";
  const settled = order.balanceCents <= 0 && order.totalCents > 0;
  const deviceName = isProductSelected(products.device) ? ProductDisplay[products.device] : null;
  const configLabel = productConfigLabel(products);
  return /* @__PURE__ */ jsx(Card, { shadow: "sm", className: "sticky top-0", children: /* @__PURE__ */ jsxs(CardContent, { className: "flex flex-col gap-3.5 p-4.5", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-2", children: [
      /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
        /* @__PURE__ */ jsx("p", { className: "text-[10.5px] font-bold tracking-widest uppercase text-muted", children: "Order summary" }),
        /* @__PURE__ */ jsx("p", { className: "text-base font-semibold text-foreground mt-0.5", children: deviceName || /* @__PURE__ */ jsx("span", { className: "text-muted font-normal", children: "No device selected" }) }),
        deviceName && configLabel && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted mt-0.5", children: configLabel })
      ] }),
      /* @__PURE__ */ jsx(
        Chip,
        {
          size: "sm",
          color: "primary",
          variant: "flat",
          className: "flex-none uppercase text-[10.5px]",
          children: cad.label
        }
      )
    ] }),
    /* @__PURE__ */ jsxs(
      SumSection,
      {
        title: `Recurring (per ${cad.months === 1 ? "month" : `${cad.months} months`})`,
        children: [
          order.plan ? /* @__PURE__ */ jsx(
            SumLine,
            {
              label: `${cad.label} subscription`,
              right: formatCents(order.recurringCents)
            }
          ) : /* @__PURE__ */ jsx("p", { className: "py-1.5 text-xs text-muted", children: "No recurring charges configured." }),
          order.includedLines.map((line) => /* @__PURE__ */ jsx(SumLine, { sub: true, label: `Includes ${line.name}`, right: "" }, line.id))
        ]
      }
    ),
    order.chargedLines.length > 0 && /* @__PURE__ */ jsx(SumSection, { title: "One-time charges", children: order.chargedLines.map((line) => /* @__PURE__ */ jsx(
      SumLine,
      {
        label: `${line.name}${line.quantity > 1 ? ` × ${line.quantity}` : ""}`,
        right: formatCents(line.totalCents)
      },
      line.id
    )) }),
    /* @__PURE__ */ jsxs("div", { className: "mt-1 px-4 py-3.5 bg-accent rounded-xl text-white", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-baseline justify-between gap-2", children: [
        /* @__PURE__ */ jsx("span", { className: "text-xs font-semibold opacity-90 tracking-wide", children: settled ? "ORDER TOTAL" : dueLabel }),
        /* @__PURE__ */ jsx("span", { className: "text-2xl font-bold tabular-nums tracking-tight", children: formatCents(settled ? order.totalCents : order.balanceCents) })
      ] }),
      isScheduled && !settled && /* @__PURE__ */ jsxs("p", { className: "text-[11px] opacity-90 mt-1", children: [
        "Post-dated · will be charged on ",
        fmtFullDate(fromDate)
      ] })
    ] }),
    nextDate && order.recurringCents > 0 && /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-2.5 px-3 py-2.5 bg-background border border-border rounded-lg text-xs", children: [
      /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
        /* @__PURE__ */ jsx("p", { className: "text-muted", children: "Next bill" }),
        /* @__PURE__ */ jsx("p", { className: "font-semibold text-foreground mt-0.5", children: fmtFullDate(nextDate) })
      ] }),
      /* @__PURE__ */ jsx("p", { className: "font-bold text-foreground tabular-nums text-sm", children: formatCents(order.recurringCents) })
    ] })
  ] }) });
}
const PaymentForm = ({
  data = INITIAL_FORM.payment,
  products = INITIAL_FORM.products,
  order = summarizeOrder(null),
  paymentRecord = null,
  attempts = [],
  inFlight = false,
  retryInstrument = null,
  defaults = {},
  homeAddress = null,
  shippingAddress = null,
  canSubmit = false,
  processing = false,
  retrying = false,
  pollingStopped = false,
  onSetMethod = () => {
  },
  onSetCard = () => {
  },
  onSetBank = () => {
  },
  onSetBilling = () => {
  },
  onSetSchedule = () => {
  },
  onSetScheduledDate = () => {
  },
  onSubmit = () => {
  },
  onRetrySame = () => {
  },
  onCorrectAndRetry = () => {
  },
  onTryNew = () => {
  },
  onCheckAgain = () => {
  },
  onSelectAttempt = () => {
  }
}) => {
  const amount = paymentRecord?.amount ?? fromCents(order.balanceCents);
  const isSuccess = data.status === "paid" || data.status === "posted_pending" || data.status === "scheduled";
  const message = paymentMessage(data, attempts, {
    inFlight,
    settled: order.balanceCents <= 0
  });
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: "grid gap-5.5 items-start w-full max-w-6xl",
      style: { gridTemplateColumns: "minmax(0, 1.5fr) minmax(300px, 1fr)" },
      children: [
        /* @__PURE__ */ jsx("style", { children: `@keyframes spin { to { transform: rotate(360deg); } }` }),
        /* @__PURE__ */ jsx(Card, { shadow: "sm", children: /* @__PURE__ */ jsxs(CardContent, { className: "p-6 flex flex-col gap-4", children: [
          message?.kind === "status" && /* @__PURE__ */ jsx(AttemptStatusCard, { attempt: message.attempt }),
          message?.kind === "declined" && /* @__PURE__ */ jsx(
            DeclineBanner,
            {
              payment: data,
              attempt: message.attempt,
              canRetry: message.canRetry,
              canCorrect: message.canCorrect,
              canRetrySame: message.canRetrySame,
              inFlight,
              onRetrySame,
              onCorrectAndRetry,
              onTryNew
            }
          ),
          isSuccess ? /* @__PURE__ */ jsx(
            SuccessCard,
            {
              status: data.status,
              payment: data,
              record: paymentRecord,
              amount
            }
          ) : data.status === "processing" ? /* @__PURE__ */ jsx(
            ProcessingCard,
            {
              payment: data,
              record: paymentRecord,
              amount,
              stopped: pollingStopped,
              onCheckAgain
            }
          ) : /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(
              PaymentFormMethod,
              {
                data,
                defaults,
                retrying,
                retryInstrument,
                onTryNew,
                onSetMethod,
                onSetCard,
                onSetBank
              }
            ),
            data.method && /* @__PURE__ */ jsx(
              PaymentFormBilling,
              {
                value: data.billing ?? INITIAL_FORM.payment.billing,
                homeAddress,
                shippingAddress,
                onChange: onSetBilling
              }
            ),
            data.method && /* @__PURE__ */ jsx(
              PaymentFormSchedule,
              {
                data,
                dueCents: order.balanceCents,
                canSubmit,
                processing,
                retrying,
                onSetSchedule,
                onSetScheduledDate,
                onSubmit
              }
            )
          ] }),
          /* @__PURE__ */ jsx(
            AttemptList,
            {
              attempts,
              selectedId: data.selectedPaymentId,
              onSelect: onSelectAttempt
            }
          )
        ] }) }),
        /* @__PURE__ */ jsx(OrderSummary, { products, payment: data, order })
      ]
    }
  );
};
function splitName(full, fallback = {}) {
  const parts = (full || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return { firstName: fallback.firstName || "", lastName: fallback.lastName || "" };
  }
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: fallback.lastName || "" };
  }
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const POLL_INTERVAL_MS = 2e3;
const MAX_POLLS = 60;
function scheduledDateInRange(scheduledDate) {
  if (!scheduledDate) return false;
  const date = localDate(scheduledDate);
  if (!date || isNaN(date)) return false;
  const today = /* @__PURE__ */ new Date();
  today.setHours(0, 0, 0, 0);
  const min = new Date(today);
  min.setDate(min.getDate() + 1);
  const max = new Date(today);
  max.setDate(max.getDate() + 90);
  max.setHours(23, 59, 59, 999);
  return date >= min && date <= max;
}
const PaymentController = reactive.component(
  ({ services, monitors }) => {
    const { enrollmentId, enrollment, setEnrollment, form, patchForm } = useOutletContext();
    const data = form.payment;
    const products = form.products;
    const customer = form.customer;
    const onChange = (patch) => patchForm("payment", patch);
    const defaults = {
      name: [customer.firstName, customer.lastName].filter(Boolean).join(" "),
      phone: customer.phones?.cell || customer.phones?.home || "",
      email: customer.email || ""
    };
    const processing = monitors.addPayment.executing;
    const awaitingOutcome = data.status === "processing";
    const retrying = data.retryOfPaymentId != null;
    const [polls, setPolls] = useState(0);
    const isLater = data.schedule === "later";
    const order = summarizeOrder(enrollment);
    const attempts = paymentAttempts(enrollment);
    const inFlight = awaitingOutcome || hasPaymentInFlight(enrollment);
    const retryInstrument = retrying ? attempts.find((a) => a.paymentId === data.retryOfPaymentId)?.instrument ?? null : null;
    const setMethod = (method) => onChange({
      method,
      status: "idle",
      declineReason: "",
      retryOfPaymentId: null,
      selectedPaymentId: null
    });
    const setCard = (card) => onChange({ card });
    const setBank = (bank) => onChange({ bank });
    const setBilling = (billing2) => onChange({ billing: billing2 });
    const setSchedule = (schedule) => onChange({ schedule, status: "idle", declineReason: "", selectedPaymentId: null });
    const setScheduledDate = (scheduledDate) => onChange({ scheduledDate });
    const selectAttempt = (paymentId) => onChange({ selectedPaymentId: paymentId });
    const retrySame = () => onChange({ status: "idle", declineReason: "", selectedPaymentId: null });
    const correctAndRetry = (paymentId = data.paymentId) => onChange({
      status: "idle",
      declineReason: "",
      method: "card",
      retryOfPaymentId: paymentId,
      selectedPaymentId: null
    });
    const tryNew = () => onChange({
      status: "idle",
      declineReason: "",
      retryOfPaymentId: null,
      selectedPaymentId: null,
      method: "",
      card: { ...INITIAL_CARD },
      bank: { ...INITIAL_BANK }
    });
    const scheduledDateValid = !isLater || scheduledDateInRange(data.scheduledDate);
    const billing = data.billing ?? { sameAsHome: true };
    const homeAddress = customer.address;
    const shippingAddress = shippingAddressOf(customer);
    const billingAddress = billing.sameAsHome ? homeAddress : billing.sameAsShipping ? shippingAddress : billing.address;
    const billingAddressValid = ["address1", "city", "state", "postalCode"].every(
      (k) => (billingAddress?.[k] || "").trim().length > 0
    );
    const canSubmit = (() => {
      if (processing) return false;
      if (inFlight) return false;
      if (order.balanceCents <= 0) return false;
      if (!scheduledDateValid) return false;
      if (!billingAddressValid) return false;
      if (data.method === "card") {
        const c = data.card || {};
        const phoneOk = onlyDigits(c.phone).length >= 10;
        const emailOk = EMAIL.test((c.email || "").trim());
        return (phoneOk || emailOk) && (!(c.email || "").trim() || emailOk) && // A retry corrects the stored card, whose number and holder can't change — and after a
        // reload aren't in hand — so only the expiry, CVV and contact are required then.
        (retrying || onlyDigits(c.number).length >= 13) && (retrying || (c.name || "").trim().length > 1) && onlyDigits(c.exp).length === 4 && onlyDigits(c.cvv).length >= 3;
      }
      if (data.method === "bank") {
        const b = data.bank || {};
        return (b.routing || "").length === 9 && (ROUTING_BANKS[b.routing] || (b.bankName || "").trim().length > 1) && (b.accountNumber || "").length >= 4 && (b.accountName || "").trim().length > 1;
      }
      return false;
    })();
    function buildProfile() {
      const contact = {
        phoneNumber: customer.phones?.cell || customer.phones?.home || customer.phones?.work || null,
        email: customer.email || null
      };
      if (data.method === "card") {
        const c = data.card || {};
        const exp = onlyDigits(c.exp);
        const yy = exp.slice(2);
        return {
          profileType: PaymentProfileType.CreditCard,
          // The API requires a display name for the stored profile. A retry keeps the stored one.
          name: retryInstrument ?? `${cardBrand(c.number) || "Card"} •••• ${last4(c.number)}`,
          cardNumber: onlyDigits(c.number),
          expirationMonth: exp.slice(0, 2),
          // Two-digit years read as expired ("28" → year 28) — send the full year.
          expirationYear: yy.length === 2 ? `20${yy}` : yy,
          cvv: c.cvv,
          // The billing contact entered on the card form, not the customer's own.
          phoneNumber: (c.phone || "").trim() || null,
          email: (c.email || "").trim() || null,
          ...splitName(c.name, customer)
        };
      }
      const b = data.bank || {};
      return {
        profileType: PaymentProfileType.Ach,
        name: `${ROUTING_BANKS[b.routing] || b.bankName || "Bank"} •••• ${last4(b.accountNumber)}`,
        routingNumber: b.routing,
        accountNumber: b.accountNumber,
        accountType: b.accountType,
        bankName: ROUTING_BANKS[b.routing] || b.bankName,
        ...contact,
        ...splitName(b.accountName, customer)
      };
    }
    function submit() {
      if (!canSubmit) return;
      const request = {
        enrollmentId,
        profile: buildProfile(),
        billingAddress: withoutRecordIdentity(billingAddress),
        amount: enrollment.orderBalance
      };
      if (isLater) {
        request.paymentDateUtc = toISO(data.scheduledDate);
      }
      if (retrying) {
        request.retryOfPaymentId = data.retryOfPaymentId;
      }
      services.enrollments.addPayment(enrollmentId, request);
    }
    useSuccessReactor(services.enrollments.addPayment, (payload) => {
      setEnrollment(payload);
      const paymentId = latestPayment(payload)?.paymentId ?? null;
      if (isLater) {
        patchForm("payment", {
          status: "scheduled",
          declineReason: "",
          paymentId,
          selectedPaymentId: paymentId,
          retryOfPaymentId: null
        });
        return;
      }
      setPolls(0);
      patchForm("payment", {
        status: "processing",
        declineReason: "",
        paymentId,
        selectedPaymentId: paymentId
      });
    });
    const pollingStopped = polls >= MAX_POLLS;
    useEffect(() => {
      if (!awaitingOutcome || pollingStopped) return void 0;
      const timer = setTimeout(() => {
        setPolls((n) => n + 1);
        services.enrollments.getPayments(enrollmentId);
      }, POLL_INTERVAL_MS);
      return () => clearTimeout(timer);
    }, [awaitingOutcome, pollingStopped, polls, enrollmentId]);
    useSuccessReactor(services.enrollments.getPayments, (payload) => {
      setEnrollment(payload);
      if (!awaitingOutcome) return;
      const payment = payload?.payments?.find((p) => p.paymentId === data.paymentId) ?? latestPayment(payload);
      const status = resolveOutcome(payment, payload);
      if (!status) return;
      patchForm("payment", { status, declineReason: "", retryOfPaymentId: null });
    });
    useReactor(services.enrollments.getPayments, {
      error: () => setPolls(MAX_POLLS)
    });
    const checkAgain = () => setPolls(0);
    useReactor(services.enrollments.addPayment, {
      error: ({ payload }) => {
        patchForm("payment", {
          status: "declined",
          declineReason: payload?.message || "Payment could not be submitted",
          selectedPaymentId: null
        });
        services.enrollments.getPayments(enrollmentId);
      }
    });
    return /* @__PURE__ */ jsx(
      PaymentForm,
      {
        data,
        products,
        order,
        defaults,
        paymentRecord: enrollment?.payments?.find((p) => p.paymentId === data.paymentId) ?? null,
        attempts,
        inFlight,
        retryInstrument,
        homeAddress,
        shippingAddress,
        canSubmit,
        processing,
        retrying,
        pollingStopped,
        onSetMethod: setMethod,
        onSetCard: setCard,
        onSetBank: setBank,
        onSetBilling: setBilling,
        onSetSchedule: setSchedule,
        onSetScheduledDate: setScheduledDate,
        onSubmit: submit,
        onRetrySame: retrySame,
        onCorrectAndRetry: correctAndRetry,
        onCheckAgain: checkAgain,
        onTryNew: tryNew,
        onSelectAttempt: selectAttempt
      }
    );
  },
  {
    name: "PaymentController",
    monitors: (services) => [services.enrollments.addPayment]
  }
);
const contactName = (c) => [c.firstName, c.mi ? `${c.mi}.` : "", c.lastName].filter(Boolean).join(" ") || c.fullName || "Unnamed contact";
function calcAge(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d)) return null;
  const today = /* @__PURE__ */ new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || m === 0 && today.getDate() < d.getDate()) age--;
  if (age < 0 || age > 130) return null;
  return age;
}
const REL_LABEL = {
  spouse: "Spouse",
  child: "Child",
  sibling: "Sibling",
  parent: "Parent",
  neighbor: "Neighbor",
  caregiver: "Caregiver",
  friend: "Friend",
  other: "Other"
};
function SumCard({ icon, title, children }) {
  return /* @__PURE__ */ jsx(Card, { shadow: "sm", children: /* @__PURE__ */ jsxs(CardContent, { className: "flex flex-col gap-2.5 px-4 py-3.5", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 text-[11px] font-bold tracking-widest uppercase text-muted", children: [
      /* @__PURE__ */ jsx("i", { className: icon }),
      title
    ] }),
    children
  ] }) });
}
function SumRow({ label, value, mono, accent, large }) {
  return /* @__PURE__ */ jsxs("div", { className: "flex justify-between items-baseline gap-3", children: [
    /* @__PURE__ */ jsx("span", { className: "text-xs text-muted flex-none", children: label }),
    /* @__PURE__ */ jsx(
      "span",
      {
        className: `${large ? "text-base font-bold" : "text-sm font-semibold"} ${accent ? "text-accent" : "text-foreground"} ${mono ? "font-mono" : ""} tabular-nums text-right`,
        children: value || /* @__PURE__ */ jsx("span", { className: "text-muted font-normal", children: "—" })
      }
    )
  ] });
}
function SumDivider() {
  return /* @__PURE__ */ jsx("div", { className: "border-t border-border my-0.5" });
}
function OrderSummaryPanel({ form, order }) {
  const products = form.products || {};
  const payment = form.payment || {};
  const customer = form.customer || {};
  const contacts = form.contacts?.list || [];
  const medical = form.medical || {};
  const hasDevice = isProductSelected(products.device);
  const { cad, nextDate } = calcBillingDates(products, scheduledChargeDate(payment));
  const isCard = payment.method === "card";
  const tail = isCard ? last4(payment.card?.number) : last4(payment.bank?.accountNumber);
  const age = calcAge(customer.dob);
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3", children: [
    /* @__PURE__ */ jsxs(SumCard, { icon: "user-line", title: "Customer", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
        /* @__PURE__ */ jsxs("span", { className: "w-10 h-10 rounded-full bg-accent-soft text-accent inline-flex items-center justify-center flex-none text-base font-bold", children: [
          customer.firstName?.[0] || "?",
          customer.lastName?.[0] || ""
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
          /* @__PURE__ */ jsx("p", { className: "text-base font-bold text-foreground tracking-tight", children: [customer.firstName, customer.lastName].filter(Boolean).join(" ") || "Not entered" }),
          /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted mt-0.5", children: [
            age ? `${age} years old` : "",
            customer.address?.city ? ` · ${customer.address.city}, ${customer.address.state}` : ""
          ] })
        ] })
      ] }),
      /* @__PURE__ */ jsx(SumDivider, {}),
      customer.phones?.cell && /* @__PURE__ */ jsx(SumRow, { label: "Cell", value: customer.phones.cell }),
      customer.address?.address1 && /* @__PURE__ */ jsx(
        SumRow,
        {
          label: "Address",
          value: `${customer.address.address1}${customer.address.address2 ? ` ${customer.address.address2}` : ""}, ${customer.address.postalCode}`
        }
      )
    ] }),
    /* @__PURE__ */ jsx(SumCard, { icon: "archive-line", title: "Device & plan", children: hasDevice ? /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2.5", children: [
        /* @__PURE__ */ jsx("span", { className: "w-8.5 h-8.5 rounded-md bg-accent text-white inline-flex items-center justify-center flex-none", children: /* @__PURE__ */ jsx("i", { className: ProductIcon[products.device] }) }),
        /* @__PURE__ */ jsxs("div", { className: "flex-1", children: [
          /* @__PURE__ */ jsx("p", { className: "text-sm font-bold text-foreground", children: ProductDisplay[products.device] }),
          /* @__PURE__ */ jsx("p", { className: "text-xs text-muted mt-0.5", children: productConfigLabel(products) })
        ] })
      ] }),
      /* @__PURE__ */ jsx(SumDivider, {}),
      /* @__PURE__ */ jsx(
        SumRow,
        {
          label: "Subscription",
          value: `${formatCents(order.recurringCents)} / ${cad.months === 1 ? "mo" : `${cad.months} mo`}`,
          large: true
        }
      ),
      order.includedLines.map((line) => /* @__PURE__ */ jsx(SumRow, { label: "Includes", value: line.name }, line.id)),
      /* @__PURE__ */ jsx(SumDivider, {}),
      /* @__PURE__ */ jsx(
        SumRow,
        {
          label: "Bill cadence",
          value: `${cad.label} · ${products.subscription?.scheduleMode === "dayOfWeek" ? `${["1st", "2nd", "3rd", "4th", "last"][products.subscription.weekOfMonth === "last" ? 4 : products.subscription.weekOfMonth - 1]} ${["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][products.subscription.dayOfWeek]}` : products.subscription?.dayOfMonth != null ? ordinalDay(products.subscription.dayOfMonth) : "—"} of month`
        }
      )
    ] }) : /* @__PURE__ */ jsx("p", { className: "text-sm text-muted", children: "No device selected" }) }),
    /* @__PURE__ */ jsxs(SumCard, { icon: "bank-card-line", title: "Payment", children: [
      /* @__PURE__ */ jsx(SumRow, { label: "Order total", value: formatCents(order.totalCents), large: true, accent: true }),
      /* @__PURE__ */ jsx(SumDivider, {}),
      order.chargedLines.map((line) => /* @__PURE__ */ jsx(
        SumRow,
        {
          label: `${line.name}${line.quantity > 1 ? ` × ${line.quantity}` : ""}`,
          value: formatCents(line.totalCents)
        },
        line.id
      )),
      order.balanceCents > 0 && /* @__PURE__ */ jsx(SumRow, { label: "Balance still due", value: formatCents(order.balanceCents) }),
      /* @__PURE__ */ jsx(SumDivider, {}),
      /* @__PURE__ */ jsx(
        SumRow,
        {
          label: payment.schedule === "later" ? `Scheduled · ${localDate(payment.scheduledDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : payment.status === "paid" ? "Paid" : payment.status === "posted_pending" ? "Posted (pending)" : "Scheduled",
          value: isCard ? `${cardBrand(payment.card?.number) || "Card"} ••••${tail}` : `${payment.bank?.accountType === "savings" ? "Savings" : "Checking"} ••••${tail}`,
          mono: true
        }
      ),
      nextDate && /* @__PURE__ */ jsx(
        SumRow,
        {
          label: `Next ${cad.label.toLowerCase()} bill`,
          value: `${nextDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })} · ${formatCents(order.recurringCents)}`
        }
      )
    ] }),
    /* @__PURE__ */ jsx(SumCard, { icon: "group-line", title: "Emergency contacts", children: contacts.length === 0 ? /* @__PURE__ */ jsx("p", { className: "text-xs text-muted", children: "None added" }) : /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-1.5", children: contacts.map((c, i) => /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsx(
        "span",
        {
          className: `w-5 h-5 rounded-full flex-none inline-flex items-center justify-center text-[10px] font-bold ${i === 0 ? "bg-accent text-white" : "bg-background border border-border text-muted"}`,
          children: i + 1
        }
      ),
      /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold text-foreground flex-1", children: contactName(c) }),
      /* @__PURE__ */ jsxs("span", { className: "text-xs text-muted", children: [
        REL_LABEL[c.relationship] || c.relationship,
        c.enableLogin ? " · Login" : ""
      ] })
    ] }, c.id)) }) }),
    (medical.medications?.length > 0 || medical.usesOxygen || medical.isDisabled || medical.preferredHospital) && /* @__PURE__ */ jsxs(SumCard, { icon: "heart-pulse-line", title: "Medical flags", children: [
      medical.medications?.length > 0 && /* @__PURE__ */ jsx(SumRow, { label: "Medications", value: `${medical.medications.length} listed` }),
      medical.usesOxygen && /* @__PURE__ */ jsx(SumRow, { label: "Oxygen", value: "Uses oxygen" }),
      medical.isDisabled && /* @__PURE__ */ jsx(SumRow, { label: "Mobility", value: "Disabled / limited" }),
      medical.preferredHospital && /* @__PURE__ */ jsx(SumRow, { label: "Hospital", value: medical.preferredHospital })
    ] })
  ] });
}
const ReviewFormSummary = ({ data, order }) => /* @__PURE__ */ jsx(OrderSummaryPanel, { form: data, order });
function ConfirmCheckbox({ label, hint, isSelected, isDisabled, onChange }) {
  return /* @__PURE__ */ jsx(
    Checkbox,
    {
      isSelected,
      isDisabled,
      onChange,
      variant: "secondary",
      children: /* @__PURE__ */ jsxs(Checkbox.Content, { children: [
        /* @__PURE__ */ jsx(Checkbox.Control, { children: /* @__PURE__ */ jsx(Checkbox.Indicator, {}) }),
        /* @__PURE__ */ jsx(Label, { children: label }),
        hint && /* @__PURE__ */ jsx("span", { className: "text-xs text-muted", children: hint })
      ] })
    }
  );
}
function SubmitButton({
  form,
  order,
  submitting = false,
  disabled = true,
  confirm = { sms: false, email: false },
  onConfirmChange = () => {
  },
  onSubmit
}) {
  const products = form.products || {};
  const payment = form.payment || {};
  const { nextDate } = calcBillingDates(products, scheduledChargeDate(payment));
  const isLater = payment.schedule === "later";
  const cell = form.customer?.phones?.cell;
  const email = form.customer?.email;
  return /* @__PURE__ */ jsx(Card, { shadow: "sm", className: "sticky top-0", children: /* @__PURE__ */ jsxs(CardContent, { className: "p-5 flex flex-col gap-3.5", children: [
    /* @__PURE__ */ jsx("p", { className: "text-[11px] font-bold tracking-widest uppercase text-muted", children: "Ready to Complete" }),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex justify-between items-baseline gap-2", children: [
        /* @__PURE__ */ jsx("span", { className: "text-xs text-muted", children: isLater ? `Scheduled charge (${localDate(payment.scheduledDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })})` : "Charged today" }),
        /* @__PURE__ */ jsx("span", { className: "text-2xl font-bold text-foreground tabular-nums tracking-tight", children: formatCents(order.chargedCents) })
      ] }),
      nextDate && /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted", children: [
        "Next bill: ",
        nextDate.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        " ",
        "· ",
        formatCents(order.recurringCents)
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
      /* @__PURE__ */ jsx("p", { className: "text-xs font-medium text-muted", children: "Send order confirmation" }),
      /* @__PURE__ */ jsx(
        ConfirmCheckbox,
        {
          label: "SMS",
          hint: cell || "No cell number on file",
          isSelected: confirm.sms,
          isDisabled: !cell || submitting,
          onChange: (sms) => onConfirmChange({ ...confirm, sms })
        }
      ),
      /* @__PURE__ */ jsx(
        ConfirmCheckbox,
        {
          label: "Email",
          hint: email || "No email on file",
          isSelected: confirm.email,
          isDisabled: !email || submitting,
          onChange: (value) => onConfirmChange({ ...confirm, email: value })
        }
      )
    ] }),
    /* @__PURE__ */ jsx(
      Button,
      {
        color: "primary",
        size: "md",
        isDisabled: disabled || submitting,
        onPress: onSubmit,
        className: "w-full justify-center h-11 text-base font-bold tracking-tight shadow-sm",
        children: submitting ? /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
          "Completing…"
        ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx("i", { className: "ri-check-double-line" }),
          "Complete order"
        ] })
      }
    )
  ] }) });
}
const genOrderNum = () => {
  const n = Math.floor(1e4 + Math.random() * 9e4);
  return `LF-${(/* @__PURE__ */ new Date()).getFullYear()}-${n}`;
};
const STEP_STATUS = { waiting: "waiting", running: "running", done: "done" };
const CHANNEL_LABEL = { [ConfirmationChannels.Sms]: "SMS", [ConfirmationChannels.Email]: "Email" };
function buildSteps(form, confirmations) {
  const loginContacts = (form.contacts?.list || []).filter((c) => c.enableLogin);
  return [
    { id: "validate", label: "Validating order", ms: 500 },
    { id: "payment", label: "Confirming payment", ms: 900 },
    { id: "activate", label: "Activating subscription", ms: 1100 },
    // Only once finalize has answered, and only for the channels the rep asked for.
    ...confirmations.length > 0 ? [
      {
        id: "confirmation",
        label: confirmations.some((c) => c.submitted) ? "Confirmation sent" : "Confirmation not sent",
        ms: 700,
        subItems: confirmations.map((c) => ({
          label: CHANNEL_LABEL[c.channel] ?? "Confirmation",
          detail: c.submitted ? "Sent" : c.reason || "Not sent",
          ok: c.submitted
        }))
      }
    ] : [],
    ...loginContacts.length > 0 ? [
      {
        id: "logins",
        label: `Creating ${loginContacts.length} portal login${loginContacts.length > 1 ? "s" : ""}`,
        ms: 800 + loginContacts.length * 300,
        subItems: loginContacts.map((c) => ({
          label: contactName(c),
          detail: c.email ? c.email : c.username || "—"
        }))
      }
    ] : [],
    { id: "complete", label: "Order finalised", ms: 400 }
  ];
}
function TimelineIcon({ status }) {
  if (status === "running") {
    return /* @__PURE__ */ jsx("span", { className: "w-7 h-7 rounded-full flex-none bg-accent-soft border-2 border-accent inline-flex items-center justify-center", children: /* @__PURE__ */ jsx("span", { className: "w-3 h-3 rounded-full border-2 border-accent border-t-primary animate-spin inline-block" }) });
  }
  if (status === "done") {
    return /* @__PURE__ */ jsx("span", { className: "w-7 h-7 rounded-full flex-none bg-success text-success-foreground inline-flex items-center justify-center", children: /* @__PURE__ */ jsx("i", { className: "ri-check-line" }) });
  }
  return /* @__PURE__ */ jsx("span", { className: "w-7 h-7 rounded-full flex-none bg-background border-2 border-border inline-flex items-center justify-center" });
}
function SubmissionTimeline({ form, orderNumber, confirmations = [], onDone }) {
  const steps = buildSteps(form, confirmations);
  const [statuses, setStatuses] = useState(
    () => Object.fromEntries(steps.map((s) => [s.id, STEP_STATUS.waiting]))
  );
  const [subRevealed, setSubRevealed] = useState({});
  const [fallbackNum] = useState(() => genOrderNum());
  const orderNum = orderNumber || fallbackNum;
  const [allDone, setAllDone] = useState(false);
  const didRun = useRef(false);
  useEffect(() => {
    if (didRun.current) return;
    didRun.current = true;
    let delay = 300;
    steps.forEach((step, idx) => {
      setTimeout(() => {
        setStatuses((prev) => ({ ...prev, [step.id]: STEP_STATUS.running }));
        if (step.subItems?.length) {
          step.subItems.forEach((_, si) => {
            setTimeout(
              () => {
                setSubRevealed((prev) => ({
                  ...prev,
                  [step.id]: (prev[step.id] || 0) + 1
                }));
              },
              (si + 1) * 220
            );
          });
        }
      }, delay);
      delay += step.ms;
      setTimeout(() => {
        setStatuses((prev) => ({ ...prev, [step.id]: STEP_STATUS.done }));
        if (idx === steps.length - 1) {
          setTimeout(() => setAllDone(true), 350);
        }
      }, delay);
      delay += 120;
    });
  }, []);
  return /* @__PURE__ */ jsx(Card, { shadow: "sm", className: "sticky top-0", children: /* @__PURE__ */ jsxs(CardContent, { className: "p-5 flex flex-col gap-0", children: [
    /* @__PURE__ */ jsx("p", { className: "text-[11px] font-bold tracking-widest uppercase text-muted mb-4", children: "Processing order" }),
    steps.map((step, idx) => {
      const status = statuses[step.id] || "waiting";
      const isLast = idx === steps.length - 1;
      const revealCount = subRevealed[step.id] || 0;
      return /* @__PURE__ */ jsxs("div", { className: "flex gap-3 relative", children: [
        !isLast && /* @__PURE__ */ jsx(
          "div",
          {
            className: `absolute left-3.5 top-7 bottom-0 w-0.5 z-0 transition-colors duration-300 ${status === "done" ? "bg-success" : "bg-default"}`
          }
        ),
        /* @__PURE__ */ jsx("div", { className: "z-10 flex-none pt-0", children: /* @__PURE__ */ jsx(TimelineIcon, { status }) }),
        /* @__PURE__ */ jsxs("div", { className: `flex-1 min-w-0 ${isLast ? "" : "pb-5"}`, children: [
          /* @__PURE__ */ jsx("div", { className: "flex items-center gap-2 min-h-7", children: /* @__PURE__ */ jsx(
            "span",
            {
              className: `text-sm font-semibold transition-colors ${status === "waiting" ? "text-muted" : "text-foreground"}`,
              children: step.label
            }
          ) }),
          step.detail && status !== "waiting" && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted mt-0.5", children: step.detail }),
          step.subItems && step.subItems.slice(0, revealCount).map((sub, si) => /* @__PURE__ */ jsxs(
            "div",
            {
              className: `flex items-center gap-2 mt-1.5 px-2.5 py-1.5 border rounded-lg ${sub.ok === false ? "bg-warning-soft border-warning/50" : "bg-success-soft border-success/50"}`,
              children: [
                /* @__PURE__ */ jsx(
                  "i",
                  {
                    className: `flex-none ${sub.ok === false ? "ri-error-warning-fill text-warning" : "ri-checkbox-circle-fill text-success"}`
                  }
                ),
                /* @__PURE__ */ jsx("span", { className: "text-xs font-medium text-foreground flex-1", children: sub.label }),
                /* @__PURE__ */ jsx("span", { className: "text-[11px] text-muted truncate max-w-40", title: sub.detail, children: sub.detail })
              ]
            },
            si
          ))
        ] })
      ] }, step.id);
    }),
    allDone && /* @__PURE__ */ jsxs("div", { className: "mt-2 flex flex-col gap-2.5 px-3.5 py-4 bg-success-soft border border-success rounded-xl", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2.5", children: [
        /* @__PURE__ */ jsx("span", { className: "w-9 h-9 rounded-full bg-success text-success-foreground inline-flex items-center justify-center flex-none", children: /* @__PURE__ */ jsx("i", { className: "ri-checkbox-circle-fill" }) }),
        /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
          /* @__PURE__ */ jsx("p", { className: "text-sm font-bold text-foreground", children: "Order complete" }),
          /* @__PURE__ */ jsx("p", { className: "font-mono text-xs text-success mt-0.5 font-semibold", children: orderNum })
        ] })
      ] }),
      /* @__PURE__ */ jsxs(Button, { color: "success", size: "sm", onPress: onDone, className: "w-full justify-center", children: [
        /* @__PURE__ */ jsx("i", { className: "ri-check-double-line" }),
        "Done"
      ] })
    ] })
  ] }) });
}
const ReviewFormSubmit = ({
  data,
  order,
  submitted = false,
  submitting = false,
  canSubmit = false,
  orderNumber,
  confirm = { sms: false, email: false },
  confirmations = [],
  onConfirmChange = () => {
  },
  onSubmit = () => {
  },
  onClose = () => {
  }
}) => /* @__PURE__ */ jsx("div", { children: submitted ? /* @__PURE__ */ jsx(
  SubmissionTimeline,
  {
    form: data,
    orderNumber,
    confirmations,
    onDone: onClose
  }
) : /* @__PURE__ */ jsx(
  SubmitButton,
  {
    form: data,
    order,
    submitting,
    disabled: !canSubmit,
    confirm,
    onConfirmChange,
    onSubmit
  }
) });
const ReviewForm = ({
  data = INITIAL_FORM,
  order,
  submitted = false,
  submitting = false,
  canSubmit = false,
  orderNumber,
  confirm,
  confirmations,
  onConfirmChange,
  onSubmit = () => {
  },
  onClose = () => {
  }
}) => /* @__PURE__ */ jsxs(
  "div",
  {
    className: "grid gap-5.5 items-start",
    style: { gridTemplateColumns: "minmax(0, 1.6fr) minmax(260px, 1fr)" },
    children: [
      /* @__PURE__ */ jsx("style", { children: `@keyframes spin { to { transform: rotate(360deg); } }` }),
      /* @__PURE__ */ jsx(ReviewFormSummary, { data, order }),
      /* @__PURE__ */ jsx(
        ReviewFormSubmit,
        {
          data,
          order,
          submitted,
          submitting,
          canSubmit,
          orderNumber,
          confirm,
          confirmations,
          onConfirmChange,
          onSubmit,
          onClose
        }
      )
    ]
  }
);
const ReviewController = reactive.component(
  ({ services, monitors }) => {
    const navigate = useNavigate();
    const { enrollmentId, enrollment, setEnrollment, form } = useOutletContext();
    const [submitted, setSubmitted] = useState(false);
    const [confirmOverrides, setConfirm] = useState({});
    const confirm = {
      sms: confirmOverrides.sms ?? !!form.customer?.phones?.cell,
      email: confirmOverrides.email ?? !!form.customer?.email
    };
    const [confirmations, setConfirmations] = useState([]);
    const data = useMemo(() => {
      const rawContacts = enrollment?.contacts ?? form.contacts?.list ?? [];
      const list = rawContacts.map((c) => ({
        ...c,
        relationship: c.relationship ?? c.relationshipTypeName ?? "",
        enableLogin: c.enableLogin ?? c.authenticationId != null
      }));
      return { ...form, contacts: { ...form.contacts, list } };
    }, [form, enrollment]);
    const order = useMemo(() => {
      const summary = summarizeOrder(enrollment);
      const payment = latestPayment(enrollment);
      return {
        ...summary,
        chargedCents: payment ? toCents(payment.amount) : summary.totalCents
      };
    }, [enrollment]);
    const submitting = monitors.finalize.executing;
    function submitOrder() {
      if (!enrollment || submitting) return;
      const sendConfirmation = (confirm.sms ? ConfirmationChannels.Sms : 0) | (confirm.email ? ConfirmationChannels.Email : 0);
      services.enrollments.finalize(enrollmentId, { sendConfirmation });
    }
    useSuccessReactor(services.enrollments.finalize, (payload) => {
      setEnrollment(payload.enrollment);
      setConfirmations(payload.confirmations ?? []);
      setSubmitted(true);
    });
    function exitWizard() {
      navigate(RoutePaths.sales.overview);
    }
    return /* @__PURE__ */ jsx(
      ReviewForm,
      {
        data,
        order,
        submitted,
        submitting,
        canSubmit: canFinalize(enrollment),
        confirm,
        confirmations,
        onConfirmChange: setConfirm,
        orderNumber: enrollment?.order?.orderNumber,
        onSubmit: submitOrder,
        onClose: exitWizard
      }
    );
  },
  {
    name: "ReviewController",
    monitors: (services) => [services.enrollments.finalize]
  }
);
const NewSaleRoutes = () => /* @__PURE__ */ jsxs(Routes, { children: [
  /* @__PURE__ */ jsx(Route, { index: true, element: /* @__PURE__ */ jsx(NewSaleStartController, {}) }),
  /* @__PURE__ */ jsxs(Route, { path: ":enrollmentId/*", element: /* @__PURE__ */ jsx(NewSaleController, {}), children: [
    /* @__PURE__ */ jsx(Route, { index: true, element: null }),
    /* @__PURE__ */ jsx(Route, { path: "customer", element: /* @__PURE__ */ jsx(CustomerController, {}) }),
    /* @__PURE__ */ jsx(Route, { path: "coverage", element: /* @__PURE__ */ jsx(CoverageController, {}) }),
    /* @__PURE__ */ jsx(Route, { path: "products", element: /* @__PURE__ */ jsx(ProductsController, {}) }),
    /* @__PURE__ */ jsx(Route, { path: "contacts", element: /* @__PURE__ */ jsx(ContactsController, {}) }),
    /* @__PURE__ */ jsx(Route, { path: "medical", element: /* @__PURE__ */ jsx(MedicalController, {}) }),
    /* @__PURE__ */ jsx(Route, { path: "payment", element: /* @__PURE__ */ jsx(PaymentController, {}) }),
    /* @__PURE__ */ jsx(Route, { path: "review", element: /* @__PURE__ */ jsx(ReviewController, {}) })
  ] })
] });
const formatSpan = ([start, end]) => start.hasSame(end, "month") ? `${start.toFormat("MMM d")} – ${end.toFormat("d")}` : `${start.toFormat("MMM d")} – ${end.toFormat("MMM d")}`;
const toRange = ([start, end]) => [toISO(start), toISO(end)];
function getPeriodPresets(now = DateTime.now()) {
  const today = [now.startOf("day"), now.endOf("day")];
  const thisWeek = [now.startOf("week"), now.endOf("week")];
  const lastWeek = thisWeek.map((dt) => dt.minus({ weeks: 1 }));
  const preset = (name, description, bounds) => ({
    name,
    description,
    values: [{ filterName: "soldAtUtc", value: toRange(bounds) }]
  });
  return [
    preset("Today", today[0].toFormat("MMM d, yyyy"), today),
    preset("This week", formatSpan(thisWeek), thisWeek),
    preset("Last week", formatSpan(lastWeek), lastWeek)
  ];
}
const periodPresets = getPeriodPresets();
const defaultPreset = periodPresets.find((p) => p.name === "This week");
const leaderboardFilters = new Filters([
  new FilterDateRange({
    name: "soldAtUtc",
    title: "Sale date",
    defaultValue: defaultPreset.values[0].value,
    options: { isApplied: true }
  })
]);
const findPresetForRange = (range) => periodPresets.find((p) => deepEquals(p.values[0].value, range));
function formatPeriod(range) {
  const preset = findPresetForRange(range);
  if (preset) return `${preset.name} · ${preset.description}`;
  const [start, end] = range ?? [];
  if (!start || !end) return "All time";
  return formatSpan([
    DateTime.fromISO(start, { zone: "utc" }).toLocal(),
    DateTime.fromISO(end, { zone: "utc" }).toLocal()
  ]);
}
const POINTS = { pay_today: 2, post_date: 1 };
const initialsOf = (name) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0].toUpperCase()).join("");
function formatLastSale(utc, now = DateTime.now()) {
  if (!utc) return "—";
  const soldAt = fromISO(utc).toLocal();
  if (now.diff(soldAt).as("seconds") < 60) return "Just now";
  if (soldAt >= now.startOf("day")) return soldAt.toRelative({ base: now });
  if (soldAt >= now.startOf("day").minus({ days: 1 })) return "Yesterday";
  if (soldAt >= now.minus({ days: 7 })) return soldAt.toFormat("ccc h:mm a");
  return soldAt.toFormat("MMM d");
}
const PointKey = () => /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 rounded-full border bg-surface-secondary px-2 py-1 text-xs", children: [
  /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1", children: [
    /* @__PURE__ */ jsxs(Chip, { color: "accent", variant: "primary", children: [
      POINTS.pay_today,
      " pts"
    ] }),
    /* @__PURE__ */ jsx("span", { className: "text-muted", children: "Pay today" })
  ] }),
  /* @__PURE__ */ jsx(Separator, { orientation: "vertical" }),
  /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1", children: [
    /* @__PURE__ */ jsxs(Chip, { color: "accent", variant: "soft", children: [
      POINTS.post_date,
      " pt"
    ] }),
    /* @__PURE__ */ jsx("span", { className: "text-muted", children: "Post date" })
  ] })
] });
const LeaderboardHeader = ({ presets }) => /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4", children: [
  /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
    /* @__PURE__ */ jsx("span", { className: "flex size-9 items-center justify-center rounded-md bg-warning-soft text-warning", children: /* @__PURE__ */ jsx("i", { className: "ri-trophy-fill text-xl" }) }),
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx("span", { className: "text-2xl font-semibold", children: "Sales Showdown" }),
      /* @__PURE__ */ jsx("p", { className: "text-sm text-muted", children: "Weekly point-based challenge across all sales teams." })
    ] })
  ] }),
  /* @__PURE__ */ jsxs("div", { className: "flex justify-between items-center", children: [
    /* @__PURE__ */ jsx(FilterPresetChips, { presets, "aria-label": "Period" }),
    /* @__PURE__ */ jsx(PointKey, {})
  ] })
] });
const Medal = Object.freeze({
  First: 0,
  Second: 1,
  Third: 2
});
const MedalLabel = Object.freeze({
  [Medal.First]: "1st place",
  [Medal.Second]: "2nd place",
  [Medal.Third]: "3rd place"
});
const MedalStripe = Object.freeze({
  [Medal.First]: "border-amber-400",
  [Medal.Second]: "border-slate-400",
  [Medal.Third]: "border-orange-500"
});
const MedalChip = Object.freeze({
  [Medal.First]: "bg-amber-300 text-amber-950 border-amber-500",
  [Medal.Second]: "bg-slate-300 text-slate-900 border-slate-500",
  [Medal.Third]: "bg-orange-400 text-orange-950 border-orange-700"
});
const MedalText = Object.freeze({
  [Medal.First]: "text-amber-500",
  [Medal.Second]: "text-slate-400",
  [Medal.Third]: "text-orange-500"
});
const YouTag = () => /* @__PURE__ */ jsx("span", { className: "ml-1.5 align-middle text-[10px] font-bold uppercase tracking-widest text-accent", children: "you" });
const PodiumCard = ({ rank, agent }) => {
  return /* @__PURE__ */ jsx(
    Card,
    {
      className: `border-t-4 ${MedalStripe[rank]} ${rank === Medal.First ? "min-h-56 shadow-lg shadow-amber-400/25" : "min-h-44"}`,
      children: /* @__PURE__ */ jsxs(Card.Content, { className: "flex h-full flex-col items-center gap-2 p-4 text-center", children: [
        /* @__PURE__ */ jsxs(
          "span",
          {
            className: `inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${MedalChip[rank]}`,
            children: [
              /* @__PURE__ */ jsx("i", { className: "ri-medal-fill" }),
              MedalLabel[rank]
            ]
          }
        ),
        agent ? /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(Avatar, { size: rank === Medal.First ? "lg" : "md", color: "accent", className: "mt-1", children: /* @__PURE__ */ jsx(Avatar.Fallback, { children: initialsOf(agent.name) }) }),
          /* @__PURE__ */ jsx("div", { className: "min-w-0", children: /* @__PURE__ */ jsxs("div", { className: `font-bold ${rank === Medal.First ? "text-base" : "text-sm"}`, children: [
            agent.name,
            agent.isCurrentUser && /* @__PURE__ */ jsx(YouTag, {})
          ] }) }),
          /* @__PURE__ */ jsxs("div", { className: "mt-auto pt-1", children: [
            /* @__PURE__ */ jsx(
              "div",
              {
                className: `font-extrabold leading-none tabular-nums ${rank === Medal.First ? "text-4xl" : "text-3xl"}`,
                children: agent.points
              }
            ),
            /* @__PURE__ */ jsx(
              "div",
              {
                className: `mt-1 text-[10.5px] font-bold uppercase tracking-widest ${MedalText[rank]}`,
                children: "points"
              }
            )
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "mt-1 flex w-full items-center justify-center gap-3 border-t border-default-200 pt-2 text-xs text-muted", children: [
            /* @__PURE__ */ jsxs("span", { children: [
              /* @__PURE__ */ jsx("strong", { className: "text-foreground tabular-nums", children: agent.payTodayCount }),
              " pay-today"
            ] }),
            /* @__PURE__ */ jsx(Separator, { orientation: "vertical", className: "h-3" }),
            /* @__PURE__ */ jsxs("span", { children: [
              /* @__PURE__ */ jsx("strong", { className: "text-foreground tabular-nums", children: agent.postDateCount }),
              " post-date"
            ] })
          ] })
        ] }) : /* @__PURE__ */ jsx("div", { className: "my-auto text-xs text-muted", children: "No agent yet" })
      ] })
    }
  );
};
const LeaderboardPodium = ({ rows }) => {
  const [first, second, third] = rows;
  return /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-1 items-end gap-3 sm:grid-cols-3", children: [
    /* @__PURE__ */ jsx(PodiumCard, { rank: Medal.Second, agent: second }),
    /* @__PURE__ */ jsx(PodiumCard, { rank: Medal.First, agent: first }),
    /* @__PURE__ */ jsx(PodiumCard, { rank: Medal.Third, agent: third })
  ] });
};
const StatCard = ({ icon, label, value, accent }) => /* @__PURE__ */ jsx(Card, { children: /* @__PURE__ */ jsxs(Card.Content, { className: "flex flex-col gap-2 p-4", children: [
  /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
    /* @__PURE__ */ jsx("span", { className: "inline-flex size-6 flex-none items-center justify-center rounded-md bg-accent-soft text-accent", children: /* @__PURE__ */ jsx("i", { className: `${icon} text-sm` }) }),
    /* @__PURE__ */ jsx("span", { className: "text-[11px] font-semibold uppercase tracking-wider text-muted", children: label })
  ] }),
  /* @__PURE__ */ jsx(
    "div",
    {
      className: `text-3xl font-bold leading-none tabular-nums ${accent ? "text-accent" : ""}`,
      children: value
    }
  )
] }) });
const LeaderboardStats = ({ totals }) => /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-1 gap-3 sm:grid-cols-3", children: [
  /* @__PURE__ */ jsx(
    StatCard,
    {
      icon: "ri-award-line",
      label: "Total points",
      value: (totals?.points ?? 0).toLocaleString(),
      accent: true
    }
  ),
  /* @__PURE__ */ jsx(
    StatCard,
    {
      icon: "ri-line-chart-line",
      label: "Scored sales",
      value: (totals?.scoredSales ?? 0).toLocaleString()
    }
  ),
  /* @__PURE__ */ jsx(StatCard, { icon: "ri-team-line", label: "Competing agents", value: totals?.agentsWithSales ?? 0 })
] });
const cols = [
  { id: "rank", label: "Rank", sortable: false },
  { id: "agent", label: "Agent", sortable: false, isRowHeader: true },
  { id: "points", label: "Points", align: "right" },
  { id: "payTodayCount", label: "Pay Today", align: "right" },
  { id: "postDateCount", label: "Post Date", align: "right" },
  { id: "scoredSales", label: "Total sales", align: "right" },
  { id: "lastSaleUtc", label: "Last sale" }
];
const compare = (a, b, column) => {
  if (column === "lastSaleUtc") return (a.lastSaleUtc ?? "").localeCompare(b.lastSaleUtc ?? "");
  return a[column] - b[column];
};
const LeaderboardTable = ({ rows, periodLabel }) => {
  const [sort, setSort] = useState({ column: "points", direction: "descending" });
  const sorted = useMemo(() => {
    const sign = sort.direction === "descending" ? -1 : 1;
    return [...rows].sort((a, b) => sign * compare(a, b, sort.column) || a.rank - b.rank);
  }, [rows, sort]);
  const onSortChange = (next) => setSort(next.column === sort.column ? next : { column: next.column, direction: "descending" });
  return /* @__PURE__ */ jsx(Card, { children: /* @__PURE__ */ jsxs(Card.Content, { className: "flex flex-col gap-3 p-4", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-baseline justify-between gap-3", children: [
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("div", { className: "text-sm font-semibold", children: "Leaderboard" }),
        /* @__PURE__ */ jsx("div", { className: "mt-0.5 text-xs text-muted", children: periodLabel })
      ] }),
      /* @__PURE__ */ jsx("div", { className: "text-xs text-muted", children: "Click any column header to sort." })
    ] }),
    /* @__PURE__ */ jsx(Table, { variant: "secondary", children: /* @__PURE__ */ jsx(Table.ScrollContainer, { children: /* @__PURE__ */ jsxs(
      Table.Content,
      {
        "aria-label": "Sales leaderboard",
        sortDescriptor: sort,
        onSortChange,
        children: [
          /* @__PURE__ */ jsx(Table.Header, { children: cols.map((column) => /* @__PURE__ */ jsx(
            Table.Column,
            {
              id: column.id,
              isRowHeader: column.isRowHeader,
              allowsSorting: column.sortable !== false,
              className: column.align === "right" ? "text-right" : void 0,
              children: ({ sortDirection }) => column.sortable === false ? column.label : /* @__PURE__ */ jsx(
                Table.SortableColumnHeader,
                {
                  sortDirection,
                  className: column.align === "right" ? "justify-end" : void 0,
                  children: column.label
                }
              )
            },
            column.id
          )) }),
          /* @__PURE__ */ jsx(
            Table.Body,
            {
              renderEmptyState: () => /* @__PURE__ */ jsx("div", { className: "py-8 text-center text-sm text-muted", children: "No scored sales in this period." }),
              children: sorted.map((row) => /* @__PURE__ */ jsxs(
                Table.Row,
                {
                  id: row.salesAgentId,
                  className: row.isCurrentUser ? "bg-accent-soft" : void 0,
                  children: [
                    /* @__PURE__ */ jsx(Table.Cell, { children: /* @__PURE__ */ jsx(RankBadge, { rank: row.rank - 1 }) }),
                    /* @__PURE__ */ jsx(Table.Cell, { children: /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 items-center gap-2.5", children: [
                      /* @__PURE__ */ jsx(Avatar, { size: "sm", color: "accent", children: /* @__PURE__ */ jsx(Avatar.Fallback, { children: initialsOf(row.name) }) }),
                      /* @__PURE__ */ jsx("div", { className: "min-w-0", children: /* @__PURE__ */ jsxs(
                        "div",
                        {
                          className: `truncate text-sm ${row.isCurrentUser ? "font-bold" : "font-medium"}`,
                          children: [
                            row.name,
                            row.isCurrentUser && /* @__PURE__ */ jsx(YouTag, {})
                          ]
                        }
                      ) })
                    ] }) }),
                    /* @__PURE__ */ jsx(
                      Table.Cell,
                      {
                        className: `text-right text-base font-bold tabular-nums ${row.rank === 1 ? "text-amber-500" : row.isCurrentUser ? "text-accent" : ""}`,
                        children: row.points
                      }
                    ),
                    /* @__PURE__ */ jsx(Table.Cell, { className: "text-right tabular-nums", children: row.payTodayCount }),
                    /* @__PURE__ */ jsx(Table.Cell, { className: "text-right tabular-nums", children: row.postDateCount }),
                    /* @__PURE__ */ jsx(Table.Cell, { className: "text-right tabular-nums text-muted", children: row.scoredSales }),
                    /* @__PURE__ */ jsx(Table.Cell, { className: "text-xs text-muted", children: formatLastSale(row.lastSaleUtc) })
                  ]
                },
                row.salesAgentId
              ))
            }
          )
        ]
      }
    ) }) })
  ] }) });
};
const currentRange = () => leaderboardFilters.get("soldAtUtc")?.value ?? [];
const LeaderboardController = reactive.component(
  ({ services, data }) => {
    const [leaderboard] = data.leaderboard;
    const [range, setRange] = useState(currentRange);
    const load = useCallback(() => {
      const next = currentRange();
      const [fromUtc, toUtc] = next;
      setRange(next);
      services.leaderboard.getForPeriod({ fromUtc, toUtc });
    }, []);
    useInterval(() => {
      load();
    }, 3e4);
    const rows = leaderboard?.agents ?? [];
    return /* @__PURE__ */ jsx(FiltersProvider, { filters: leaderboardFilters, onApply: load, children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4", children: [
      /* @__PURE__ */ jsx(LeaderboardHeader, { presets: periodPresets }),
      /* @__PURE__ */ jsx(LeaderboardPodium, { rows }),
      /* @__PURE__ */ jsx(LeaderboardStats, { totals: leaderboard?.totals }),
      /* @__PURE__ */ jsx(LeaderboardTable, { rows, periodLabel: formatPeriod(range) })
    ] }) });
  },
  {
    name: "LeaderboardController",
    queries: () => [
      {
        name: "leaderboard",
        // A list, not `.get()`: reactive swaps a missing result for `[]`, which is truthy.
        execute: (db) => db.leaderboard.where("id").equals(LEADERBOARD_ROW_ID).toArray(),
        defaultValue: []
      }
    ]
  }
);
const RankBadge = ({ rank }) => {
  if (rank > 2) return /* @__PURE__ */ jsxs("span", { className: "text-sm font-semibold tabular-nums text-muted", children: [
    "#",
    rank + 1
  ] });
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: `inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold tabular-nums ${MedalChip[rank]}`,
      children: [
        /* @__PURE__ */ jsx("i", { className: "ri-trophy-fill" }),
        "#",
        rank + 1
      ]
    }
  );
};
const HeaderSelect = ({ label, items, value, onChange }) => /* @__PURE__ */ jsxs(Select, { variant: "secondary", "aria-label": label, value, onChange, children: [
  /* @__PURE__ */ jsxs(Select.Trigger, { children: [
    /* @__PURE__ */ jsx(Select.Value, {}),
    /* @__PURE__ */ jsx(Select.Indicator, {})
  ] }),
  /* @__PURE__ */ jsx(Select.Popover, { children: /* @__PURE__ */ jsx(ListBox, { items, children: (item) => /* @__PURE__ */ jsxs(ListBox.Item, { id: item.id, textValue: item.label, children: [
    item.label,
    /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
  ] }) }) })
] });
const DashboardHeader = ({
  userName,
  periods,
  period,
  onPeriodChange,
  role,
  onRoleChange,
  onNewSale
}) => /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3", children: [
  /* @__PURE__ */ jsx("span", { className: "flex size-9 items-center justify-center rounded-md bg-accent-soft text-accent", children: /* @__PURE__ */ jsx("i", { className: "ri-line-chart-fill text-xl" }) }),
  /* @__PURE__ */ jsxs("div", { className: "flex-1", children: [
    /* @__PURE__ */ jsx("h1", { className: "text-2xl font-semibold", children: "Sales dashboard" }),
    /* @__PURE__ */ jsxs("p", { className: "text-sm text-muted", children: [
      userName && /* @__PURE__ */ jsxs("strong", { children: [
        userName,
        " · "
      ] }),
      "Sales agent"
    ] })
  ] }),
  /* @__PURE__ */ jsxs(Card, { className: "flex-row items-center gap-2", children: [
    /* @__PURE__ */ jsx(RequirePolicy, { resource: "enrollments", operation: "create", children: /* @__PURE__ */ jsxs(Button, { size: "sm", variant: "secondary", className: "text-success", onPress: onNewSale, children: [
      /* @__PURE__ */ jsx("i", { className: "ri-add-line" }),
      " New sale"
    ] }) }),
    /* @__PURE__ */ jsx(HeaderSelect, { label: "Period", items: periods, value: period, onChange: onPeriodChange })
  ] })
] });
function getDashboardPeriods(now = DateTime.now()) {
  const tomorrow = now.startOf("day").plus({ days: 1 });
  const period = (id, label, start) => ({
    id,
    label,
    query: { fromUtc: toISO(start), toUtc: toISO(tomorrow) }
  });
  return [
    period("today", "Today", now.startOf("day")),
    period("week", "This week", now.startOf("week")),
    period("month", now.toFormat("MMMM yyyy"), now.startOf("month")),
    period("quarter", `Q${now.quarter} ${now.year}`, now.startOf("quarter")),
    period("year", "Year-to-date", now.startOf("year"))
  ];
}
const dashboardPeriods = getDashboardPeriods();
const DEFAULT_PERIOD = "today";
const SALE_BUCKETS = [
  {
    id: "complete",
    label: "Complete",
    icon: "ri-checkbox-circle-line",
    tone: "success",
    statuses: [EnrollmentStatusType.Complete]
  },
  {
    id: "scheduled",
    label: "Scheduled",
    icon: "ri-calendar-2-line",
    tone: "accent",
    statuses: [EnrollmentStatusType.PaymentScheduled]
  },
  {
    id: "submitted",
    label: "Payment submitted",
    icon: "ri-time-line",
    tone: "default",
    statuses: [EnrollmentStatusType.PaymentSubmitted]
  },
  {
    id: "action",
    label: "Needs action",
    icon: "ri-error-warning-line",
    tone: "warning",
    // Received is a cleared payment waiting on the agent to review and complete the sale.
    statuses: [
      EnrollmentStatusType.Open,
      EnrollmentStatusType.PaymentFailed,
      EnrollmentStatusType.PaymentReceived
    ]
  }
];
const bucketSales = (enrollments) => Object.fromEntries(
  SALE_BUCKETS.map((bucket) => [
    bucket.id,
    enrollments.filter((sale) => bucket.statuses.includes(sale.statusType))
  ])
);
const formatMoney = (amount) => amount >= 1e3 ? `$${(amount / 1e3).toFixed(amount >= 1e4 ? 0 : 1)}k` : `$${Math.round(amount).toLocaleString()}`;
const plural = (count, noun) => `${count} ${noun}${count === 1 ? "" : "s"}`;
const KpiCard = ({ label, value, sub, accent }) => /* @__PURE__ */ jsx(Card, { className: "h-full", children: /* @__PURE__ */ jsxs(Card.Content, { className: "flex h-full flex-col gap-1.5 p-4", children: [
  /* @__PURE__ */ jsx("span", { className: "text-[11px] font-semibold uppercase tracking-wider text-muted", children: label }),
  /* @__PURE__ */ jsx("span", { className: `text-2xl font-semibold leading-tight tabular-nums ${accent ?? ""}`, children: value }),
  /* @__PURE__ */ jsx("span", { className: "mt-auto text-xs text-muted", children: sub })
] }) });
const DashboardKpis = ({ dashboard }) => {
  const sales = dashboard?.totalSales ?? 0;
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      KpiCard,
      {
        label: "MRR",
        value: dashboard ? `${formatMoney(dashboard.mrr)}/mo` : "—",
        sub: plural(sales, "sale"),
        accent: "text-accent"
      }
    ),
    /* @__PURE__ */ jsx(
      KpiCard,
      {
        label: "Sales",
        value: dashboard ? sales : "—",
        sub: dashboard && `${plural(dashboard.enrollments, "enrollment")} · ${dashboard.closedPercent}% closed`
      }
    )
  ] });
};
const stopRowPress = (event) => event.stopPropagation();
const columns = [
  // {
  //   id: 'id',
  //   header: 'Sale',
  //   accessorKey: 'id',
  //   width: 110,
  //   cellClassName: 'font-mono text-xs text-muted',
  // },
  {
    id: "customer",
    header: "Customer",
    accessorKey: "customerName",
    isRowHeader: true,
    allowsSorting: true
  },
  {
    id: "customerId",
    header: "Customer #",
    accessorKey: "customerId",
    allowsSorting: true,
    cellClassName: "tabular-nums",
    cell: (sale) => /* @__PURE__ */ jsx("span", { onPointerDown: stopRowPress, onClick: stopRowPress, children: /* @__PURE__ */ jsx(EnrollmentCustomerId, { enrollment: sale }) })
  },
  {
    id: "orderId",
    header: "Order #",
    accessorKey: "orderId",
    allowsSorting: true,
    cellClassName: "tabular-nums",
    cell: (sale) => sale.orderId ?? null
  },
  {
    id: "status",
    header: "Status",
    allowsSorting: true,
    cell: (sale) => {
      return /* @__PURE__ */ jsx(Chip, { color: EnrollmentStatusColor[sale.statusType], children: sale.status });
    }
  },
  {
    id: "time",
    header: "Time",
    align: "end",
    cellClassName: "text-xs text-muted tabular-nums",
    cell: (sale) => /* @__PURE__ */ jsx("span", { children: parseAndFormatDateTime(sale.startedOnUtc) })
  }
];
const DashboardSalesTable = ({ rows, bucket, onClearFilter, onRowSelect }) => {
  const [completedSale, setCompletedSale] = useState(null);
  function selectRow(key) {
    const sale = rows.find((row) => row.id === key);
    if (isEnrollmentComplete(sale)) setCompletedSale(sale);
    else onRowSelect(key);
  }
  return /* @__PURE__ */ jsxs(Card, { children: [
    /* @__PURE__ */ jsxs(Card.Header, { className: "flex flex-row items-start justify-between gap-3", children: [
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx(Card.Title, { children: bucket?.label ?? "All sales" }),
        /* @__PURE__ */ jsx(Card.Description, { children: bucket ? `${rows.length} sale${rows.length === 1 ? "" : "s"} · click the bucket again to clear` : `${rows.length} total sales` })
      ] }),
      bucket && /* @__PURE__ */ jsxs(Button, { size: "sm", variant: "tertiary", onPress: onClearFilter, children: [
        /* @__PURE__ */ jsx("i", { className: "ri-close-line" }),
        " Clear filter"
      ] })
    ] }),
    /* @__PURE__ */ jsx(Card.Content, { children: /* @__PURE__ */ jsx(
      DataGrid,
      {
        selectionMode: "single",
        "aria-label": "All sales",
        variant: "secondary",
        data: rows,
        columns,
        getRowId: (sale) => sale.id,
        renderEmptyState: () => bucket ? "No sales in this bucket" : "No sales found",
        onRowAction: selectRow
      }
    ) }),
    /* @__PURE__ */ jsx(CompletedSaleModal, { enrollment: completedSale, onClose: () => setCompletedSale(null) })
  ] });
};
const TONE = {
  success: "bg-success-soft text-success",
  accent: "bg-accent-soft text-accent",
  default: "bg-default text-muted",
  warning: "bg-warning-soft text-warning"
};
const ACTIVE = {
  success: "ring-2 ring-success bg-success-soft",
  accent: "ring-2 ring-accent bg-accent-soft",
  default: "ring-2 ring-border bg-default",
  warning: "ring-2 ring-warning bg-warning-soft"
};
const orderTotal = (sales) => sales.reduce((sum, sale) => sum + (sale.orderTotal ?? 0), 0);
const SaleBuckets = ({ buckets, activeId, onToggle }) => /* @__PURE__ */ jsx(Fragment, { children: SALE_BUCKETS.map((bucket) => {
  const sales = buckets[bucket.id];
  const isActive = bucket.id === activeId;
  const alert = bucket.tone === "warning" && sales.length > 0;
  return /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      "aria-pressed": isActive,
      onClick: () => onToggle(bucket.id),
      className: "h-full cursor-pointer rounded-3xl text-left focus-visible:outline-2 focus-visible:outline-focus",
      children: /* @__PURE__ */ jsx(
        Card,
        {
          className: `h-full transition-colors hover:bg-surface-secondary ${isActive ? ACTIVE[bucket.tone] : ""}`,
          children: /* @__PURE__ */ jsxs(Card.Content, { className: "flex flex-col gap-2.5 p-4", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
              /* @__PURE__ */ jsx(
                "span",
                {
                  className: `inline-flex size-6 items-center justify-center rounded-md ${TONE[bucket.tone]}`,
                  children: /* @__PURE__ */ jsx("i", { className: `${bucket.icon} text-sm` })
                }
              ),
              /* @__PURE__ */ jsx("span", { className: "text-xs font-semibold text-muted", children: bucket.label })
            ] }),
            /* @__PURE__ */ jsx(
              "span",
              {
                className: `text-3xl font-semibold leading-none tabular-nums ${alert ? "text-warning" : ""}`,
                children: sales.length
              }
            ),
            /* @__PURE__ */ jsx("span", { className: "text-xs text-muted tabular-nums", children: sales.length > 0 ? `${formatCurrencyUSD(orderTotal(sales))} in orders` : "—" })
          ] })
        }
      )
    },
    bucket.id
  );
}) });
const DashboardController = reactive.component(
  ({ services, data }) => {
    const navigate = useNavigate();
    const [periodId, setPeriodId] = useState(DEFAULT_PERIOD);
    const [role, setRole] = useState("agent");
    const [bucketId, setBucketId] = useState(null);
    const { enrollments } = data;
    const [dashboard] = data.dashboard;
    const user = appRuntime?.user;
    const userName = user?.displayName || [user?.firstName, user?.lastName].filter(Boolean).join(" ") || void 0;
    const period = dashboardPeriods.find((p) => p.id === periodId) ?? dashboardPeriods[0];
    useEffect(() => {
      services.dashboard.getForPeriod(period.query);
    }, [period]);
    const sales = useMemo(
      () => enrollments.filter((sale) => sale.statusType !== EnrollmentStatusType.Voided),
      [enrollments]
    );
    const buckets = useMemo(() => bucketSales(sales), [sales]);
    const activeBucket = SALE_BUCKETS.find((bucket) => bucket.id === bucketId) ?? null;
    const toggleBucket = (id) => setBucketId((current) => current === id ? null : id);
    return /* @__PURE__ */ jsxs("div", { className: "flex h-full flex-col gap-4 [&>*]:shrink-0", children: [
      /* @__PURE__ */ jsx(
        DashboardHeader,
        {
          userName,
          periods: dashboardPeriods,
          period: periodId,
          onPeriodChange: setPeriodId,
          role,
          onRoleChange: setRole,
          onNewSale: () => navigate(RoutePaths.sales.create.start)
        }
      ),
      /* @__PURE__ */ jsxs("div", { className: "grid auto-rows-fr grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6", children: [
        /* @__PURE__ */ jsx(DashboardKpis, { dashboard }),
        /* @__PURE__ */ jsx(SaleBuckets, { buckets, activeId: bucketId, onToggle: toggleBucket })
      ] }),
      /* @__PURE__ */ jsx(
        DashboardSalesTable,
        {
          rows: activeBucket ? buckets[activeBucket.id] : sales,
          bucket: activeBucket,
          onClearFilter: () => setBucketId(null),
          onRowSelect: (key) => navigate(RoutePaths.sales.create.open(key))
        }
      )
    ] });
  },
  {
    name: "SalesDashboardController",
    sync: (services) => [
      {
        execute: () => services.enrollments.getBySalesAgent(),
        deps: []
      }
    ],
    queries: () => [
      {
        name: "enrollments",
        execute: (db) => db.enrollments.toArray(),
        defaultValue: []
      },
      {
        name: "dashboard",
        // A list, not `.get()`: reactive swaps a missing result for `[]`, which is truthy.
        execute: (db) => db.dashboard.where("id").equals(DASHBOARD_ROW_ID).toArray(),
        defaultValue: []
      }
    ]
  }
);
const ReferralSourceForm = ({ source, onChange }) => /* @__PURE__ */ jsxs(Uniformly, { name: "referralSource", className: "flex flex-col gap-3", onChange, children: [
  /* @__PURE__ */ jsx(
    TextField,
    {
      isRequired: true,
      name: "code",
      label: "Code",
      value: source?.code,
      validation: (x) => x.string().max(20).required("Code is required")
    }
  ),
  /* @__PURE__ */ jsx(
    TextField,
    {
      isRequired: true,
      name: "name",
      label: "Name",
      value: source?.name,
      validation: (x) => x.string().max(100).required("Name is required")
    }
  ),
  /* @__PURE__ */ jsx(SwitchField, { name: "isActive", value: source?.isActive ?? true, children: "Active" }),
  /* @__PURE__ */ jsx(SwitchField, { name: "isPrivate", value: source?.isPrivate ?? false, children: "Private" })
] });
const ReferralSourceModal = ({ source, isSaving, onSave, onDelete, onClose }) => {
  const [form, setForm] = useState({});
  const [armed, setArmed] = useState(false);
  const isEdit = !!source?.id;
  return /* @__PURE__ */ jsx(Modal.Backdrop, { isOpen: !!source, onOpenChange: (open) => !open && onClose(), children: /* @__PURE__ */ jsx(Modal.Container, { size: "sm", placement: "center", children: /* @__PURE__ */ jsxs(Modal.Dialog, { children: [
    /* @__PURE__ */ jsx(Modal.Header, { children: /* @__PURE__ */ jsx(Modal.Heading, { children: isEdit ? "Edit referral source" : "New referral source" }) }),
    /* @__PURE__ */ jsx(Modal.Body, { children: /* @__PURE__ */ jsx(ReferralSourceForm, { source, onChange: setForm }) }),
    /* @__PURE__ */ jsxs(Modal.Footer, { className: "flex justify-between", children: [
      isEdit ? /* @__PURE__ */ jsx(RequirePolicy, { resource: "ReferralSources", operation: "delete", children: /* @__PURE__ */ jsx(
        Button,
        {
          size: "sm",
          variant: armed ? "danger" : "tertiary",
          isDisabled: isSaving,
          onPress: () => armed ? onDelete(source.id) : setArmed(true),
          children: armed ? "Confirm delete" : "Delete"
        }
      ) }) : /* @__PURE__ */ jsx("span", {}),
      /* @__PURE__ */ jsxs("div", { className: "flex gap-2", children: [
        /* @__PURE__ */ jsx(Button, { size: "sm", variant: "tertiary", onPress: onClose, children: "Cancel" }),
        /* @__PURE__ */ jsx(
          Button,
          {
            size: "sm",
            variant: "primary",
            isDisabled: !form.isValid || isSaving,
            onPress: () => onSave({ ...source, ...form.data }),
            children: "Save"
          }
        )
      ] })
    ] })
  ] }) }) });
};
const TH = "py-2 px-1 text-left text-[10.5px] font-semibold text-default-400 uppercase tracking-wider";
const ReferralSourcesTable = ({ rows, onRowClick }) => {
  if (rows.length === 0) {
    return /* @__PURE__ */ jsx("div", { className: "py-10 text-center text-default-400 text-sm", children: "No referral sources." });
  }
  return /* @__PURE__ */ jsxs("table", { className: "w-full border-collapse", children: [
    /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsxs("tr", { className: "border-b border-default-200", children: [
      /* @__PURE__ */ jsx("th", { className: TH, children: "Code" }),
      /* @__PURE__ */ jsx("th", { className: TH, children: "Name" }),
      /* @__PURE__ */ jsx("th", { className: TH, children: "Status" }),
      /* @__PURE__ */ jsx("th", { className: TH, children: "Modified" })
    ] }) }),
    /* @__PURE__ */ jsx("tbody", { children: rows.map((row) => /* @__PURE__ */ jsxs(
      "tr",
      {
        className: "border-b border-default-100 cursor-pointer hover:bg-default-50",
        onClick: () => onRowClick(row),
        children: [
          /* @__PURE__ */ jsx("td", { className: "py-3 px-1 font-mono text-xs", children: row.code }),
          /* @__PURE__ */ jsx("td", { className: "py-3 px-1 text-sm", children: row.name }),
          /* @__PURE__ */ jsxs("td", { className: "py-3 px-1", children: [
            /* @__PURE__ */ jsx(Chip, { size: "sm", color: row.isActive ? "success" : "default", children: /* @__PURE__ */ jsx(Chip.Label, { children: row.isActive ? "Active" : "Inactive" }) }),
            row.isPrivate && /* @__PURE__ */ jsx(Chip, { size: "sm", className: "ml-1", children: /* @__PURE__ */ jsx(Chip.Label, { children: "Private" }) })
          ] }),
          /* @__PURE__ */ jsxs("td", { className: "py-3 px-1 text-xs text-default-400", children: [
            row.modifiedOnUtc ? new Date(row.modifiedOnUtc).toLocaleDateString() : "",
            row.modifiedBy ? ` · ${row.modifiedBy}` : ""
          ] })
        ]
      },
      row.id
    )) })
  ] });
};
const NEW_SOURCE = { code: "", name: "", isActive: true, isPrivate: false };
const ReferralSourcesController = reactive.component(
  ({ services, monitors }) => {
    const [rows, setRows] = useState([]);
    const [editing, setEditing] = useState(null);
    const [search, setSearch] = useState("");
    const upsert = (saved) => {
      setRows((prev) => [...prev.filter((r) => r.id !== saved.id), saved]);
      setEditing(null);
    };
    useSuccessReactor(services.referralSources.getAll, (list) => setRows(list ?? []));
    useSuccessReactor(services.referralSources.createSource, upsert);
    useSuccessReactor(services.referralSources.updateSource, upsert);
    useSuccessReactor(services.referralSources.deleteSource, (id) => {
      setRows((prev) => prev.filter((r) => r.id !== id));
      setEditing(null);
    });
    const visible = useMemo(() => {
      const q = search.trim().toLowerCase();
      return rows.filter((r) => !q || r.code?.toLowerCase().includes(q) || r.name?.toLowerCase().includes(q)).sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    }, [rows, search]);
    const save = (model) => model.id ? services.referralSources.updateSource(model.id, model) : services.referralSources.createSource(model);
    const isSaving = monitors.createSource.executing || monitors.updateSource.executing || monitors.deleteSource.executing;
    return /* @__PURE__ */ jsx(Card, { className: "flex-1", children: /* @__PURE__ */ jsxs(Card.Content, { className: "p-4 flex flex-col gap-3", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-4", children: [
        /* @__PURE__ */ jsx(
          "input",
          {
            type: "search",
            placeholder: "Search code or name",
            "aria-label": "Search referral sources",
            className: "border border-default-200 rounded-md px-2 py-1 text-sm w-64",
            value: search,
            onChange: (e) => setSearch(e.target.value)
          }
        ),
        /* @__PURE__ */ jsx(RequirePolicy, { resource: "ReferralSources", operation: "create", children: /* @__PURE__ */ jsx(NewButton, { size: "sm", label: "New source", onPress: () => setEditing(NEW_SOURCE) }) })
      ] }),
      monitors.getAll.executing && rows.length === 0 ? /* @__PURE__ */ jsx("div", { className: "py-10 text-center text-default-400 text-sm", children: "Loading…" }) : /* @__PURE__ */ jsx(ReferralSourcesTable, { rows: visible, onRowClick: setEditing }),
      editing && /* @__PURE__ */ jsx(
        ReferralSourceModal,
        {
          source: editing,
          isSaving,
          onSave: save,
          onDelete: (id) => services.referralSources.deleteSource(id),
          onClose: () => setEditing(null)
        },
        editing.id ?? "new"
      )
    ] }) });
  },
  {
    name: "ReferralSourcesController",
    sync: (services) => [{ execute: () => services.referralSources.getAll(), deps: [] }],
    monitors: (services) => [
      services.referralSources.getAll,
      services.referralSources.createSource,
      services.referralSources.updateSource,
      services.referralSources.deleteSource
    ]
  }
);
const ReferralSourcesRoutes = () => /* @__PURE__ */ jsx(Routes, { children: /* @__PURE__ */ jsx(
  Route,
  {
    path: "/*",
    element: /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4 w-full h-full", children: [
      /* @__PURE__ */ jsxs(Breadcrumbs, { children: [
        /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Sales" }),
        /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "Referral sources" })
      ] }),
      /* @__PURE__ */ jsx(ReferralSourcesController, {})
    ] })
  }
) });
const Router = () => /* @__PURE__ */ jsx(AppLayout, { children: /* @__PURE__ */ jsxs(Routes, { children: [
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "/create/*",
      element: /* @__PURE__ */ jsx(
        RequirePolicy,
        {
          resource: "enrollments",
          operation: ["view", "create", "update"],
          fallback: /* @__PURE__ */ jsx(Forbidden, {}),
          children: /* @__PURE__ */ jsx(NewSaleRoutes, {})
        }
      )
    }
  ),
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "/referral-sources/*",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "ReferralSources", operation: "create", fallback: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(ReferralSourcesRoutes, {}) })
    }
  ),
  /* @__PURE__ */ jsx(Route, { path: "/leaderboard", element: /* @__PURE__ */ jsx(LeaderboardController, {}) }),
  /* @__PURE__ */ jsx(
    Route,
    {
      index: true,
      path: "/*",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "enrollments", operation: "view", fallback: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(DashboardController, {}) })
    }
  )
] }) });
function getSalesNavigation(runtime) {
  const user = runtime.user;
  const apiKey = runtime.config?.api?.apiKey ?? "";
  return {
    isVisible: user.hasApiAccess(apiKey),
    items: [
      {
        title: "Dashboard",
        route: "/",
        icon: "ri-file-list-3-line",
        isVisible: user.hasPolicy(apiKey, "enrollments", "view")
      },
      {
        title: "Leaderboard",
        route: "/leaderboard",
        icon: "ri-trophy-line",
        isVisible: user.hasPolicy(apiKey, "enrollments", "view")
      },
      {
        title: "Lead sources",
        route: "/referral-sources",
        icon: "ri-share-forward-line",
        isVisible: user.hasPolicy(apiKey, "ReferralSources", "create")
      }
    ]
  };
}
function getSalesResources() {
  const items = [];
  return {
    items,
    handlers: ((navigate) => ({
      // The /create landing route opens the enrollment session (NewSaleStartController)
      // and replaces the URL with /create/{enrollmentId}/customer.
      sale: () => navigate(RoutePaths.sales.create.start)
    }))
  };
}
const Embed = createEmbed({ runtime: () => appRuntime, render });
function QtyStepper({ value, onChange, min = 1, max = 99 }) {
  const button = (disabled, onClick, icon) => /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      onClick,
      disabled,
      className: `inline-flex h-7 w-7 items-center justify-center border-none bg-transparent text-muted transition-opacity ${disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer hover:bg-surface-secondary"}`,
      children: /* @__PURE__ */ jsx("i", { className: icon })
    }
  );
  return /* @__PURE__ */ jsxs("div", { className: "inline-flex h-8 flex-none items-center overflow-hidden rounded-lg border border-border bg-background", children: [
    button(value <= min, () => onChange(Math.max(min, value - 1)), "ri-subtract-line"),
    /* @__PURE__ */ jsx("span", { className: "w-7 text-center text-sm font-semibold tabular-nums text-foreground", children: value }),
    button(value >= max, () => onChange(Math.min(max, value + 1)), "ri-add-line")
  ] });
}
const lineTotal = (line) => (line.unitPrice ?? 0) * (line.quantity ?? 0);
const lineKey = (line) => `${line.productId}:${(line.options ?? []).map((o) => `${o.typeName}=${o.valueName}`).join("|")}`;
const LineRow = ({ line, onChange, onRemove }) => /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3 rounded-xl border border-border bg-surface px-3.5 py-2.5", children: [
  /* @__PURE__ */ jsx("span", { className: "w-16 flex-none font-mono text-xs text-muted", children: line.sku }),
  /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
    /* @__PURE__ */ jsx("p", { className: "truncate text-sm font-semibold text-foreground", children: line.name }),
    line.options?.length > 0 && /* @__PURE__ */ jsx("p", { className: "truncate text-xs text-muted", children: line.options.map((option) => option.valueName).join(" · ") }),
    /* @__PURE__ */ jsxs("p", { className: "mt-0.5 text-xs tabular-nums text-muted", children: [
      "Line total: ",
      /* @__PURE__ */ jsx("span", { className: "font-semibold", children: formatCurrencyUSD(lineTotal(line)) })
    ] })
  ] }),
  /* @__PURE__ */ jsx(QtyStepper, { value: line.quantity, onChange: (quantity) => onChange({ quantity }) }),
  /* @__PURE__ */ jsx(
    PriceInput,
    {
      value: toCents(line.unitPrice),
      defaultPrice: toCents(line.defaultPrice),
      onChange: (cents) => onChange({ unitPrice: fromCents(cents) }),
      width: 96
    }
  ),
  /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      onClick: onRemove,
      className: "inline-flex h-8 w-8 flex-none cursor-pointer items-center justify-center rounded-lg border border-border bg-transparent text-muted transition-colors hover:border-danger hover:bg-danger-soft hover:text-danger",
      title: "Remove",
      "aria-label": `Remove ${line.name}`,
      children: /* @__PURE__ */ jsx("i", { className: "ri-close-line" })
    }
  )
] });
const OrderLinesEditor = ({ lines, onChange, onRemove }) => {
  const subtotal = lines.reduce((sum, line) => sum + lineTotal(line), 0);
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
    lines.length === 0 ? /* @__PURE__ */ jsx("p", { className: "rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-muted", children: "No products yet. Search above to add one." }) : lines.map((line) => /* @__PURE__ */ jsx(
      LineRow,
      {
        line,
        onChange: (patch) => onChange(lineKey(line), patch),
        onRemove: () => onRemove(lineKey(line))
      },
      lineKey(line)
    )),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between px-1 pt-1 text-sm", children: [
      /* @__PURE__ */ jsx("span", { className: "text-muted", children: "Subtotal" }),
      /* @__PURE__ */ jsx("span", { className: "font-semibold tabular-nums text-foreground", children: formatCurrencyUSD(subtotal) })
    ] })
  ] });
};
const ORDER_TYPE_BUTTONS = [
  { key: OrderType.Customer, label: "Customer" },
  { key: OrderType.Subscription, label: "Subscription" }
];
const toLine = (product, options = []) => ({
  productId: product.id,
  sku: product.sku,
  name: product.displayName || product.name || product.sku,
  options,
  unitPrice: product.suggestedPrice ?? 0,
  defaultPrice: product.suggestedPrice ?? 0,
  quantity: 1
});
const OrderCreate$1 = reactive.component(
  ({ services, monitors, accountId, onSuccess }) => {
    const [form, setForm] = useState({ isValid: true, data: {} });
    const [lines, setLines] = useState([]);
    const [withPayment, setWithPayment] = useState(false);
    const [pending, setPending] = useState(null);
    const [optionState, setOptionState] = useState(null);
    const [searchKey, setSearchKey] = useState(0);
    const ProductSearchComboBox = useComponent("products.product-search-combo-box");
    const ProductOptionsPicker = useComponent("products.product-options-picker");
    const PaymentProfileField = useComponent("accounts.payment-profiles-field");
    useSuccessReactor(services.orderPlacement.place, (payload, args) => {
      if (Number(args[0]?.order?.accountId) === Number(accountId)) onSuccess?.(payload);
    });
    const addLine = (product, options = []) => {
      const line = toLine(product, options);
      if (lines.some((existing) => lineKey(existing) === lineKey(line))) return;
      setLines((current) => [...current, line]);
      setPending(null);
      setOptionState(null);
      setSearchKey((key) => key + 1);
    };
    const changeLine = (key, patch) => setLines(lines.map((line) => lineKey(line) === key ? { ...line, ...patch } : line));
    const removeLine = (key) => setLines(lines.filter((line) => lineKey(line) !== key));
    const handleOptions = (state) => {
      setOptionState(state);
      if (pending && !state.isLoading && state.options.length === 0) addLine(pending);
    };
    const canAddPending = Boolean(
      pending && optionState?.isComplete && optionState.options.length > 0
    );
    const subtotal = lines.reduce((sum, line) => sum + lineTotal(line), 0);
    const orderType = Number(form.data.type ?? OrderType.Customer);
    const paymentProfileId = form.data.paymentProfileId;
    const canSubmit = form.isValid && lines.length > 0 && lines.every((line) => line.quantity > 0 && line.unitPrice >= 0) && (!withPayment || paymentProfileId != null && paymentProfileId !== "");
    const place = () => services.orderPlacement.place({
      order: {
        accountId: Number(accountId),
        type: orderType,
        notes: form.data.notes || null
      },
      items: lines.map((line) => ({
        productId: line.productId,
        options: line.options ?? [],
        quantity: line.quantity,
        unitPrice: line.unitPrice
      })),
      payment: withPayment ? { paymentProfileId: Number(paymentProfileId) } : null
    });
    return /* @__PURE__ */ jsxs(Uniformly, { className: "flex flex-col gap-4", onChange: setForm, children: [
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1.5", children: [
        /* @__PURE__ */ jsx(Label, { children: "Order type" }),
        /* @__PURE__ */ jsx(
          ToggleButtonsField,
          {
            name: "type",
            value: OrderType.Customer,
            initialValue: OrderType.Customer,
            buttons: ORDER_TYPE_BUTTONS
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1.5", children: [
        /* @__PURE__ */ jsx(Label, { children: "Products" }),
        /* @__PURE__ */ jsx(
          ProductSearchComboBox,
          {
            "aria-label": "Search products",
            excludedProductIds: lines.map((line) => line.productId),
            onSelect: (_id, product) => {
              setPending(product ?? null);
              setOptionState(null);
            }
          },
          searchKey
        ),
        pending && /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3 rounded-xl border border-border p-3", children: [
          /* @__PURE__ */ jsx(ProductOptionsPicker, { productId: pending.id, onChange: handleOptions }),
          optionState?.options?.length > 0 && /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-3", children: [
            /* @__PURE__ */ jsxs("span", { className: "text-xs text-muted", children: [
              "Choose every option to add ",
              pending.displayName || pending.name,
              "."
            ] }),
            /* @__PURE__ */ jsxs("div", { className: "flex gap-2", children: [
              /* @__PURE__ */ jsx(Button, { size: "sm", variant: "ghost", onPress: () => setPending(null), children: "Cancel" }),
              /* @__PURE__ */ jsx(
                Button,
                {
                  size: "sm",
                  variant: "primary",
                  isDisabled: !canAddPending,
                  onPress: () => addLine(pending, optionState.selections),
                  children: "Add"
                }
              )
            ] })
          ] })
        ] })
      ] }),
      /* @__PURE__ */ jsx(OrderLinesEditor, { lines, onChange: changeLine, onRemove: removeLine }),
      /* @__PURE__ */ jsx(
        TextAreaField,
        {
          name: "notes",
          label: "Notes",
          validation: (v) => v.string().max(500, "Notes cannot exceed 500 characters")
        }
      ),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3 rounded-xl border border-border p-3", children: [
        /* @__PURE__ */ jsx(Toggle, { isSelected: withPayment, onChange: setWithPayment, children: "Take payment now" }),
        withPayment && // Inside the Uniformly form, because the registered field reports through its context.
        // Unmounted when off so a required, empty picker does not hold the form invalid.
        /* @__PURE__ */ jsx(
          PaymentProfileField,
          {
            accountId: Number(accountId),
            name: "paymentProfileId",
            label: "Payment profile",
            isRequired: true,
            validation: (v) => v.number().required("Payment profile is required")
          }
        )
      ] }),
      /* @__PURE__ */ jsx(DrawerActions, { children: /* @__PURE__ */ jsx(
        SaveButton,
        {
          label: withPayment ? "Create Order & Pay" : "Create Order",
          iconClass: "ri-add-line",
          withConfirm: true,
          isDisabled: !canSubmit,
          isLoading: monitors.place.executing,
          body: canSubmit && /* @__PURE__ */ jsxs("div", { children: [
            "This will place a",
            " ",
            orderType === OrderType.Subscription ? "subscription" : "customer",
            " order of",
            " ",
            /* @__PURE__ */ jsx("strong", { children: formatCurrencyUSD(subtotal) }),
            " for account",
            " ",
            /* @__PURE__ */ jsx("strong", { children: accountId }),
            withPayment ? " and charge the selected payment profile for it." : "."
          ] }),
          onPress: place
        }
      ) })
    ] });
  },
  {
    name: "OrderCreate",
    monitors: (services) => [services.orderPlacement.place]
  }
);
const ORDER_CREATE = "sales.order-create";
const OrderCreate = (props) => createElement(
  Embed,
  null,
  createElement(OrderCreate$1, props)
);
const SKELETON_ROWS = [0, 1, 2, 3];
const OrderDetailView = ({
  orderId,
  order,
  items,
  isLoadingItems,
  failure,
  statusActions
}) => {
  if (failure) {
    return failure.notFound ? /* @__PURE__ */ jsx(EmptyState, { className: "py-6", children: /* @__PURE__ */ jsxs(EmptyState.Header, { children: [
      /* @__PURE__ */ jsx(EmptyState.Media, { variant: "icon", children: /* @__PURE__ */ jsx("i", { className: "ri-file-search-line" }) }),
      /* @__PURE__ */ jsxs(EmptyState.Title, { children: [
        "Order #",
        orderId,
        " was not found"
      ] })
    ] }) }) : /* @__PURE__ */ jsx(EmptyState, { className: "py-6", children: /* @__PURE__ */ jsxs(EmptyState.Header, { children: [
      /* @__PURE__ */ jsx(EmptyState.Media, { variant: "icon", children: /* @__PURE__ */ jsx("i", { className: "ri-error-warning-line" }) }),
      /* @__PURE__ */ jsx(EmptyState.Title, { children: "Couldn't load this order" }),
      /* @__PURE__ */ jsx(EmptyState.Description, { children: "Nothing was changed." })
    ] }) });
  }
  if (!order) {
    return /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-3 px-4 py-4", "aria-busy": "true", "aria-label": "Loading", children: SKELETON_ROWS.map((i) => /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-[110px_1fr] gap-3", children: [
      /* @__PURE__ */ jsx(Skeleton, { className: "h-3 w-16 rounded" }),
      /* @__PURE__ */ jsx(Skeleton, { className: "h-3 w-40 rounded" })
    ] }, i)) });
  }
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col", children: [
    /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-1 gap-x-4 gap-y-3 px-4 py-3 sm:grid-cols-2", children: [
      /* @__PURE__ */ jsx(WidgetField, { label: "Type", children: order.typeName }),
      /* @__PURE__ */ jsx(WidgetField, { label: "Status", children: order.statusTypeName }),
      /* @__PURE__ */ jsx(WidgetField, { label: "Entered", children: parseAndFormatDateTime(order.enteredOnUtc) }),
      order.completedOnUtc && /* @__PURE__ */ jsx(WidgetField, { label: "Completed", children: parseAndFormatDateTime(order.completedOnUtc) }),
      order.notes && /* @__PURE__ */ jsx(WidgetField, { label: "Notes", span: 2, children: order.notes }),
      /* @__PURE__ */ jsx(WidgetField, { label: "Subtotal", children: formatCurrencyUSD(order.subtotal) }),
      order.discountTotal ? /* @__PURE__ */ jsxs(WidgetField, { label: "Discount", children: [
        "-",
        formatCurrencyUSD(order.discountTotal)
      ] }) : null,
      order.shippingTotal ? /* @__PURE__ */ jsx(WidgetField, { label: "Shipping", children: formatCurrencyUSD(order.shippingTotal) }) : null,
      /* @__PURE__ */ jsx(WidgetField, { label: "Total", children: /* @__PURE__ */ jsx("strong", { children: formatCurrencyUSD(order.grandTotal) }) })
    ] }),
    statusActions,
    /* @__PURE__ */ jsxs(Card, { variant: "transparent", className: "rounded-none border-0 border-t border-[var(--border)]", children: [
      /* @__PURE__ */ jsx(Card.Header, { className: "px-4 pt-3 pb-1", children: /* @__PURE__ */ jsxs(Card.Title, { className: "text-[10.5px] font-semibold uppercase tracking-[0.06em] text-[var(--fg-3)]", children: [
        "Line items",
        items != null ? ` (${items.length})` : ""
      ] }) }),
      /* @__PURE__ */ jsx(Card.Content, { className: "p-0", children: items == null ? /* @__PURE__ */ jsx("div", { className: "px-4 py-6 text-center text-[12.5px] text-[var(--fg-3)]", children: isLoadingItems ? "Loading lines…" : "" }) : items.length === 0 ? /* @__PURE__ */ jsx(EmptyState, { className: "py-6", children: /* @__PURE__ */ jsxs(EmptyState.Header, { children: [
        /* @__PURE__ */ jsx(EmptyState.Media, { variant: "icon", children: /* @__PURE__ */ jsx("i", { className: "ri-shopping-cart-line" }) }),
        /* @__PURE__ */ jsx(EmptyState.Title, { children: "This order has no lines" })
      ] }) }) : /* @__PURE__ */ jsx(Table, { className: "w-full text-[13px]", children: /* @__PURE__ */ jsx(Table.ScrollContainer, { children: /* @__PURE__ */ jsxs(Table.Content, { "aria-label": "Order lines", children: [
        /* @__PURE__ */ jsxs(Table.Header, { children: [
          /* @__PURE__ */ jsx(Table.Column, { isRowHeader: true, children: "Item" }),
          /* @__PURE__ */ jsx(Table.Column, { align: "end", children: "Qty" }),
          /* @__PURE__ */ jsx(Table.Column, { align: "end", children: "Unit" }),
          /* @__PURE__ */ jsx(Table.Column, { align: "end", children: "Total" })
        ] }),
        /* @__PURE__ */ jsx(Table.Body, { items, children: (item) => /* @__PURE__ */ jsxs(Table.Row, { id: item.id, children: [
          /* @__PURE__ */ jsx(Table.Cell, { children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col", children: [
            /* @__PURE__ */ jsx("span", { children: item.productDescription ?? item.productSku ?? `#${item.productId}` }),
            item.productSku && item.productDescription && /* @__PURE__ */ jsx("span", { className: "font-mono text-[11px] text-[var(--fg-3)]", children: item.productSku })
          ] }) }),
          /* @__PURE__ */ jsx(Table.Cell, { align: "end", children: item.quantity }),
          /* @__PURE__ */ jsx(Table.Cell, { align: "end", children: item.unitPrice == null ? "—" : formatCurrencyUSD(item.unitPrice) }),
          /* @__PURE__ */ jsx(Table.Cell, { align: "end", children: item.total == null ? "—" : formatCurrencyUSD(item.total) })
        ] }, item.id) })
      ] }) }) }) })
    ] })
  ] });
};
const ORDER_STATUS_OPTIONS = Object.entries(OrderStatusType).map(([key, id]) => ({
  id,
  label: sentenceCase(unCamelCase(key))
}));
const labelOf = (statusType) => ORDER_STATUS_OPTIONS.find((option) => option.id === statusType)?.label ?? String(statusType);
const OrderStatusActions = reactive.component(
  ({ services, monitors, order, onSuccess }) => {
    const [statusType, setStatusType] = useState(null);
    useSuccessReactor(services.orders.save, (updated, args) => {
      if (Number(args[0]) !== Number(order?.id)) return;
      setStatusType(null);
      onSuccess?.(updated);
    });
    if (!order) return null;
    const options = ORDER_STATUS_OPTIONS.filter((option) => option.id !== order.statusType);
    const chosen = options.find((option) => option.id === statusType) ?? null;
    return /* @__PURE__ */ jsx(RequirePolicy, { resource: "Orders", operation: "Update", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-end gap-2 border-t border-border px-4 py-3", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 flex-1 flex-col gap-1", children: [
        /* @__PURE__ */ jsx("span", { className: "text-xs text-muted", children: "Change status" }),
        /* @__PURE__ */ jsxs(
          Select,
          {
            "aria-label": "New status",
            placeholder: "Choose a status",
            value: statusType,
            onChange: (key) => setStatusType(key == null ? null : Number(key)),
            children: [
              /* @__PURE__ */ jsxs(Select.Trigger, { children: [
                /* @__PURE__ */ jsx(Select.Value, {}),
                /* @__PURE__ */ jsx(Select.Indicator, {})
              ] }),
              /* @__PURE__ */ jsx(Select.Popover, { children: /* @__PURE__ */ jsx(ListBox, { items: options, children: (option) => /* @__PURE__ */ jsxs(ListBox.Item, { id: option.id, textValue: option.label, children: [
                option.label,
                /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
              ] }) }) })
            ]
          }
        )
      ] }),
      /* @__PURE__ */ jsx(
        ConfirmableButton,
        {
          withConfirm: true,
          size: "sm",
          variant: "primary",
          confirmColor: "primary",
          iconClass: "ri-exchange-line",
          label: "Update status",
          isDisabled: !chosen,
          isLoading: monitors.save.executing,
          body: chosen && /* @__PURE__ */ jsxs("div", { children: [
            "Change order ",
            /* @__PURE__ */ jsxs("strong", { children: [
              "#",
              order.id
            ] }),
            " from",
            " ",
            /* @__PURE__ */ jsx("strong", { children: order.statusTypeName || labelOf(order.statusType) }),
            " to",
            " ",
            /* @__PURE__ */ jsx("strong", { children: chosen.label }),
            "? Nothing else on the order changes."
          ] }),
          onPress: () => chosen && services.orders.save(order.id, { ...order, statusType: chosen.id })
        }
      )
    ] }) });
  },
  {
    name: "OrderStatusActions",
    monitors: (services) => [services.orders.save]
  }
);
const OrderDetailController = reactive.component(
  ({ services, monitors, orderId, onLoaded, onNotFound, onChanged }) => {
    const id = Number(orderId);
    const [order, setOrder] = useState(null);
    const [items, setItems] = useState(null);
    const [failure, setFailure] = useState(null);
    const isMine = (args) => Number(args[0]) === id;
    useSuccessReactor(services.orders.getById, (record, args) => {
      if (!isMine(args)) return;
      setOrder(record);
      setFailure(null);
      onLoaded?.(record);
    });
    useErrorReactor(services.orders.getById, (error, args) => {
      if (!isMine(args)) return;
      const notFound = error?.statusCode === 404;
      setFailure({ notFound });
      if (notFound) onNotFound?.();
    });
    useSuccessReactor(services.orders.getItems, (list, args) => {
      if (isMine(args)) setItems(list ?? []);
    });
    useSuccessReactor(services.orders.save, (record, args) => {
      if (!isMine(args)) return;
      setOrder(record);
      onLoaded?.(record);
      onChanged?.();
    });
    return /* @__PURE__ */ jsx(
      OrderDetailView,
      {
        orderId: id,
        order,
        items,
        isLoadingItems: monitors.getItems.executing,
        failure,
        statusActions: /* @__PURE__ */ jsx(OrderStatusActions, { order })
      }
    );
  },
  {
    name: "OrderDetailController",
    sync: (services, { orderId }) => [
      { execute: () => orderId && services.orders.getById(Number(orderId)), deps: [orderId] },
      { execute: () => orderId && services.orders.getItems(Number(orderId)), deps: [orderId] }
    ],
    monitors: (services) => [services.orders.getById, services.orders.getItems]
  }
);
const OrderDetailForbidden = /* @__PURE__ */ jsx(EmptyState, { className: "py-6", children: /* @__PURE__ */ jsxs(EmptyState.Header, { children: [
  /* @__PURE__ */ jsx(EmptyState.Media, { variant: "icon", children: /* @__PURE__ */ jsx("i", { className: "ri-lock-line" }) }),
  /* @__PURE__ */ jsx(EmptyState.Title, { children: "Order detail unavailable" }),
  /* @__PURE__ */ jsx(EmptyState.Description, { children: "Viewing an order requires permission to view orders." })
] }) });
const OrderDetail$1 = (props) => /* @__PURE__ */ jsx(RequirePolicy, { resource: "Orders", operation: "View", placeholder: OrderDetailForbidden, children: /* @__PURE__ */ jsx(OrderDetailController, { ...props }) });
const ORDER_DETAIL = "sales.order-detail";
const OrderDetail = (props) => createElement(
  Embed,
  null,
  createElement(OrderDetail$1, props)
);
let appRuntime = null;
const initSalesRuntime = (hostRuntime) => {
  appRuntime = hostRuntime.registerApp("sales", {
    description: {
      title: "Sales",
      iconClass: "ri-line-chart-line",
      defaultRoute: "/",
      isVisible: (runtime) => runtime.user.hasApiAccess(runtime.config?.api?.apiKey ?? "")
    },
    routes: Router,
    navigation: getSalesNavigation,
    resources: getSalesResources,
    config: () => SalesConfig.current
  });
  hostRuntime.components.register(ORDER_CREATE, OrderCreate);
  hostRuntime.components.register(ORDER_DETAIL, OrderDetail);
};
export {
  initSalesRuntime
};
//# sourceMappingURL=index.js.map
